import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import DeliveryJourneySidebar from '../components/DeliveryJourneySidebar';
import { PurposeIcon, FlowIcon, OutcomeIcon, TipsIcon } from '../components/HowToIcons';
import { SECTION_GUIDANCE } from '../sectionGuidance';
import { track } from '../utils/analytics';

import { CANVAS_API, UPLOAD_API as UPLOAD_API_URL } from '../config';
import { mergeSectionPreservingMeta, countDeepDiveSections } from '../utils/mergeCanvasUpload';
import EvaluationModal from '../components/EvaluationModal';
const API = CANVAS_API;
const UPLOAD_API = UPLOAD_API_URL;

// ── Storage ───────────────────────────────────────────────────────
const STORAGE_KEY = 'guruone_strategy';

function loadCanvasData(businessKey) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const all = JSON.parse(raw);
    return all[businessKey] || {};
  } catch { return {}; }
}

function saveCanvasData(businessKey, data) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[businessKey] = { ...data, lastUpdated: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {}
}

// ── Section definitions ───────────────────────────────────────────
// Field schemas here are the runtime reflection of sectionGuidance.js.
// When the spec changes, this array is what brings it to the UI.
// ~30 starter themes grouped loosely from trust/ethics → craft → customer
// → people → growth → drive → simplicity → purpose. The chip picker
// shows them all in one block with an "Other" escape for custom terms.
const VALUE_THEMES = [
  // Trust & ethics
  'Integrity','Honesty','Transparency','Trust','Ethics','Fairness',
  // Quality & craft
  'Excellence','Quality','Craft','Reliability','Discipline',
  // Accountability
  'Accountability','Ownership',
  // Customer & service
  'Customer focus','Service','Empathy',
  // People & team
  'Collaboration','Respect','Humility',
  // Growth & innovation
  'Innovation','Curiosity','Learning','Adaptability',
  // Drive & ambition
  'Boldness','Ambition','Speed',
  // Simplicity
  'Simplicity','Pragmatism',
  // Purpose & wellbeing
  'Impact','Sustainability','Wellbeing','Resilience',
];
const MISSION_THEMES = ['Wellbeing','Prosperity','Entrepreneurship','Standards','Community','Sustainability','Innovation','Education','Productivity','Fairness'];

// Cost categories — mirror DG's fixed_costs_breakdown (questions.js) exactly
// so the SplitSelector widget can be reused without config drift.
const COST_CATEGORIES = [
  { key: 'salaries',     label: 'Salaries & wages' },
  { key: 'premises',     label: 'Rent & premises' },
  { key: 'tech',         label: 'Technology & software' },
  { key: 'marketing',    label: 'Marketing & sales' },
  { key: 'professional', label: 'Professional services' },
  { key: 'other',        label: 'Other fixed costs' },
];

const ACQUISITION_MOTIONS = [
  { key: 'direct_outreach', label: 'Direct outreach' },
  { key: 'inbound_content', label: 'Inbound content' },
  { key: 'partners_referrals', label: 'Partners & referrals' },
  { key: 'word_of_mouth', label: 'Word of mouth' },
];

const REVENUE_TYPES = [
  { key: 'project_based', label: 'Project-based' },
  { key: 'retainer',      label: 'Retainer' },
  { key: 'subscription',  label: 'Subscription' },
  { key: 'transaction',   label: 'Transaction' },
  { key: 'commission',    label: 'Commission' },
];

export const SECTIONS = [
  {
    key: 'founder_purpose', title: 'Founder Purpose', icon: '♥', colour: '#C0556E',
    description: 'Why you started and where you want to go personally',
    group: 'foundation',
    hasDecision: false,
    explorePrompt: 'business leader purpose, personal motivation, risk tolerance and time horizon',
    fields: [
      { key: 'origin_driver', label: 'Origin driver', type: 'chips', options: ['Problem I experienced','Opportunity spotted','Passion for craft','Forced change / necessity'], multi: true, required: true },
      { key: 'primary_motivation', label: 'Primary motivation', type: 'chips', options: ['Wealth','Impact','Lifestyle','Legacy'], multi: false, required: true },
      { key: 'risk_tolerance', label: 'Risk tolerance', type: 'chips', options: ['Low','Moderate','High'], multi: false, required: true },
      { key: 'time_horizon', label: 'Intended time horizon', type: 'chips', options: ['Build long-term','Sell in 1–3 years','Sell in 3–5 years','Sell in 5+ years','Unsure'], multi: false, required: true },
    ],
  },
  {
    key: 'values', title: 'Values', icon: '✦', colour: '#7F77DD',
    description: 'What you stand for and will not compromise on',
    group: 'foundation',
    hasDecision: false,
    explorePrompt: 'core values, what the business stands for, non-negotiables and how values influence decisions',
    repeatable: true,
    addLabel: '+ Add a value',
    fields: [
      { key: 'value_theme', label: 'Value', type: 'chips', options: VALUE_THEMES, multi: false, required: true, allow_custom: true },
      { key: 'behavioural_translation', label: 'How it shows up in behaviour', type: 'textarea', required: true, placeholder: 'e.g. We always tell clients the truth even when it costs us work' },
      { key: 'non_negotiable', label: 'Non-negotiable line', type: 'text', placeholder: 'What line would you never cross around this value?' },
      { key: 'decision_influence', label: 'How often it drives decisions', type: 'chips', options: ['Rarely','Sometimes','Often','Always'], multi: false },
    ],
  },
  {
    key: 'mission', title: 'Mission', icon: '◎', colour: '#0D8C8C',
    description: 'Your core objective, who you serve and what success looks like',
    group: 'direction',
    hasDecision: false,
    explorePrompt: 'the business mission, who it primarily serves, and what success looks like in 3-5 years',
    fields: [
      { key: 'core_objective_themes', label: 'Core objective themes', type: 'chips', options: MISSION_THEMES, multi: true },
      { key: 'primary_beneficiary', label: 'Primary beneficiary', type: 'text', placeholder: 'Who does this mission primarily serve?' },
      { key: 'time_horizon', label: 'Mission time horizon', type: 'chips', options: ['1–3 years','3–5 years'], multi: false },
      { key: 'success_definition', label: 'Success definition', type: 'chips', options: ['Revenue','Impact','Market share','Other'], multi: false },
    ],
  },
  {
    key: 'vision', title: 'Vision', icon: '◉', colour: '#1D9E75',
    description: 'Where you are heading and by when',
    group: 'direction',
    hasDecision: true,
    decisionPrompt: 'What should our strategic ambition and direction be?',
    explorePrompt: 'the business vision — where it is heading, what it will look like when it gets there, and over what timeframe',
    fields: [
      // Stage selector — calibrates how the Explore dialogue probes and
      // how the auto-evaluator scores. A good vision needs both an
      // aspirational long-term picture AND a credible near-term path,
      // but the weighting shifts by stage.
      { key: 'vision_stage', label: 'Business stage', type: 'chips',
        options: ['Early stage (pre-revenue or early traction)', 'Growth stage (scaling a proven model)', 'Established (mature, stable)'],
        multi: false, required: true,
        explainer: "Vision needs to do two jobs at once — paint an aspirational destination AND show a credible near-term path. The weighting shifts by stage: early-stage emphasises ambition, growth balances both, established prioritises near-term realism. Pick the closest match so the dialogue and the review calibrate to your stage." },
      { key: 'vision_statement', label: 'Finalise the one-line vision', type: 'textarea', placeholder: 'Edit the narrative above into the one-line vision you want to commit to — or write it from scratch.' },
      { key: 'target_type', label: 'Target type', type: 'chips', options: ['Revenue','Scale','Exit','Acquisition'], multi: false, required: true },
      { key: 'target_detail', label: 'Target detail', type: 'text', placeholder: 'e.g. £2m ARR, 3 locations, exit to trade buyer', required: true },
      { key: 'time_horizon_years', label: 'Time horizon (years)', type: 'text', placeholder: 'e.g. 3, 5, 10' },
    ],
  },
  {
    key: 'value_proposition', title: 'Value Proposition', icon: '◆', colour: '#185FA5',
    description: 'What you do, for whom, and how you are positioned',
    group: 'direction',
    hasDecision: true,
    decisionPrompt: 'What price positioning and value proposition should we adopt?',
    explorePrompt: 'the value proposition — what problem is solved, for whom, what outcomes are delivered, and how it is positioned in the market',
    fields: [
      { key: 'primary_problem_solved', label: 'Primary problem solved', type: 'textarea', required: true, placeholder: 'The specific problem, in the leader\'s own words. Two or three sentences if it helps.' },
      { key: 'target_customer_type', label: 'Target customer type', type: 'textarea', required: true, placeholder: 'Who has this problem most acutely — described with enough detail that a stranger could picture them.' },
      { key: 'top_three_benefits', label: 'Top three benefits or outcomes delivered', type: 'textarea', required: true },
      { key: 'market_positioning', label: 'Market positioning', type: 'chips', options: ['Tailored','Best-in-class','Reliable & efficient'], multi: false, required: true },
      { key: 'price_positioning', label: 'Price positioning', type: 'chips', options: ['Premium','Mid-market','Low cost'], multi: false, required: true },
    ],
  },
  {
    key: 'customers', title: 'Customers', icon: '◕', colour: '#E07340',
    description: 'Who buys from you and what they are worth',
    group: 'market_a',
    hasDecision: true,
    decisionPrompt: 'Which customer segments should we prioritise and invest in?',
    explorePrompt: 'customer segments — who buys from you, what percentage of revenue each segment represents, how strategically important each is, and how easy or complex they are to serve',
    repeatable: true,
    addLabel: '+ Add customer segment',
    fields: [
      { key: 'segment_name', label: 'Segment name', type: 'text', required: true },
      { key: 'revenue_pct', label: '% of revenue', type: 'text', placeholder: 'e.g. 40%' },
      { key: 'strategic_importance', label: 'Strategic importance', type: 'chips', options: ['Core','Growth','Secondary','Declining'], multi: false, required: true },
      { key: 'typical_margin', label: 'Typical margin', type: 'chips', options: ['Low','Medium','High'], multi: false },
      { key: 'ease_of_acquisition', label: 'Ease of acquisition', type: 'rating', max: 5,
        explainer: 'How easy is it to win a new customer in this segment? Click a number to rate.',
        low_label: 'very hard', high_label: 'very easy' },
      { key: 'delivery_complexity', label: 'Delivery complexity', type: 'rating', max: 5,
        explainer: 'How complex is it to deliver to this segment? Click a number to rate.',
        low_label: 'simple', high_label: 'highly complex' },
    ],
    future_trend: {
      per_entry: true,
      question: 'Future of this segment',
      will_continue_question: 'Will this segment continue to be served?',
      will_continue_options: ['Yes — still a segment', 'No — we will exit'],
      trend_question: 'How will it shift?',
      trend_options: ['Grow', 'Stay the same', 'Shrink'],
      reasoning_label: "What's driving the trend",
      reasoning_placeholder: "Why will this segment grow / shrink / stay the same?",
    },
    future_additions: {
      label: 'New customer segments in the future',
      intro: "Segments you'll serve that you don't today, in line with your vision horizon.",
      name_label: 'Segment',
      name_placeholder: 'e.g. Enterprise FinTech, EU mid-market',
      reasoning_label: 'Why this segment',
      reasoning_placeholder: 'What makes this segment attractive and reachable then?',
    },
  },
  {
    key: 'competitors', title: 'Competitors', icon: '✂', colour: '#BA7517',
    description: 'Who you compete with and how you win or lose',
    group: 'market_a',
    hasDecision: false,
    explorePrompt: 'the competitive landscape — who the alternatives are, why customers choose them, and when and why this business wins or loses',
    fields: [
      { key: 'alternative_types', label: 'Types of alternative', type: 'chips', options: ['Direct competitors','Lower-cost alternatives','Premium / specialists','In-house solutions','DIY tools','Do nothing (status quo)'], multi: true },
      { key: 'key_examples', label: 'Key examples (3–5 names)', type: 'text', placeholder: 'e.g. Company A, Company B, Company C' },
      { key: 'customer_drivers', label: 'Customers choose them for', type: 'chips', options: ['Price','Quality','Service','Reputation','Familiarity'], multi: true },
      { key: 'win_pattern', label: 'Head-to-head, we', type: 'chips', options: ['Win often','Win sometimes','Lose often','Rarely compete directly'], multi: false },
      { key: 'win_reason', label: 'When we win, it is on', type: 'chips', options: ['Price','Risk reduction','Speed','Brand / trust','Relationships','Expertise'], multi: true },
    ],
    future_trend: {
      per_entry: false,
      question: 'How will the competitive landscape shift?',
      will_continue_question: "Will today's competitive picture hold?",
      will_continue_options: ['Yes — landscape stable', 'Largely — some shift', 'No — material change coming'],
      trend_question: 'Which shifts are likely?',
      trend_options: ['New direct entrants', 'AI-native challengers', 'Lower-cost alternatives rising', 'In-house / DIY substitutes', 'Customer behaviour changing', 'Sector consolidation'],
      trend_multi: true,
      reasoning_label: "What's driving the change",
      reasoning_placeholder: 'New entrants, technology, regulation, customer behaviour…',
    },
    future_additions: {
      label: 'Anticipated new competitors or threats',
      intro: "Future competition you don't face today but expect to, by your vision horizon.",
      name_label: 'Competitor / threat',
      name_placeholder: 'e.g. AI-native challenger from the US, big incumbent moving down-market',
      reasoning_label: 'Why this threat',
      reasoning_placeholder: 'What makes this a credible future competitor?',
    },
  },
  {
    key: 'differentiators', title: 'Differentiators', icon: '❄', colour: '#7B3AC2',
    description: 'What makes you genuinely different and how defensible it is',
    group: 'market_a',
    hasDecision: true,
    decisionPrompt: 'Which differentiators should we double down on and invest in?',
    explorePrompt: 'what genuinely differentiates this business from alternatives, how strong the evidence is, and how defensible those differences are',
    repeatable: true,
    addLabel: '+ Add a differentiator',
    fields: [
      { key: 'differentiator', label: 'Differentiator', type: 'text', required: true, placeholder: 'What sets you apart from alternatives?' },
      { key: 'evidence', label: 'Evidence', type: 'textarea', placeholder: 'Concrete proof this is real — not aspiration' },
      { key: 'defensibility_type', label: 'Type of defensibility', type: 'chips', options: ['Structural','Human / cultural','Cost-prohibitive to replicate','Time-advantage only'], multi: false, required: true },
      { key: 'defensibility_detail', label: 'How it is defensible', type: 'text', placeholder: 'One line on what makes it hard to replicate' },
      { key: 'is_lead', label: 'Is this your lead differentiator?', type: 'chips', options: ['Yes — this is the lead'], multi: false },
    ],
    future_trend: {
      per_entry: true,
      question: 'Future of this differentiator',
      will_continue_question: 'Will this still be a differentiator?',
      will_continue_options: ['Yes — still differentiates', 'No — we will phase out'],
      trend_question: 'How will it shift?',
      trend_options: ['Strengthen', 'Maintain'],
      reasoning_label: "What's driving the trend",
      reasoning_placeholder: 'Why will this strengthen / hold / fade?',
    },
    future_additions: {
      label: 'New differentiators to build',
      intro: "Differentiators you don't have today but will need by your vision horizon.",
      name_label: 'Differentiator',
      name_placeholder: 'e.g. Proprietary regulatory data set, AI-native delivery model',
      reasoning_label: 'Why this differentiator',
      reasoning_placeholder: "What's the strategic case for building this one?",
    },
  },
  {
    key: 'go_to_market', title: 'Go to Market', icon: '▶', colour: '#2B7CB0',
    description: 'How you reach, win and keep customers',
    group: 'market_a',
    hasDecision: true,
    decisionPrompt: 'What is the right go-to-market approach for where we are now?',
    explorePrompt: 'how the business reaches and wins customers — the blend of acquisition motions, the primary motion, sales approach and cycle; and how customers are retained after the first sale',
    fields: [
      { key: 'acquisition_motion_blend', label: 'Acquisition motion blend', type: 'split', widget_config: { options: ACQUISITION_MOTIONS, allowAbsolute: false }, required: true },
      { key: 'primary_motion', label: 'Primary motion', type: 'chips', options: ACQUISITION_MOTIONS.map(m => m.label), multi: false, required: true, constrained_by: 'acquisition_motion_blend', constraint_source: ACQUISITION_MOTIONS },
      { key: 'sales_approach', label: 'Sales approach', type: 'chips', options: ['Founder-led','In-house sales team','Self-serve','Mixed'], multi: false, required: true },
      { key: 'avg_sales_cycle', label: 'Average sales cycle length', type: 'text', placeholder: 'e.g. 2 weeks, 3 months' },
      { key: 'customer_acquisition_cost', label: 'Estimated customer acquisition cost', type: 'text', placeholder: 'Optional — e.g. £500, £2,000' },
      { key: 'retention_mechanism', label: 'How customers stay', type: 'chips', options: ['Contracted renewal','Customers come back','Growing the account','Customer referrals','None — transactional'], multi: false, required: true },
      { key: 'retention_approach', label: 'Retention approach', type: 'chips', options: ['Deliberate','Ad hoc','Not really a focus'], multi: false, required: true },
      { key: 'retention_read', label: 'How retention is going', type: 'text', placeholder: 'Your read — strong, weak, mixed, unclear' },
    ],
    future_trend: {
      per_entry: false,
      question: 'How will the GTM motion shift?',
      will_continue_question: "Will today's GTM motion hold?",
      will_continue_options: ['Yes — same motion at scale', 'Largely — small adjustments', 'No — major shift'],
      trend_question: 'Which shifts are likely?',
      trend_options: ['Stay founder-led', 'Tilt to inbound / content', 'Tilt to partners & referrals', 'Tilt to direct outreach', 'Build sales team', 'Add self-serve / product-led'],
      trend_multi: true,
      reasoning_label: "What's driving the shift",
      reasoning_placeholder: 'What forces the change — scale ceiling, positioning, segment mix?',
    },
    future_additions: {
      label: 'New GTM motions or channels to add',
      intro: "Channels or motions you don't use today but will, by your vision horizon.",
      name_label: 'Motion or channel',
      name_placeholder: 'e.g. Outbound SDR team, partner programme, industry events',
      reasoning_label: 'Why this addition',
      reasoning_placeholder: 'What does this unlock?',
    },
  },
  {
    key: 'revenue_model', title: 'Revenue Model', icon: '◑', colour: '#9B6B3A',
    description: 'How the business makes money and from whom',
    group: 'market_b',
    hasDecision: true,
    decisionPrompt: 'What revenue model should we pursue to best match our customers and capabilities?',
    explorePrompt: 'how the business makes money — who buys, the blend of revenue types, typical deal size and payment terms, and gross margin',
    fields: [
      { key: 'business_type', label: 'Business type', type: 'chips', options: ['B2B','B2C','C2C','C2B','D2C'], multi: true, required: true },
      { key: 'revenue_blend', label: 'Revenue blend', type: 'split', widget_config: { options: REVENUE_TYPES, allowAbsolute: false }, required: true },
      { key: 'primary_revenue_type', label: 'Primary revenue type', type: 'chips', options: REVENUE_TYPES.map(r => r.label), multi: false, required: true, constrained_by: 'revenue_blend', constraint_source: REVENUE_TYPES },
      { key: 'typical_deal_size', label: 'Typical deal size', type: 'text', placeholder: 'e.g. £5k–£15k per engagement', required: true },
      { key: 'payment_terms', label: 'Payment terms', type: 'chips', options: ['Upfront','On delivery','Net 30','Net 60+','Milestones'], multi: false },
      { key: 'gross_margin', label: 'Gross margin', type: 'text', placeholder: 'Range is fine — e.g. 40–55%' },
      { key: 'contract_length_typical', label: 'Typical contract length', type: 'chips', options: ['<3 months','3–12 months','12+ months'], multi: false, shown_when: { field: 'revenue_blend', any_of_keys: ['retainer','subscription'] } },
    ],
    future_trend: {
      per_entry: false,
      question: 'How will the revenue model shift?',
      will_continue_question: "Will today's revenue shape hold?",
      will_continue_options: ['Yes — same mix at scale', 'Largely — small shift', 'No — material shift'],
      trend_question: 'Which shifts are likely?',
      trend_options: ['Shift toward retainer / recurring', 'Shift toward subscription', 'Bigger deal sizes', 'Smaller deal sizes / higher volume', 'Higher margin', 'Lower margin / higher scale'],
      trend_multi: true,
      reasoning_label: "What's driving the shift",
      reasoning_placeholder: 'What forces the change — segment mix, positioning, market evolution?',
    },
    future_additions: {
      label: 'New revenue streams to add',
      intro: "Ways the business will make money that it doesn't today, by your vision horizon.",
      name_label: 'Revenue stream',
      name_placeholder: 'e.g. SaaS platform fees, IP licensing, training & certification',
      reasoning_label: 'Why this stream',
      reasoning_placeholder: 'Strategic rationale and approximate timing?',
    },
  },
  {
    key: 'cost_model', title: 'Cost Model', icon: '⚙', colour: '#6B7A8D',
    description: 'How costs are structured and what drives margin',
    group: 'market_b',
    hasDecision: false,
    explorePrompt: 'how costs are structured — the monthly fixed base, where those fixed costs go, the fixed/variable split, rough breakeven, and margin volatility',
    fields: [
      { key: 'monthly_fixed_costs', label: 'Rough monthly fixed costs', type: 'text', placeholder: 'Range is fine — e.g. £25–30k/month', required: true },
      { key: 'fixed_costs', label: 'Where the fixed costs go', type: 'split', widget_config: { options: COST_CATEGORIES, allowAbsolute: true, absoluteTotalFrom: 'monthly_fixed_costs' }, required: true },
      { key: 'fixed_variable_ratio', label: 'How the cost base flexes', type: 'chips', options: ['Mostly fixed','Balanced','Mostly variable','Not sure'], multi: false, required: true },
      { key: 'breakeven_estimate', label: 'Rough monthly breakeven', type: 'text', placeholder: 'Optional — e.g. £30k/month. "Not sure" is fine.' },
      { key: 'margin_volatility', label: 'Margin stability', type: 'chips', options: ['Stable','Moderate','Volatile'], multi: false },
    ],
    future_trend: {
      per_entry: false,
      question: 'How will the cost shape shift?',
      will_continue_question: "Will today's cost shape hold?",
      will_continue_options: ['Yes — same shape at scale', 'Largely — small shift', 'No — material shift'],
      trend_question: 'Which shifts are likely?',
      trend_options: ['Heavier on people', 'Heavier on marketing', 'Heavier on tech / R&D', 'More fixed', 'More variable', 'Premises step-up at scale'],
      trend_multi: true,
      reasoning_label: "What's driving the shift",
      reasoning_placeholder: 'What forces the change — scaling team, capability investment, infrastructure?',
    },
    future_additions: {
      label: 'New cost categories that emerge at scale',
      intro: "Cost lines you don't have today but will, by your vision horizon.",
      name_label: 'Cost category',
      name_placeholder: 'e.g. Compliance team, finance director, dedicated infrastructure',
      reasoning_label: 'Why this cost',
      reasoning_placeholder: 'When does this come in and what drives it?',
    },
  },
  {
    key: 'current_challenges', title: 'Current Challenges', icon: '⚡', colour: '#C84B1A',
    description: 'The most pressing issues the business faces right now',
    group: 'focus',
    hasDecision: true,
    decisionPrompt: 'How should we prioritise and address our biggest current challenges?',
    explorePrompt: 'the challenges currently blocking the business — what each one is, concrete evidence it is live, which area it sits in, how urgent it is, and what happens if it is not addressed',
    repeatable: true,
    addLabel: '+ Add a challenge',
    fields: [
      { key: 'challenge', label: 'The challenge', type: 'textarea', required: true, placeholder: 'Describe the challenge specifically — not generically' },
      { key: 'evidence', label: "What's actually happening", type: 'text', placeholder: 'One sentence of concrete evidence this is live' },
      { key: 'area', label: 'Challenge area', type: 'chips', options: ['Growth','Cash & Finance','People','Operations','Market','Technology','Other'], multi: false, required: true },
      { key: 'urgency', label: 'Urgency', type: 'chips', options: ['Critical','High','Medium','Unclear'], multi: false, required: true },
      { key: 'impact_if_ignored', label: "If we don't address this", type: 'text', placeholder: 'One sentence on the consequence of inaction' },
    ],
  },
];

// ── Layout groups ─────────────────────────────────────────────────
const LAYOUT_ROWS = [
  { key: 'foundation', label: 'Foundation', sublabel: 'Who you are and what you stand for', colour: '#C0556E', cols: 2 },
  { key: 'direction',  label: 'Direction',  sublabel: 'Where you are going and how you will get there', colour: '#1D9E75', cols: 3 },
  { key: 'market_a',  label: 'Market & Model', sublabel: 'How you compete and win customers', colour: '#E07340', cols: 4 },
  { key: 'market_b',  label: null, sublabel: null, colour: '#9B6B3A', cols: 2 },
  { key: 'focus',     label: 'Focus', sublabel: 'Current challenges to work through', colour: '#C84B1A', cols: 1 },
];

// ── Completeness ──────────────────────────────────────────────────
// Field-level "is filled" check — strips empty strings, empty arrays, and
// empty object shapes (e.g. SplitEditor's initial { mode, splits: {} }).
function isFieldFilled(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') {
    if (v.entries) return Array.isArray(v.entries) && v.entries.some(e => Object.values(e).some(isFieldFilled));
    if (v.splits) return Object.keys(v.splits).length > 0;
    return Object.values(v).some(isFieldFilled);
  }
  return String(v).trim().length > 0;
}

// 0–5 score. Preferred source: the LLM-evaluated score saved at end-of-
// Explore. Falls back to mechanical field-count when no evaluation
// exists (i.e. section saved before the evaluator existed, or eval
// failed).
//
// Mechanical fallback:
//   - up to 4 from field completeness (proportion of declared fields filled)
//   - +1 bonus when a deep-dive narrative or Explore transcript is captured
function getSectionCompleteness(section, sectionData) {
  if (!sectionData) return 0;
  // Prefer LLM evaluation when present — that's the depth/consistency/
  // clarity judgment the leader asked for.
  if (sectionData._evaluation && typeof sectionData._evaluation.score === 'number') {
    return Math.max(0, Math.min(5, Math.round(sectionData._evaluation.score)));
  }
  const hasNarrative = !!(sectionData._narrative || sectionData._explore_transcript);

  if (section.repeatable) {
    const entries = Array.isArray(sectionData.entries) ? sectionData.entries : [];
    if (entries.length === 0) return 0;
    // Average completeness across entries: each entry filled ratio against
    // its own field set, averaged.
    const fields = section.fields;
    const perEntry = entries.map(e =>
      fields.filter(f => isFieldFilled(e[f.key])).length / Math.max(fields.length, 1)
    );
    const avg = perEntry.reduce((s, r) => s + r, 0) / perEntry.length;
    // Light reward for having multiple entries (more breadth = more confidence)
    const breadthBoost = Math.min(0.1, (entries.length - 1) * 0.03);
    const fieldScore = Math.min(4, (avg + breadthBoost) * 4);
    return Math.min(5, Math.round(fieldScore + (hasNarrative ? 1 : 0)));
  }

  const fields = section.fields;
  if (fields.length === 0) return 0;
  const filled = fields.filter(f => isFieldFilled(sectionData[f.key])).length;
  const ratio = filled / fields.length;
  const fieldScore = Math.min(4, ratio * 4);
  return Math.min(5, Math.round(fieldScore + (hasNarrative ? 1 : 0)));
}

// Tier label — narrower than the star score, designed for at-a-glance
// "how mature is my thinking here" reading. Drives the small pill badge
// rendered next to the stars on section cards.
function getSectionTier(section, sectionData) {
  if (!sectionData) return { level: 0, label: '' };
  const hasNarrative = !!(sectionData._narrative || sectionData._explore_transcript);

  let filledRatio = 0;
  if (section.repeatable) {
    const entries = Array.isArray(sectionData.entries) ? sectionData.entries : [];
    if (entries.length === 0) return { level: 0, label: '' };
    const fields = section.fields;
    const perEntry = entries.map(e =>
      fields.filter(f => isFieldFilled(e[f.key])).length / Math.max(fields.length, 1)
    );
    filledRatio = perEntry.reduce((s, r) => s + r, 0) / perEntry.length;
  } else {
    const fields = section.fields;
    if (fields.length === 0) return { level: 0, label: '' };
    const filled = fields.filter(f => isFieldFilled(sectionData[f.key])).length;
    filledRatio = filled / fields.length;
  }

  // Tier thresholds — slightly forgiving so a single empty optional
  // cell across many entries doesn't keep the leader at "some fields".
  // 100% is reserved for the deep-dive tier (along with a narrative).
  //   level 1 — under 90% filled
  //   level 2 — 90% or more filled, no narrative yet
  //   level 3 — 90% or more filled AND narrative captured
  if (filledRatio === 0) return { level: 0, label: '' };
  if (filledRatio < 0.9) return { level: 1, label: 'some fields' };
  if (!hasNarrative)     return { level: 2, label: 'all fields' };
  return { level: 3, label: 'deep-dive' };
}

// ── Format for CDDD ───────────────────────────────────────────────
export function formatStrategyContext(canvasData) {
  if (!canvasData || Object.keys(canvasData).length === 0) return '';
  const lines = ['Strategy canvas context:'];
  const fp = canvasData.founder_purpose;
  if (fp?.primary_motivation) lines.push(`- Business leader motivation: ${fp.primary_motivation}`);
  if (fp?.risk_tolerance) lines.push(`- Risk tolerance: ${fp.risk_tolerance}`);
  const vp = canvasData.value_proposition;
  if (vp?.top_three_benefits) lines.push(`- Key benefits: ${vp.top_three_benefits}`);
  if (vp?.market_positioning) lines.push(`- Market positioning: ${vp.market_positioning}`);
  if (vp?.price_positioning) lines.push(`- Price positioning: ${vp.price_positioning}`);
  const vis = canvasData.vision;
  if (vis?.target_detail) lines.push(`- Vision: ${vis.target_detail} (${vis.time_horizon_years || 'TBC'} years)`);
  if (vis?.vision_statement) lines.push(`- Vision statement: ${vis.vision_statement}`);
  const vals = canvasData.values?.entries;
  if (vals?.length) lines.push(`- Values: ${vals.map(v => v.value_theme).filter(Boolean).join(', ')}`);
  const diff = canvasData.differentiators?.entries;
  if (diff?.length) lines.push(`- Differentiators: ${diff.map(d => d.differentiator).filter(Boolean).join('; ')}`);
  const gtm = canvasData.go_to_market;
  if (gtm?.primary_motion) lines.push(`- Go to market: ${gtm.primary_motion} (${gtm.sales_approach || 'TBC'})`);
  if (gtm?.retention_mechanism) lines.push(`- Retention: ${gtm.retention_mechanism} / ${gtm.retention_approach || 'TBC'}`);
  const rm = canvasData.revenue_model;
  if (rm?.primary_revenue_type) lines.push(`- Revenue model: ${rm.primary_revenue_type} (deal size: ${rm.typical_deal_size || 'TBC'})`);
  if (rm?.gross_margin) lines.push(`- Gross margin: ${rm.gross_margin}`);
  const cm = canvasData.cost_model;
  if (cm?.monthly_fixed_costs) lines.push(`- Monthly fixed costs: ${cm.monthly_fixed_costs}`);
  if (cm?.fixed_variable_ratio) lines.push(`- Cost flex: ${cm.fixed_variable_ratio}`);
  const cust = canvasData.customers?.entries;
  if (cust?.length) lines.push(`- Customer segments: ${cust.map(c => `${c.segment_name}${c.strategic_importance ? ` (${c.strategic_importance})` : ''}`).filter(Boolean).join(', ')}`);
  const cc = canvasData.current_challenges?.entries;
  if (cc?.length) lines.push(`- Challenges: ${cc.map(c => `${c.challenge} [${c.urgency || 'unclear'}]`).filter(Boolean).join('; ')}`);
  return lines.length > 1 ? lines.join('\n') : '';
}

// ── Field components ──────────────────────────────────────────────
function ChipSelect({ options, value, onChange, multi, max, allowCustom }) {
  const vals = Array.isArray(value) ? value : (value ? [value] : []);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');

  const toggle = (opt) => {
    if (multi) {
      if (vals.includes(opt)) onChange(vals.filter(v => v !== opt));
      else { if (max && vals.length >= max) return; onChange([...vals, opt]); }
    } else { onChange(vals.includes(opt) ? null : opt); }
  };

  // Active values not in the preset options are user-added — render them
  // as extra active chips so they stay visible after selection.
  const customActive = vals.filter(v => !options.includes(v));

  function commitCustom() {
    const v = customInput.trim();
    if (!v) { setAddingCustom(false); setCustomInput(''); return; }
    // Dedupe against preset options (case-insensitive)
    const presetMatch = options.find(o => o.toLowerCase() === v.toLowerCase());
    const final = presetMatch || v;
    if (multi) {
      if (!vals.includes(final)) {
        if (max && vals.length >= max) { setAddingCustom(false); setCustomInput(''); return; }
        onChange([...vals, final]);
      }
    } else {
      onChange(final);
    }
    setCustomInput('');
    setAddingCustom(false);
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem', alignItems: 'center' }}>
      {options.map(opt => {
        const active = vals.includes(opt);
        return (
          <button key={opt} onClick={() => toggle(opt)}
            style={{ padding: '0.3rem 0.7rem', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? '1.5px solid #1D9E75' : '1.5px solid #e0e0e0', background: active ? '#E1F5EE' : 'white', color: active ? '#0F6E56' : '#555', fontWeight: active ? 600 : 400, width: 'auto', transition: 'all 0.12s' }}>
            {opt}
          </button>
        );
      })}
      {customActive.map(custom => (
        <button key={custom} onClick={() => toggle(custom)}
          style={{ padding: '0.3rem 0.7rem', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', border: '1.5px solid #1D9E75', background: '#E1F5EE', color: '#0F6E56', fontWeight: 600, width: 'auto', transition: 'all 0.12s' }}>
          {custom} ×
        </button>
      ))}
      {allowCustom && (addingCustom ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <input
            type="text" value={customInput} autoFocus
            onChange={e => setCustomInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); commitCustom(); }
              else if (e.key === 'Escape') { setAddingCustom(false); setCustomInput(''); }
            }}
            placeholder="Custom…"
            style={{
              padding: '0.25rem 0.65rem', fontSize: '0.78rem', borderRadius: 20,
              border: '1.5px solid #1D9E75', outline: 'none', fontFamily: 'inherit',
              minWidth: 120,
            }}
          />
          <button type="button" onMouseDown={(e) => { e.preventDefault(); commitCustom(); }}
            disabled={!customInput.trim()}
            style={{
              padding: '0.25rem 0.65rem', fontSize: '0.78rem', borderRadius: 16,
              border: 'none', background: customInput.trim() ? '#1D9E75' : '#ccc',
              color: 'white', fontWeight: 700,
              cursor: customInput.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit', width: 'auto',
            }}>
            Add
          </button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); setAddingCustom(false); setCustomInput(''); }}
            style={{
              border: 'none', background: 'none', color: '#888',
              cursor: 'pointer', fontSize: '0.9rem', width: 'auto', padding: '0 4px',
              fontFamily: 'inherit',
            }}>✕</button>
        </span>
      ) : (
        <button onClick={() => setAddingCustom(true)}
          style={{ padding: '0.3rem 0.7rem', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', border: '1.5px dashed #aaa', background: 'white', color: '#888', width: 'auto' }}>
          + Other
        </button>
      ))}
    </div>
  );
}

function RatingInput({ value, onChange, max = 5, lowLabel, highLabel }) {
  const hasValue = typeof value === 'number' && value >= 1;
  return (
    <div style={{ marginTop: '0.4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {lowLabel && (
          <span style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap', minWidth: 60, textAlign: 'right' }}>
            1 = {lowLabel}
          </span>
        )}
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {Array.from({ length: max }, (_, i) => i + 1).map(n => (
            <button key={n} type="button" onClick={() => onChange(value === n ? null : n)}
              title={`Rate ${n} of ${max}`}
              style={{
                width: 34, height: 34, borderRadius: '50%',
                border: '1.5px solid',
                borderColor: hasValue && value >= n ? '#1D9E75' : '#d0d0d6',
                background: hasValue && value >= n ? '#1D9E75' : 'white',
                color: hasValue && value >= n ? 'white' : '#888',
                fontSize: '0.82rem', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                flexShrink: 0, padding: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1,
              }}>
              {n}
            </button>
          ))}
        </div>
        {highLabel && (
          <span style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
            {max} = {highLabel}
          </span>
        )}
      </div>
      {!hasValue && (lowLabel || highLabel) && (
        <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: 4, fontStyle: 'italic' }}>
          Click a number to rate · not yet rated
        </div>
      )}
    </div>
  );
}

function SplitEditor({ field, value, onChange, sectionData }) {
  const cfg = field.widget_config || {};
  const opts = cfg.options || [];
  const allowAbsolute = !!cfg.allowAbsolute;

  const initial = value || { mode: allowAbsolute ? 'percentage' : 'percentage', splits: {}, absoluteValues: {}, customOpts: [] };
  const mode = initial.mode || 'percentage';
  const splits = initial.splits || {};
  const absoluteValues = initial.absoluteValues || {};
  const customOpts = initial.customOpts || [];

  const allOpts = [...opts, ...customOpts];
  const active = mode === 'percentage' ? splits : absoluteValues;
  const activeKeys = Object.keys(active);
  const total = Object.values(active).reduce((s, v) => s + (Number(v) || 0), 0);

  // Pull target total from another field if configured (e.g. monthly_fixed_costs)
  const targetTotal = (() => {
    if (mode !== 'absolute' || !cfg.absoluteTotalFrom || !sectionData) return null;
    const raw = sectionData[cfg.absoluteTotalFrom];
    if (!raw) return null;
    const m = String(raw).match(/([\d,]+(?:\.\d+)?)\s*(k|m)?/i);
    if (!m) return null;
    const n = parseFloat(m[1].replace(/,/g, ''));
    if (isNaN(n)) return null;
    const suffix = (m[2] || '').toLowerCase();
    if (suffix === 'k') return n * 1000;
    if (suffix === 'm') return n * 1000000;
    return n;
  })();

  const emit = (next) => onChange({ mode, splits, absoluteValues, customOpts, ...next });

  const updateValue = (key, v) => {
    const newV = Math.max(0, Number(v) || 0);
    if (mode === 'percentage') emit({ splits: { ...splits, [key]: Math.min(100, newV) } });
    else emit({ absoluteValues: { ...absoluteValues, [key]: newV } });
  };

  const addOption = (key) => {
    if (mode === 'percentage') {
      const existing = Object.keys(splits).filter(k => splits[k] > 0);
      if (existing.length === 0) return emit({ splits: { [key]: 100 } });
      const share = 20;
      const available = 100 - share;
      const existingTotal = existing.reduce((s, k) => s + splits[k], 0);
      const next = { ...splits };
      existing.forEach(k => { next[k] = Math.max(1, Math.round((splits[k] / existingTotal) * available)); });
      next[key] = share;
      emit({ splits: next });
    } else {
      emit({ absoluteValues: { ...absoluteValues, [key]: 1000 } });
    }
  };

  const removeOption = (key) => {
    if (mode === 'percentage') {
      const { [key]: _, ...rest } = splits;
      emit({ splits: rest });
    } else {
      const { [key]: _, ...rest } = absoluteValues;
      emit({ absoluteValues: rest });
    }
  };

  const switchMode = (next) => emit({ mode: next });

  const [customInput, setCustomInput] = useState('');
  const [addingCustom, setAddingCustom] = useState(false);

  const handleAddCustom = () => {
    const label = customInput.trim();
    if (!label) { setAddingCustom(false); setCustomInput(''); return; }
    const safeKey = 'custom_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
    if (!safeKey || allOpts.find(o => o.key === safeKey)) { setAddingCustom(false); setCustomInput(''); return; }
    const newCustomOpts = [...customOpts, { key: safeKey, label }];
    const seed = mode === 'percentage' ? 20 : 1000;
    const updatedValues = mode === 'percentage'
      ? { splits: { ...splits, [safeKey]: seed } }
      : { absoluteValues: { ...absoluteValues, [safeKey]: seed } };
    onChange({ mode, splits, absoluteValues, customOpts: newCustomOpts, ...updatedValues });
    setAddingCustom(false); setCustomInput('');
  };

  const palette = ['hsl(142,70%,45%)','hsl(219,70%,45%)','hsl(262,70%,45%)','hsl(39,70%,45%)','hsl(335,70%,45%)','hsl(185,70%,45%)','hsl(310,70%,45%)'];

  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>{field.label}{field.required ? ' *' : ''}</div>

      {allowAbsolute && (
        <div style={{ marginBottom: '0.75rem', display: 'inline-flex', border: '1px solid #e0e0e0', borderRadius: 6, padding: 2, background: '#f8f9fc' }}>
          <button type="button" onClick={() => switchMode('percentage')}
            style={{ padding: '0.3rem 0.7rem', fontSize: '0.72rem', border: 'none', borderRadius: 4, background: mode === 'percentage' ? '#1D9E75' : 'transparent', color: mode === 'percentage' ? 'white' : '#666', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', width: 'auto' }}>
            %
          </button>
          <button type="button" onClick={() => switchMode('absolute')}
            style={{ padding: '0.3rem 0.7rem', fontSize: '0.72rem', border: 'none', borderRadius: 4, background: mode === 'absolute' ? '#1D9E75' : 'transparent', color: mode === 'absolute' ? 'white' : '#666', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', width: 'auto' }}>
            £
          </button>
        </div>
      )}

      {activeKeys.length > 0 && (
        <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', border: '1px solid #e0e0e0', marginBottom: '0.75rem' }}>
          {activeKeys.map((key, idx) => {
            const v = active[key] || 0;
            const pct = mode === 'percentage' ? v : (targetTotal ? (v / targetTotal) * 100 : (total > 0 ? (v / total) * 100 : 0));
            const opt = allOpts.find(o => o.key === key);
            return (
              <div key={key} style={{ width: `${pct}%`, background: palette[idx % palette.length], display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
                {pct > 12 && <span style={{ fontSize: 10, fontWeight: 500, color: 'white', padding: '0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt?.label}</span>}
              </div>
            );
          })}
        </div>
      )}

      {activeKeys.map((key, idx) => {
        const opt = allOpts.find(o => o.key === key);
        const v = active[key] || 0;
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: palette[idx % palette.length], flexShrink: 0 }} />
            <span style={{ fontSize: '0.75rem', color: '#1a1a2e', flex: 1, fontWeight: 500 }}>{opt?.label || key}</span>
            {mode === 'absolute' && <span style={{ fontSize: '0.72rem', color: '#888' }}>£</span>}
            <input type="number" min="0" max={mode === 'percentage' ? 100 : undefined} step={mode === 'percentage' ? 1 : 100}
              value={Math.round(v) || ''} placeholder="0"
              onChange={(e) => updateValue(key, e.target.value)}
              style={{ width: 64, fontSize: '0.75rem', fontWeight: 500, padding: '3px 6px', border: '1px solid #ddd', borderRadius: 5, textAlign: 'center', fontFamily: 'inherit' }} />
            {mode === 'percentage' && <span style={{ fontSize: '0.7rem', color: '#aaa', minWidth: 12 }}>%</span>}
            <button type="button" onClick={() => removeOption(key)}
              style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer', fontSize: '0.95rem', width: 'auto', padding: '0 4px' }}>×</button>
          </div>
        );
      })}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: '0.5rem', marginBottom: '0.5rem' }}>
        {allOpts.filter(o => !activeKeys.includes(o.key)).map(o => (
          <button key={o.key} type="button" onClick={() => addOption(o.key)}
            style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem', borderRadius: 20, background: 'white', color: '#555', cursor: 'pointer', border: '1.5px solid #e0e0e0', fontFamily: 'inherit', width: 'auto' }}>
            + {o.label}
          </button>
        ))}
        {addingCustom ? (
          <input type="text" autoFocus value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustom(); } else if (e.key === 'Escape') { setAddingCustom(false); setCustomInput(''); } }}
            onBlur={handleAddCustom} placeholder="Category name…"
            style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem', borderRadius: 20, border: '1.5px solid #1D9E75', outline: 'none', fontFamily: 'inherit', minWidth: 140 }} />
        ) : (
          <button type="button" onClick={() => setAddingCustom(true)}
            style={{ padding: '0.25rem 0.65rem', fontSize: '0.72rem', borderRadius: 20, background: 'white', color: '#888', cursor: 'pointer', border: '1.5px dashed #ccc', fontFamily: 'inherit', width: 'auto' }}>
            + Add custom
          </button>
        )}
      </div>

      <div style={{ fontSize: '0.72rem', textAlign: 'right' }}>
        {mode === 'percentage' ? (
          <span style={{ color: Math.round(total) === 100 ? '#0F6E56' : '#BA7517' }}>
            Total: {Math.round(total)}%{Math.round(total) === 100 ? ' ✓' : ' (aim for 100%)'}
          </span>
        ) : targetTotal ? (
          <span style={{ color: total > targetTotal ? '#A32D2D' : total < targetTotal ? '#666' : '#0F6E56' }}>
            Allocated: £{total.toLocaleString()} of £{targetTotal.toLocaleString()}
            {total > targetTotal && <> — £{(total - targetTotal).toLocaleString()} over</>}
            {total < targetTotal && <> — £{(targetTotal - total).toLocaleString()} unallocated</>}
            {total === targetTotal && <> ✓</>}
          </span>
        ) : (
          <span style={{ color: '#666' }}>Total: £{total.toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}

function FieldEditor({ field, value, onChange, sectionData }) {
  // Conditional field rendering — skip if shown_when condition fails
  if (field.shown_when && sectionData) {
    const cond = field.shown_when;
    const other = sectionData[cond.field];
    if (cond.any_of_keys && other) {
      // For split fields, check if any of the specified keys have share > 0
      const splits = other.splits || {};
      const hasAny = cond.any_of_keys.some(k => (splits[k] || 0) > 0);
      if (!hasAny) return null;
    }
  }

  if (field.type === 'split') return (
    <SplitEditor field={field} value={value} onChange={onChange} sectionData={sectionData} />
  );
  if (field.type === 'chips') {
    // Constrained chips — filter options based on a split-field's active keys
    let options = field.options;
    if (field.constrained_by && sectionData) {
      const parent = sectionData[field.constrained_by];
      if (parent?.active && field.constraint_source) {
        // Map active split keys → labels, filter chip options to only active ones
        const activeLabels = new Set(
          field.constraint_source
            .filter(opt => parent.active.includes(opt.key))
            .map(opt => opt.label)
        );
        const filtered = field.options.filter(label => activeLabels.has(label));
        if (filtered.length > 0) options = filtered;
      }
    }
    return (
      <div style={{ marginBottom: '1.1rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e' }}>{field.label}{field.required ? ' *' : ''}</div>
        {field.explainer && (
          <div style={{ fontSize: '0.74rem', color: '#666', marginTop: 4, lineHeight: 1.5 }}>{field.explainer}</div>
        )}
        {field.max && <div style={{ fontSize: '0.68rem', color: '#aaa', marginTop: 2 }}>Select up to {field.max}</div>}
        <ChipSelect options={options} value={value} onChange={onChange} multi={field.multi} max={field.max} allowCustom={!!field.allow_custom} />
      </div>
    );
  }
  if (field.type === 'rating') return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e' }}>{field.label}{field.required ? ' *' : ''}</div>
      {field.explainer && (
        <div style={{ fontSize: '0.74rem', color: '#666', marginTop: 4, lineHeight: 1.5 }}>{field.explainer}</div>
      )}
      <RatingInput value={value} onChange={onChange} max={field.max} lowLabel={field.low_label} highLabel={field.high_label} />
    </div>
  );
  if (field.type === 'textarea') {
    // Auto-grow: count line breaks + estimate wrap lines so long content
    // is fully visible without scroll bars or manual resize. Capped at
    // 16 rows to keep the field from dominating the page.
    const v = value || '';
    const lineBreaks = (v.match(/\n/g) || []).length;
    const wrapEstimate = Math.ceil((v.length || 1) / 80);
    const computedRows = Math.max(3, Math.min(16, lineBreaks + wrapEstimate + 1));
    return (
      <div style={{ marginBottom: '1.1rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{field.label}{field.required ? ' *' : ''}</div>
        <textarea value={v} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} rows={computedRows}
          style={{ width: '100%', fontSize: '0.82rem', padding: '0.45rem 0.6rem', borderRadius: 7, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
      </div>
    );
  }
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{field.label}{field.required ? ' *' : ''}</div>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''}
        style={{ width: '100%', fontSize: '0.82rem', padding: '0.45rem 0.6rem', borderRadius: 7, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box' }} />
    </div>
  );
}

// ── Future-trend annotation block ─────────────────────────────────
// Five Market & Model sections capture how today's picture will hold
// (or shift) into the future, plus a separate appendix for entirely
// new entries to expect by the vision horizon. The trend block is the
// per-entry / per-section "will this continue + how will it shift +
// why" annotation. Layout — three stacked rows, all visible together;
// the trend chips collapse when the leader says "No / material change"
// because directional shift no longer applies.
function FutureTrendBlock({ config, value, onChange, colour }) {
  const v = value || {};
  const cfg = config || {};
  // Hide the trend chips when the leader explicitly says "No / material
  // change" — the last option in will_continue_options is the negative
  // pick, where directional shift doesn't apply.
  const willOpts = cfg.will_continue_options || [];
  const negativePick = willOpts[willOpts.length - 1];
  const trendChipsShown = !!v.will_continue && v.will_continue !== negativePick && Array.isArray(cfg.trend_options) && cfg.trend_options.length > 0;
  const reasoningRows = Math.max(2, Math.min(8, Math.ceil((v.reasoning || '').length / 80) + 1));
  return (
    <div style={{ borderTop: '1px dashed #d8d8d8', marginTop: '0.9rem', paddingTop: '0.9rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: colour || '#1a1a2e', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        ↗ {cfg.question || 'Future outlook'}
      </div>
      <div style={{ marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '0.76rem', color: '#444', fontWeight: 600 }}>
          {cfg.will_continue_question || 'Will today\'s picture hold?'}
        </div>
        <ChipSelect options={willOpts} value={v.will_continue} onChange={x => onChange({ ...v, will_continue: x })} multi={false} />
      </div>
      {trendChipsShown && (
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{ fontSize: '0.76rem', color: '#444', fontWeight: 600 }}>
            {cfg.trend_question || 'How will it shift?'}
          </div>
          <ChipSelect options={cfg.trend_options} value={v.trend} onChange={x => onChange({ ...v, trend: x })} multi={!!cfg.trend_multi} />
        </div>
      )}
      {!!v.will_continue && (
        <div>
          <div style={{ fontSize: '0.76rem', color: '#444', fontWeight: 600, marginBottom: 3 }}>
            {cfg.reasoning_label || "What's driving it"}
          </div>
          <textarea value={v.reasoning || ''} onChange={e => onChange({ ...v, reasoning: e.target.value })}
            placeholder={cfg.reasoning_placeholder || ''}
            rows={reasoningRows}
            style={{ width: '100%', fontSize: '0.82rem', padding: '0.45rem 0.6rem', borderRadius: 7, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
        </div>
      )}
    </div>
  );
}

// ── Future-additions appendix ─────────────────────────────────────
// Section-level repeatable list of net-new items the leader expects
// by their vision horizon. Anchors to canvas.vision.time_horizon_years
// so the "by year X" framing is real, not implicit. The leader can
// override the year per entry. Empty entries are fine — section
// completeness doesn't require any future_additions content; this is
// pure capture, not gating.
function FutureAdditionsAppendix({ config, value, onChange, colour, visionHorizonYear }) {
  const cfg = config || {};
  const entries = (value && Array.isArray(value.entries)) ? value.entries : [];
  const update = (idx, key, val) => {
    const next = [...entries];
    next[idx] = { ...next[idx], [key]: val };
    onChange({ entries: next });
  };
  const add = () => onChange({ entries: [...entries, { horizon_year: visionHorizonYear || '' }] });
  const remove = (idx) => onChange({ entries: entries.filter((_, i) => i !== idx) });
  return (
    <div style={{ borderTop: '1px solid #ececec', marginTop: '1.5rem', paddingTop: '1.1rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.4rem' }}>
        <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: colour || '#1a1a2e', margin: 0 }}>
          ＋ {cfg.label || 'Future additions'}
        </h3>
        {visionHorizonYear && (
          <span style={{ fontSize: '0.7rem', color: '#888', fontWeight: 600, padding: '0.15rem 0.55rem', background: '#f4f4f4', borderRadius: 12 }}>
            Vision horizon: {visionHorizonYear}
          </span>
        )}
      </div>
      {cfg.intro && (
        <p style={{ fontSize: '0.78rem', color: '#666', margin: '0 0 0.85rem', lineHeight: 1.5 }}>{cfg.intro}</p>
      )}
      {entries.map((entry, idx) => (
        <div key={idx} style={{ background: '#fafafa', border: '1px solid #ececec', borderRadius: 8, padding: '0.8rem 0.95rem', marginBottom: '0.6rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: colour || '#1a1a2e' }}>
              {cfg.name_label || 'Item'} {idx + 1}
            </div>
            <button type="button" onClick={() => remove(idx)} style={{ background: 'none', border: 'none', color: '#bbb', fontSize: '0.75rem', cursor: 'pointer', width: 'auto', padding: 0 }}>× Remove</button>
          </div>
          <input type="text" value={entry.name || ''} onChange={e => update(idx, 'name', e.target.value)}
            placeholder={cfg.name_placeholder || ''}
            style={{ width: '100%', fontSize: '0.82rem', padding: '0.45rem 0.6rem', borderRadius: 7, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '0.5rem' }} />
          <textarea value={entry.reasoning || ''} onChange={e => update(idx, 'reasoning', e.target.value)}
            placeholder={cfg.reasoning_placeholder || ''}
            rows={Math.max(2, Math.min(6, Math.ceil((entry.reasoning || '').length / 80) + 1))}
            style={{ width: '100%', fontSize: '0.82rem', padding: '0.45rem 0.6rem', borderRadius: 7, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5, marginBottom: '0.5rem' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>By year</span>
            <input type="number" min="2024" max="2099" value={entry.horizon_year || ''}
              onChange={e => update(idx, 'horizon_year', e.target.value ? parseInt(e.target.value, 10) : '')}
              placeholder={visionHorizonYear || 'YYYY'}
              style={{ width: 80, fontSize: '0.78rem', padding: '0.3rem 0.5rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            {visionHorizonYear && !entry.horizon_year && (
              <span style={{ fontSize: '0.68rem', color: '#aaa', fontStyle: 'italic' }}>(defaults to vision horizon)</span>
            )}
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        style={{ fontSize: '0.78rem', padding: '0.4rem 0.95rem', borderRadius: 18, border: `1.5px dashed ${colour || '#999'}`, background: 'white', color: colour || '#666', cursor: 'pointer', width: 'auto', fontFamily: 'inherit', fontWeight: 600 }}>
        + {cfg.name_label ? `Add ${String(cfg.name_label).toLowerCase()}` : 'Add another'}
      </button>
    </div>
  );
}

// ── Read the vision time-horizon as an absolute year ──────────────
// Vision stores `time_horizon_years` as a relative string ("5"). We
// resolve it to an absolute calendar year at render time so future
// additions can read "By 2031" by default rather than "in 5 years"
// (which becomes wrong the moment the canvas is re-opened next year).
function readVisionHorizonYear() {
  try {
    const user = JSON.parse(localStorage.getItem('guruone_user') || '{}');
    const userKey = user?.id ? `user_${user.id}` : 'default';
    const raw = localStorage.getItem('guruone_strategy');
    if (!raw) return null;
    const all = JSON.parse(raw);
    const scoped = (all[userKey] && typeof all[userKey] === 'object') ? all[userKey] : null;
    const yrsRaw = scoped?.vision?.time_horizon_years;
    if (!yrsRaw) return null;
    const m = String(yrsRaw).match(/(\d+)/);
    if (!m) return null;
    const yrs = parseInt(m[1], 10);
    if (isNaN(yrs) || yrs < 1 || yrs > 50) return null;
    return new Date().getFullYear() + yrs;
  } catch { return null; }
}

function RepeatableEditor({ section, data, onChange }) {
  const entries = (data && data.entries) ? data.entries : [{}];
  const perEntryTrend = section.future_trend && section.future_trend.per_entry;
  const update = (idx, field, val) => { const next = [...entries]; next[idx] = { ...next[idx], [field]: val }; onChange({ entries: next }); };
  const add = () => onChange({ entries: [...entries, {}] });
  const remove = (idx) => { if (entries.length <= 1) return; onChange({ entries: entries.filter((_, i) => i !== idx) }); };
  return (
    <div>
      {entries.map((entry, idx) => (
        <div key={idx} style={{ background: '#f9f9f9', borderRadius: 10, padding: '1rem', marginBottom: '0.85rem', border: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: section.colour }}>{section.title.replace(/s$/, '')} {idx + 1}</div>
            {entries.length > 1 && <button onClick={() => remove(idx)} style={{ background: 'none', border: 'none', color: '#ccc', fontSize: '0.75rem', cursor: 'pointer', width: 'auto', padding: 0 }}>× Remove</button>}
          </div>
          {section.fields.map(f => <FieldEditor key={f.key} field={f} value={entry[f.key]} onChange={v => update(idx, f.key, v)} sectionData={entry} />)}
          {perEntryTrend && (
            <FutureTrendBlock config={section.future_trend}
              value={entry._future} onChange={v => update(idx, '_future', v)}
              colour={section.colour} />
          )}
        </div>
      ))}
      <button onClick={add} style={{ fontSize: '0.8rem', padding: '0.45rem 1rem', borderRadius: 20, border: `1.5px dashed ${section.colour}`, background: 'white', color: section.colour, cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
        {section.addLabel || '+ Add another'}
      </button>
    </div>
  );
}

// Build a hover tooltip from a saved _evaluation (headline + first improvement)
// or a sensible fallback when no evaluation exists yet.
function starsTooltip(sectionData) {
  const e = sectionData?._evaluation;
  if (e && typeof e.score === 'number') {
    const lines = [`${e.score}/5 — ${e.headline || 'Section reviewed.'}`];
    if (Array.isArray(e.improvements) && e.improvements.length > 0) {
      lines.push('To improve: ' + e.improvements[0]);
    }
    return lines.join('\n');
  }
  return 'Not yet rated — complete the Explore deep dive to get a quality review.';
}

// Quality score — strictly from the LLM evaluation. Returns null when
// no _evaluation is present so the Stars renderer can show an empty
// 5-star skeleton with a "Not yet rated" tooltip. Stars represent
// quality only; completeness is signalled by the StatusBadge pill.
function getSectionQuality(sectionData) {
  const e = sectionData?._evaluation;
  if (e && typeof e.score === 'number') {
    return Math.max(0, Math.min(5, Math.round(e.score)));
  }
  return null;
}

// Stars render: filled to `score` when numeric; all hollow when
// score is null (the "not yet rated" placeholder Glenn signed off
// on). Hollow stars use a slightly lighter colour to read as a
// skeleton rather than a "0/5" rating.
function Stars({ score, max = 5, title }) {
  const isRated = typeof score === 'number';
  const fillCount = isRated ? score : 0;
  return (
    <div
      style={{ display: 'flex', gap: 2, cursor: title ? 'help' : 'default', opacity: isRated ? 1 : 0.55 }}
      title={title || undefined}
    >
      {Array.from({ length: max }, (_, i) => (
        <span key={i} style={{ color: i < fillCount ? '#FFB300' : '#e0e0e0', fontSize: '0.75rem' }}>★</span>
      ))}
    </div>
  );
}

// EvaluationModal lives in components/EvaluationModal.js as a shared
// component used by both StrategyCanvas and OperatingModelCanvas.

// Four-state status badge. Replaces the older "FILL pip" tier pill +
// the duplicated "In progress / Complete" top-right pill that used to
// live on each section card. Levels map directly to getSectionTier:
//   0 → Not started   (no fields filled)
//   1 → In progress   (some fields filled, below the all-fields gate)
//   2 → Basic         (all fields filled via Quick Select / paste)
//   3 → Detailed      (all fields + a deep-dive narrative captured)
// Quality lives on the Stars (driven by the LLM _evaluation score)
// and is rendered separately. Status here is purely completeness.
function StatusBadge({ tier, compact = false }) {
  if (!tier) return null;
  // Labels are self-explanatory at both densities — "Fields captured"
  // and "Deep-dive done" read clearly without needing the leader to
  // hover for a tooltip. The compact form trims only "captured" → ""
  // when space is tight.
  const config = [
    { label: 'Not started',     short: 'Not started',     bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' },
    { label: 'In progress',     short: 'In progress',     bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' },
    { label: 'Fields captured', short: 'Fields captured', bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' },
    { label: 'Deep-dive done',  short: 'Deep-dive done',  bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' },
  ][tier.level || 0];
  const tooltip = [
    'No fields filled yet.',
    'Some fields filled — keep going to complete the section.',
    'All fields captured via Quick Select. Run an Explore session for a deep-dive review.',
    'Deep-dive captured — all fields plus a narrative from an Explore conversation.',
  ][tier.level || 0];
  return (
    <span title={tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '0.22rem 0.6rem', borderRadius: 14,
        background: config.bg, color: config.color,
        border: `1px solid ${config.border}`,
        fontSize: '0.62rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}>
      {compact ? config.short : config.label}
    </span>
  );
}

// ── Mode selector ─────────────────────────────────────────────────
function ModeSelector({ section, onSelect, onBack, existing }) {
  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem' }}>
        ← Back to canvas
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '1.75rem', color: section.colour }}>{section.icon}</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{section.description}</p>
        </div>
      </div>
      <div style={{ height: 1, background: '#f0f0f0', margin: '1.25rem 0' }} />

      {existing && (
        <div style={{ background: '#f0faf6', border: '1px solid #b8e4d4', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1.25rem', fontSize: '0.8rem', color: '#0F6E56' }}>
          ✓ You have existing data for this section — any journey will let you update it.
        </div>
      )}

      {/* Explore — primary journey, full width */}
      <button onClick={() => onSelect('explore')}
        style={{ width: '100%', textAlign: 'left', padding: '1.25rem 1.5rem', borderRadius: 12, border: '2px solid #185FA5', background: '#f0f6ff', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '0.85rem', transition: 'all 0.15s', display: 'block' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#e0eeff'; e.currentTarget.style.borderColor = '#0D4E8A'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#f0f6ff'; e.currentTarget.style.borderColor = '#185FA5'; }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ fontSize: '1.3rem' }}>◎</span>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#185FA5' }}>Explore</span>
            <span style={{ fontSize: '0.65rem', color: '#185FA5', fontWeight: 700, padding: '2px 8px', background: '#185FA520', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recommended</span>
          </div>
          <span style={{ fontSize: '0.65rem', color: '#185FA5', fontWeight: 600, padding: '2px 8px', background: '#185FA515', borderRadius: 20 }}>~15 min</span>
        </div>
        <div style={{ fontSize: '0.82rem', color: '#1a2e4a', lineHeight: 1.6, maxWidth: '85%' }}>
          A guided conversation with AI that works through your thinking, challenges surface-level answers, and builds the richest possible picture of this section. The most meaningful journey on the canvas — and the one that makes Decision Guru most powerful.
        </div>
      </button>

      {/* Context-loading group label */}
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
        Or — load from existing materials
      </div>

      {/* Quick Select + Infer side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <button onClick={() => onSelect('quick')}
          style={{ textAlign: 'left', padding: '1rem 1.1rem', borderRadius: 10, border: `1.5px solid ${section.colour}33`, background: 'white', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
          onMouseEnter={e => { e.currentTarget.style.background = section.colour + '08'; e.currentTarget.style.borderColor = section.colour; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = section.colour + '33'; }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '1.1rem', color: section.colour }}>⚡</span>
            <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, padding: '2px 7px', background: '#f5f5f5', borderRadius: 20 }}>~2 min</span>
          </div>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e' }}>Quick Select</div>
          <div style={{ fontSize: '0.75rem', color: '#777', lineHeight: 1.5 }}>
            Pick from pre-defined options to load context quickly. Good for sections you've already thought through, or when you want to get up and running fast.
          </div>
        </button>

        <button onClick={() => onSelect('infer')}
          style={{ textAlign: 'left', padding: '1rem 1.1rem', borderRadius: 10, border: '1.5px solid #9B6B3A33', background: 'white', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#9B6B3A08'; e.currentTarget.style.borderColor = '#9B6B3A'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#9B6B3A33'; }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '1.1rem', color: '#9B6B3A' }}>↑</span>
            <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, padding: '2px 7px', background: '#f5f5f5', borderRadius: 20 }}>~5 min</span>
          </div>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e' }}>Infer from document</div>
          <div style={{ fontSize: '0.75rem', color: '#777', lineHeight: 1.5 }}>
            Paste text from a business plan, pitch deck, or website. AI reads it and suggests values — you review before saving. Good starting point before an Explore conversation.
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Quick Select editor ───────────────────────────────────────────
function QuickEditor({ section, sectionData, onSave, onBack, onDecision, onPatch, onSwitchToExplore, onSwitchToInfer }) {
  const [local, setLocal] = useState(sectionData || (section.repeatable ? { entries: [{}] } : {}));
  // Quality score — null if not yet evaluated, so Stars renders an
  // empty skeleton with a "not yet rated" tooltip.
  const qualityScore = getSectionQuality(local);
  const hasEval = !!(local?._evaluation && typeof local._evaluation.score === 'number');
  const [reviewOpen, setReviewOpen] = useState(false);
  // The review modal's stage is local so Re-evaluate can flip it
  // between 'done' and 'evaluating' without unmounting the modal.
  const [reviewStage, setReviewStage] = useState('done');
  const update = (key, val) => setLocal(prev => ({ ...prev, [key]: val }));

  // Re-runs the evaluator against the currently-saved fields. No
  // conversation needed — useful when the leader wants a fresh review
  // (e.g. after the evaluator prompt has been improved) without
  // redoing Explore. Persists the new evaluation via onPatch so the
  // canvas reflects it and the server save picks it up on the next
  // debounce.
  async function handleReEvaluate() {
    if (!local?._evaluation) return;
    setReviewStage('evaluating');
    try {
      const g = SECTION_GUIDANCE?.[section.key] || {};
      const guidanceText = [
        g.objective && `Objective: ${g.objective}`,
        g.good_answer_looks_like && `Good answer looks like: ${g.good_answer_looks_like}`,
        g.probe_deeper_on && `Probe deeper on: ${g.probe_deeper_on}`,
      ].filter(Boolean).join('\n\n');
      const fieldsSchema = (section.fields || []).map(f => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: !!f.required,
        options: Array.isArray(f.options) ? f.options : undefined,
      }));
      const res = await axios.post(`${API}/evaluate-section`, {
        sectionKey: section.key,
        sectionTitle: section.title,
        sectionData: local,
        sectionFields: fieldsSchema,
        repeatable: !!section.repeatable,
        transcript: local._explore_transcript || null,
        guidance: guidanceText,
      });
      const evaluation = res.data?.evaluation;
      if (evaluation) {
        const stamped = { ...evaluation, evaluatedAt: new Date().toISOString() };
        setLocal(prev => ({ ...prev, _evaluation: stamped }));
        if (onPatch) onPatch({ _evaluation: stamped });
        setReviewStage('done');
        track('section_re_evaluated', { section: section.key, score: stamped.score });
      } else {
        setReviewStage('done');
      }
    } catch (err) {
      console.warn('Re-evaluate failed:', err.message);
      setReviewStage('done');
    }
  }
  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem' }}>
        ← Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '1.75rem', color: section.colour }}>{section.icon}</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Quick Select — choose from pre-defined options</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Stars score={qualityScore} title={starsTooltip(local)} />
          <StatusBadge tier={getSectionTier(section, local)} />
          {hasEval && (
            <button onClick={() => { setReviewStage('done'); setReviewOpen(true); }}
              style={{
                background: 'none', border: 'none', color: section.colour,
                fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', padding: '0.15rem 0.4rem', width: 'auto',
                textDecoration: 'underline', textUnderlineOffset: 2,
              }}>
              View review
            </button>
          )}
        </div>
      </div>
      {reviewOpen && hasEval && (
        <EvaluationModal
          section={section}
          evalState={{ stage: reviewStage, evaluation: local._evaluation }}
          readOnly
          onCancel={() => setReviewOpen(false)}
          onReEvaluate={handleReEvaluate}
        />
      )}
      {/* Action row — Revise / Upload affordances on completed sections.
          Glenn's feedback: re-entering a completed card was sending him
          via ModeSelector then via entry_choice on Explore. From here
          the leader can edit fields inline, jump straight back into a
          deep-dive conversation (no entry-choice screen), or upload
          new material to refresh. */}
      {(onSwitchToExplore || onSwitchToInfer) && (
        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
          {onSwitchToExplore && (
            <button onClick={onSwitchToExplore}
              style={{
                padding: '0.4rem 0.85rem', borderRadius: 18,
                background: 'white', color: '#185FA5',
                border: '1px solid #185FA5',
                fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap',
              }}>
              ◎ Revise with deep dive
            </button>
          )}
          {onSwitchToInfer && (
            <button onClick={onSwitchToInfer}
              style={{
                padding: '0.4rem 0.85rem', borderRadius: 18,
                background: 'white', color: '#9B6B3A',
                border: '1px solid #9B6B3A',
                fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap',
              }}>
              ↑ Upload new info
            </button>
          )}
        </div>
      )}
      <div style={{ height: 1, background: '#f0f0f0', margin: '1.25rem 0' }} />
      {sectionData?._narrative && (
        <div style={{ background: '#fafaf7', border: '1px solid #e8e4d4', borderRadius: 8, padding: '1rem 1.2rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', color: '#9B6B3A', textTransform: 'uppercase', marginBottom: 6 }}>Summary</div>
          <div style={{ fontSize: '0.88rem', color: '#1a1a2e', lineHeight: 1.55 }}>{sectionData._narrative}</div>
        </div>
      )}
      {section.repeatable ? (
        <RepeatableEditor section={section} data={local} onChange={next => setLocal(next)} />
      ) : (
        section.fields.map(f => <FieldEditor key={f.key} field={f} value={local[f.key]} onChange={v => update(f.key, v)} sectionData={local} />)
      )}
      {/* Section-level future-trend block (Competitors, GTM, Revenue,
          Cost). Per-entry trend lives inside RepeatableEditor for
          Customers / Differentiators and is wired separately. */}
      {section.future_trend && !section.future_trend.per_entry && (
        <FutureTrendBlock config={section.future_trend}
          value={local._future} onChange={v => update('_future', v)}
          colour={section.colour} />
      )}
      {/* Future-additions appendix (all 5 sections — repeatable list of
          net-new items by the leader's vision horizon). */}
      {section.future_additions && (
        <FutureAdditionsAppendix config={section.future_additions}
          value={local.future_additions} onChange={v => update('future_additions', v)}
          colour={section.colour} visionHorizonYear={readVisionHorizonYear()} />
      )}
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
        <button onClick={() => onSave(local)} style={{ flex: 2, padding: '0.75rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save section</button>
        {section.hasDecision && (
          <button onClick={() => onDecision(section, local)} style={{ flex: 2, padding: '0.75rem', borderRadius: 8, background: 'white', color: '#BA7517', border: '1.5px dashed #BA7517', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Work through a decision →</button>
        )}
        <button onClick={onBack} style={{ flex: 1, padding: '0.75rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Chat message renderer ─────────────────────────────────────────
// Renders AI (and user) chat messages with basic formatting:
// double newlines → paragraph breaks, **bold**, numbered/bullet lists.
// Keeps it readable without importing a full markdown library.
function ChatMessage({ content, role }) {
  if (role === 'user') {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>;
  }
  // AI message — parse into segments
  const paragraphs = content.split(/\n{2,}/);
  return (
    <div>
      {paragraphs.map((para, pi) => {
        const lines = para.split('\n').filter(l => l.trim());
        if (lines.length === 0) return null;
        // Detect list block (lines starting with - / * / digit.)
        const isList = lines.every(l => /^(\s*[-*•]\s+|\d+\.\s+)/.test(l));
        if (isList) {
          return (
            <ul key={pi} style={{ margin: pi === 0 ? '0 0 0.5rem 0' : '0.5rem 0 0.5rem 0', paddingLeft: '1.25rem' }}>
              {lines.map((line, li) => {
                const text = line.replace(/^(\s*[-*•]\s+|\d+\.\s+)/, '');
                return <li key={li} style={{ fontSize: '0.88rem', lineHeight: 1.6, marginBottom: 2 }}>{renderInline(text)}</li>;
              })}
            </ul>
          );
        }
        // Regular paragraph — join lines with space
        const text = lines.join(' ');
        return (
          <p key={pi} style={{ margin: pi === 0 ? '0 0 0.5rem 0' : '0.5rem 0', lineHeight: 1.65, fontSize: '0.88rem' }}>
            {renderInline(text)}
          </p>
        );
      })}
    </div>
  );
}

// Renders **bold** inline, returns array of React nodes
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// Q1/Q2 card pattern removed — the dialogue now renders every AI reply
// as a single chat bubble. The parseQuestions parser and QuestionCard
// component that used to drive the two-step UX have been retired.

// ── Values chip picker ─────────────────────────────────────────────
// Shown before the AI dialogue for the Values section so the leader
// declares which values they observe up-front. The dialogue then probes
// each one (1–2 questions per value) rather than fishing for them.
function ValueChipPicker({ section, onBack, onSubmit }) {
  const [selected, setSelected] = useState([]);
  const [customAdds, setCustomAdds] = useState([]);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');

  const allChips = [...VALUE_THEMES, ...customAdds];

  function toggleChip(value) {
    setSelected(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  }
  function addCustom() {
    const v = customInput.trim();
    if (!v) { setAddingCustom(false); setCustomInput(''); return; }
    if (allChips.some(c => c.toLowerCase() === v.toLowerCase())) {
      // Already exists — just select it
      if (!selected.includes(v)) setSelected(prev => [...prev, v]);
    } else {
      setCustomAdds(prev => [...prev, v]);
      setSelected(prev => [...prev, v]);
    }
    setCustomInput('');
    setAddingCustom(false);
  }

  const canSubmit = selected.length >= 2;

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.25rem' }}>
        ← Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.8rem' }}>
        <span style={{ fontSize: '1.75rem', color: section.colour }}>{section.icon}</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Pick the values you observe — we'll probe each one</p>
        </div>
      </div>

      <div style={{ background: '#f9f9f9', borderRadius: 12, padding: '1.25rem 1.4rem', marginBottom: '1.25rem' }}>
        <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem', color: '#1a1a2e', fontWeight: 700 }}>
          Which values describe how this business actually operates?
        </h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#666', lineHeight: 1.55 }}>
          Pick the ones you genuinely observe in day-to-day behaviour or decisions — not the aspirational list. We'll explore each one to validate it and capture how it shows up. Aim for 3–6.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '0.85rem' }}>
          {allChips.map(v => {
            const isSel = selected.includes(v);
            return (
              <button key={v} type="button" onClick={() => toggleChip(v)}
                style={{
                  padding: '0.4rem 0.85rem', fontSize: '0.78rem', borderRadius: 20,
                  border: isSel ? '1.5px solid #7F77DD' : '1.5px solid #e0e0e0',
                  background: isSel ? '#efeefb' : 'white',
                  color: isSel ? '#3D38A1' : '#444',
                  fontWeight: isSel ? 600 : 500,
                  cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                }}>
                {v}
              </button>
            );
          })}
          {addingCustom ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="text" value={customInput} autoFocus
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); addCustom(); }
                  else if (e.key === 'Escape') { setAddingCustom(false); setCustomInput(''); }
                }}
                placeholder="Custom value…"
                style={{
                  padding: '0.35rem 0.85rem', fontSize: '0.78rem', borderRadius: 20,
                  border: '1.5px solid #7F77DD', outline: 'none', fontFamily: 'inherit',
                  minWidth: 140,
                }}
              />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); addCustom(); }}
                disabled={!customInput.trim()}
                title="Add value"
                style={{
                  padding: '0.32rem 0.7rem', fontSize: '0.8rem', borderRadius: 16,
                  border: 'none', background: customInput.trim() ? '#7F77DD' : '#ccc',
                  color: 'white', fontWeight: 700,
                  cursor: customInput.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit', width: 'auto',
                }}>
                Add
              </button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); setAddingCustom(false); setCustomInput(''); }}
                title="Cancel"
                style={{
                  border: 'none', background: 'none', color: '#888',
                  cursor: 'pointer', fontSize: '0.95rem', width: 'auto', padding: '0 4px',
                  fontFamily: 'inherit',
                }}>
                ✕
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setAddingCustom(true)}
              style={{
                padding: '0.4rem 0.85rem', fontSize: '0.78rem', borderRadius: 20,
                border: '1.5px dashed #aaa', background: 'white', color: '#888',
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              + Other
            </button>
          )}
        </div>

        <div style={{ fontSize: '0.78rem', color: '#666' }}>
          {selected.length === 0 && 'Select at least 2 to continue.'}
          {selected.length === 1 && '1 selected — pick at least one more.'}
          {selected.length >= 2 && `${selected.length} selected.`}
        </div>
      </div>

      <button onClick={() => onSubmit(selected)} disabled={!canSubmit}
        style={{
          padding: '0.7rem 1.4rem', borderRadius: 8,
          background: canSubmit ? '#7F77DD' : '#ccc',
          color: 'white', border: 'none', fontSize: '0.88rem', fontWeight: 600,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          fontFamily: 'inherit', width: 'auto',
        }}>
        Start exploring →
      </button>
    </div>
  );
}

// ── In-progress transcript persistence ─────────────────────────────
// Auto-save the live conversation to localStorage on every message
// change so that a crashed extract / blank screen / browser refresh
// never loses the leader's input. Cleared on successful save.
function inProgressKey(sectionKey) {
  try {
    const user = JSON.parse(localStorage.getItem('guruone_user'));
    const uid = user?.id ? `user_${user.id}` : 'default';
    return `guruone_explore_inprogress_${uid}_${sectionKey}`;
  } catch {
    return `guruone_explore_inprogress_default_${sectionKey}`;
  }
}
function readInProgress(sectionKey) {
  try {
    const raw = localStorage.getItem(inProgressKey(sectionKey));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeInProgress(sectionKey, payload) {
  try {
    localStorage.setItem(inProgressKey(sectionKey), JSON.stringify({
      ...payload,
      lastUpdated: new Date().toISOString(),
    }));
  } catch { /* quota / disabled — non-fatal */ }
}
function clearInProgress(sectionKey) {
  try { localStorage.removeItem(inProgressKey(sectionKey)); } catch { /* noop */ }
}

// ── Per-card quality rubrics — assessment phase ──────────────────────────────
// Strong / weak signals per section, used by buildSystemPrompt('assessment')
// to deliver an honest evaluation of the captured thinking and to name
// 1–3 underdeveloped areas the advisory phase can then engage with.
const SECTION_RUBRICS = {
  founder_purpose: {
    strong: [
      'Specific origin story with a concrete trigger — not a generic opportunity narrative ("I spotted a gap")',
      'Primary motivation defended with behavioural evidence — actual decisions made, not self-description',
      'Risk tolerance grounded in real decisions taken and avoided — not a self-rating without examples',
      "Time horizon that reflects the founder's actual life and planning window — not a default 'long-term'",
    ],
    weak_signals: [
      'Generic origin story — "I spotted a gap" or "I saw an opportunity" with no concrete trigger',
      'Self-described motivation without behavioural evidence or examples',
      'Risk self-rating with no concrete decisions to point to — taken or avoided',
      'Time horizon defaulting to "long-term" without personal reasoning behind it',
    ],
  },
  values: {
    strong: [
      'Up to 5 principles — more than 5 loses force',
      'Each phrased distinctively to this business — "we never hide bad news from clients, even when it costs us" rather than just "honesty"',
      'Each evidenced in actual behaviour — the founder can point to decisions or moments that prove it',
      'Each paired with a concrete non-negotiable: what the business would never do, with an example of it being tested',
    ],
    weak_signals: [
      'Generic list (Integrity, Excellence, Collaboration) — sounds inherited from corporate culture',
      'Values stated as universal abstractions without specificity about what they mean in this business',
      'No behavioural evidence — the founder cannot name a moment the value was tested or cost them something',
      'Values that appear aspirational rather than operational',
    ],
  },
  mission: {
    strong: [
      'Specific — not "make the world better" or "help businesses succeed"',
      'Active — describes what the business does for beneficiaries, not what it aspires to be',
      'References an external audience — customer group, community, profession, cause — not just commercial interest',
      'Credible given actual operations today',
      "Bigger than the founder's personal goals",
    ],
    weak_signals: [
      'Commercial goals dressed as mission — "to be the best", "to lead the market"',
      'Generic statements applicable to any business in the sector',
      'Mission that describes the product rather than the contribution',
      'Mission that only references internal audiences or founder goals',
    ],
  },
  vision: {
    strong: [
      'Concrete — includes a specific metric or picture, not vague aspiration',
      'Time-bound with a horizon the founder can hold in their head',
      'Consistent with mission — an expression of the mission reaching maturity',
      'Stretching but plausible',
      'Written in a voice the team can recognise — specific enough that a prospective hire could decide whether they want to be part of it',
    ],
    weak_signals: [
      'Vision that restates current operations at larger scale with no structural difference',
      "Time horizon extending beyond the founder's personal planning window",
      'Commercial vision disconnected from the stated mission',
      'Exit valuation as the destination — exit is an outcome, not a picture of what the business is',
    ],
  },
  value_proposition: {
    strong: [
      'Named problem specific enough that customers recognise themselves immediately',
      'Target customer expressed in terms the business can actually identify and reach — not "SMEs"',
      'Outcomes framed as what changes for the customer — not what the business delivers',
      'A clear, explicitly chosen market positioning (Reliable & efficient / Best-in-class / Tailored) that matches how the business actually wins',
      'Price positioning the founder can defend without discomfort',
    ],
    weak_signals: [
      'Feature list dressed as a value proposition',
      'Customer description that includes everyone',
      "Outcomes expressed as the founder's effort rather than the customer's gain",
      'No explicit market positioning — trying to claim all three simultaneously',
      'Price positioning stated without a justification the founder actually believes',
    ],
  },
  customers: {
    strong: [
      "2–4 genuinely distinct segments named in the founder's own internal language",
      'Revenue share quantified at least approximately for each segment',
      'Margin and complexity honestly assessed with supporting reasoning',
      'Each segment answers, at least implicitly, why that group buys from this business',
      'A clear view on strategic intent per segment — grow, protect, maintain, or de-prioritise',
    ],
    weak_signals: [
      '"All our customers are great" — no differentiation or prioritisation',
      'Segments defined only by company size — a proxy for something more useful',
      'Margin and complexity self-reports with no supporting reasoning',
      'No strategic intent per segment — treating all customers as equally worthy',
      'More than 4 segments that are not truly distinct',
    ],
  },
  competitors: {
    strong: [
      'A mix of alternative types — direct rivals, lower-cost, in-house, and "do nothing"',
      'Named examples per alternative type where possible',
      'An honest win/lose pattern including recent losses — not just wins reported',
      'A clear articulation of what makes the business win and what makes it lose — these should differ',
      'Win reasons internally consistent with claimed differentiators',
    ],
    weak_signals: [
      "\"We don't really have competitors\" — almost always wrong",
      'Only naming direct rivals, missing substitutes and the status quo',
      'Win/lose patterns expressed only as wins',
      'Win reason claimed as "quality" or "relationships" without a specific mechanism',
      'Premium positioning with "price" as the win reason — a live contradiction',
    ],
  },
  differentiators: {
    strong: [
      '2–4 specific differentiators in language a customer would recognise',
      'Each backed by concrete evidence — specific examples, tenure data, a named mechanism',
      'Each honestly rated for defensibility — including naming "time advantage only" rather than inflating',
      'At least one structurally defensible element if the business claims premium positioning',
      'One named as the clear lead differentiator',
      'Consistency: stated differentiators track the win reasons in Competitors',
    ],
    weak_signals: [
      'Generic claims — "quality", "service", "relationships" — without a specific mechanism',
      'Features described as differentiators — what the product does, not what makes customers choose it',
      'Internal virtues the customer never experiences',
      'Everything rated as structurally defensible — likely inflation',
      'No honest reckoning with what a well-funded competitor entering tomorrow could replicate',
    ],
  },
  go_to_market: {
    strong: [
      'An acquisition blend that reflects the last 10 actual closed deals — not aspiration',
      'A primary motion that has earned its primacy — specific wins it produced',
      "Sales motion named honestly — founder-led is legitimate; claiming a team that doesn't exist is not",
      'Retention that distinguishes deliberate retention from inertia',
      'Honest on what isn\'t yet measured — "unclear" on CAC is better than a made-up number',
    ],
    weak_signals: [
      '"We have a good mix" without naming what the mix actually is',
      '"Word of mouth drives our pipeline" without a mechanism — who refers, why, when',
      '"Content drives our pipeline" without attribution evidence',
      'Acquisition given 10x the attention of retention with no read on what % of revenue is rebuilt each year',
      '"Retention is great" without knowing what specifically makes customers stay',
    ],
  },
  revenue_model: {
    strong: [
      'Revenue blend matching actual revenue from the last 12 months — not aspiration',
      "Primary revenue type named with awareness of what that model's operating rhythm demands",
      'Deal size given as a range, not a point estimate',
      'Payment terms honest about what customers actually pay, not invoice terms',
      "Gross margin given as a range with a clear view on what's included",
    ],
    weak_signals: [
      '"We do a mix" without actual ratios from the last 12 months',
      'Gross margin stated confidently without tracking delivery cost properly',
      'Payment terms stated as invoice terms when customers routinely pay late',
      'Deal size as a single point estimate — always misleading',
      '"Subscription" claimed when the reality is contracted retainers',
    ],
  },
  cost_model: {
    strong: [
      'A breakdown that reflects real monthly spend — not an idealised version',
      'Founder time treated explicitly — either included or named as excluded',
      'Fixed/variable split tracking what would actually flex if revenue dropped',
      'Breakeven as a range or "not sure" honestly',
      'Cost shape coherent with stated market positioning',
    ],
    weak_signals: [
      'Monthly fixed costs stated without founder salary included',
      '"We\'re mostly variable" when most costs are fixed premises, salaries, and tech subscriptions',
      'No honest view of tech subscription spend',
      '"Our costs are stable" meaning they haven\'t been reviewed recently',
      'Best-in-class or Premium positioning with low Marketing/Sales or Technology cost weight',
    ],
  },
  current_challenges: {
    strong: [
      '3–8 entries — fewer means under-surfacing, more means noise',
      'Each expressed specifically — "clients stretching payment terms from 30 to 60+ days" beats "cash flow"',
      "Each with one-sentence concrete evidence of what's actually happening",
      "Each honestly tagged by area and urgency — with urgency that doesn't inflate everything to Critical",
      'Each with a one-sentence impact-if-ignored',
    ],
    weak_signals: [
      'Bare labels without specificity — "Cash flow", "Hiring", "Growth"',
      'Symptoms presented without the underlying cause',
      'Generic challenges applicable to every SME without a specific version',
      'Missing challenges the canvas obviously implies',
      'Urgency inflated to Critical across multiple entries',
    ],
  },
};

// ── Explore editor (AI dialogue) ──────────────────────────────────
// skipEntryChoice = true when the leader has explicitly chosen to
// revise an already-completed section. In that case we don't want to
// show the entry-choice screen — they've already decided to deep-dive
// again. Continue from the saved transcript if there is one, or open
// fresh if not.
function ExploreEditor({ section, sectionData, onSave, onBack, onSwitchToQuick, exploreContext, contextLoading, skipEntryChoice, user }) {
  // Has a prior Explore conversation been captured for this section?
  const priorTranscript = sectionData?._explore_transcript;
  const hasPriorTranscript = !!(priorTranscript && priorTranscript.messages && priorTranscript.messages.length > 0);

  // In-progress transcript from a prior session (browser refresh, crash,
  // extract failure) — auto-saved separately from the committed one.
  const initialInProgress = readInProgress(section.key);
  const hasInProgress = !!(initialInProgress && Array.isArray(initialInProgress.messages) && initialInProgress.messages.length > 0);

  // Are there non-transcript fields already populated (from inference, quick select, or prior explore save)?
  const hasPriorFieldData = sectionData && Object.keys(sectionData).some(k =>
    !k.startsWith('_') && sectionData[k] != null &&
    (Array.isArray(sectionData[k]) ? sectionData[k].length > 0 : String(sectionData[k]).length > 0)
  );

  // Possible starting modes:
  //   'entry_choice'      — prior data; ask the leader how to begin
  //   'value_chip_picker' — Values section, fresh start: pick from chip set first
  //   'chatting'          — conversation in progress
  //   'extracting'        — LLM extracting structured data
  //   'reviewing'         — ProposedReview shown for edit + save
  //   'saved'             — terminal; post-save summary
  const isValuesFresh = section.key === 'values' && !hasPriorTranscript && !hasPriorFieldData;
  // If an in-progress transcript exists, route to entry_choice so the
  // leader can resume it explicitly rather than silently overwriting.
  // EXCEPTION: skipEntryChoice (set when the leader picked "Revise
  // with deep dive" from the Quick Select header on a completed
  // section) bypasses entry_choice and continues from the prior
  // transcript directly.
  const initialPhase = skipEntryChoice
    ? 'chatting'
    : (hasPriorTranscript || hasPriorFieldData || hasInProgress)
      ? 'entry_choice'
      : (isValuesFresh ? 'value_chip_picker' : 'chatting');

  // Selected values from the chip picker (Values section only). Drives the
  // opener and the followup prompt — the LLM is told the leader has
  // already identified these, so it probes each one rather than fishing.
  const [selectedValues, setSelectedValues] = useState(initialInProgress?.selectedValues || []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(initialPhase);
  const [proposed, setProposed] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [extractError, setExtractError] = useState(null); // eslint-disable-line no-unused-vars
  // Two-question queue — when the AI asks Q1+Q2, we hold Q2 client-side
  // and only send to the AI once both are answered.
  // Q1/Q2 card pattern removed — every AI reply now renders as a single
  // chat bubble. The system prompt asks for one question per turn; if
  // the LLM gives two, they appear as paragraphs in the same bubble and
  // the leader can answer both in a single message.
  const [showManualExtract, setShowManualExtract] = useState(false);
  const chatEndRef = useRef(null);
  const questionCount = useRef(0);

  // Journey phase — tracks which stage of the deep dive we're in.
  //   'capture'   → standard interview (existing behaviour)
  //   'assessing' → single assessment turn in progress (no user input)
  //   'advisory'  → adviser conversation on named gaps
  //   'closing'   → moving to extract + save
  // Ref is kept in sync with state for use inside async callbacks where
  // a stale closure on `journeyPhase` would otherwise read the old value.
  const [journeyPhase, setJourneyPhase] = useState('capture'); // eslint-disable-line no-unused-vars
  const journeyPhaseRef = useRef('capture');

  useEffect(() => {
    // Only auto-start the conversation if we didn't land on entry_choice.
    if (initialPhase !== 'chatting') return;
    // Revise-with-deep-dive path on a completed section: load the
    // saved transcript and continue rather than starting fresh.
    if (skipEntryChoice && hasPriorTranscript) {
      setMessages(priorTranscript.messages || []);
      questionCount.current = priorTranscript.questionCount || priorTranscript.messages.filter(m => m.role === 'assistant').length;
      return;
    }
    startConversation({ useExistingData: skipEntryChoice && hasPriorFieldData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-save every change to the in-progress transcript so a crashed
  // extract / refresh / blank screen never loses the leader's input.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    if (phase === 'reviewing' || phase === 'saved') return;
    writeInProgress(section.key, {
      messages,
      questionCount: questionCount.current,
      selectedValues,
      phase,
    });
  }, [messages, phase, selectedValues, section.key]);

  // Resume from a previously interrupted session.
  // If the saved transcript ended in a wrap-up (the LLM had said it had
  // enough and the leader walked away from the review), automatically
  // slip into refinement mode: strip the wrap-up tail, append a fresh
  // "Back to it" prompt, reset the question counter so the hard cap
  // doesn't fire on the very next turn, and ask the LLM to open with
  // a question targeting the last review's biggest gap.
  function handleResumeInProgress() {
    const ip = readInProgress(section.key);
    if (!ip || !Array.isArray(ip.messages)) return;

    const msgs = [...ip.messages];
    const lastIdx = msgs.length - 1;
    const last = lastIdx >= 0 ? msgs[lastIdx] : null;
    // Either wrap-up variant counts as "ended in wrap-up" for the
    // resume-into-refinement flow.
    const endedInWrapup = !!(last && last.role === 'assistant'
      && typeof last.content === 'string'
      && (last.content.startsWith("Great — I've got enough")
          || last.content.startsWith("We've been through a lot here")));
    const lastEval = sectionData?._evaluation || sectionData?.current?._evaluation;
    const hasCritique = !!(lastEval && Array.isArray(lastEval.improvements) && lastEval.improvements.length > 0);

    if (endedInWrapup || hasCritique) {
      // Drop the wrap-up so the conversation doesn't read as finished.
      if (endedInWrapup) msgs.splice(lastIdx, 1);
      const intro = hasCritique
        ? `Back to it — I'll help you tighten the bits the last review flagged. First: ${lastEval.improvements[0]} — what would you add or change here?`
        : "Back to it — what would you like to add or refine? When you're ready for me to update the review, just say so.";
      msgs.push({ role: 'assistant', content: intro });
      setMessages(msgs);
      if (Array.isArray(ip.selectedValues)) setSelectedValues(ip.selectedValues);
      // Reset the question counter so the leader gets a full refinement
      // budget regardless of how many turns the original session burned.
      questionCount.current = 0;
      setIsRefining(true);
      setPhase('chatting');
      return;
    }

    setMessages(ip.messages);
    if (Array.isArray(ip.selectedValues)) setSelectedValues(ip.selectedValues);
    questionCount.current = ip.questionCount || ip.messages.filter(m => m.role === 'assistant').length;
    setPhase('chatting');
  }

  // Entry-choice handlers — the business leader picks how to begin
  function handleContinueFromTranscript() {
    setMessages(priorTranscript.messages || []);
    questionCount.current = priorTranscript.questionCount || priorTranscript.messages.filter(m => m.role === 'assistant').length;
    setPhase('chatting');
  }

  function handleStartFromInferred() {
    setPhase('chatting');
    startConversation({ useExistingData: true });
  }

  function handleStartFresh() {
    // From entry_choice "start fresh": for Values route through the chip
    // picker first so the leader declares which values they observe before
    // the AI starts probing.
    if (section.key === 'values') {
      setPhase('value_chip_picker');
      return;
    }
    setPhase('chatting');
    startConversation({ useExistingData: false });
  }

  // Values chip picker → starts the AI dialogue with the selected list.
  function handleValuesPickerSubmit(values) {
    setSelectedValues(values);
    setPhase('chatting');
    startConversation({ useExistingData: false, preSelectedValues: values });
  }

  async function startConversation({ useExistingData, preSelectedValues }) {
    setLoading(true);
    try {
      const systemPrompt = buildSystemPrompt('open', { useExistingData, preSelectedValues });
      const opener = preSelectedValues && preSelectedValues.length
        ? `The leader has just selected these values to explore: ${preSelectedValues.join(', ')}. Start the conversation by acknowledging the list and probing the FIRST one.`
        : `Start the conversation about ${section.title}.`;
      const res = await axios.post(`${API}/chat`, {
        messages: [{ role: 'user', content: opener }],
        context: systemPrompt
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: `Let's work on your ${section.title}. ${getOpeningQuestion(section)}` }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  // Map the option label stored in vision_stage to a short key used to
  // look up stage_guidance / evaluator criteria. Tolerates the leader
  // typing a custom variant by matching on the leading word.
  function visionStageKey(label) {
    if (typeof label !== 'string' || !label.trim()) return null;
    const s = label.trim().toLowerCase();
    if (s.startsWith('early')) return 'early';
    if (s.startsWith('growth')) return 'growth';
    if (s.startsWith('established') || s.startsWith('mature')) return 'established';
    return null;
  }

  function buildSystemPrompt(phaseArg, opts = {}) {
    const { useExistingData = true, preSelectedValues } = opts;
    // Today's date — injected so the LLM doesn't compute years-to-target
    // off its training cutoff. "Vision for 2030" should resolve against
    // today, not against the LLM's stale internal date.
    const todayIso = new Date().toISOString().slice(0, 10);
    const todayLong = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const todayBlock = `\nToday's date is ${todayLong} (${todayIso}). Use this when reasoning about time horizons, vision target years, or any "how many years from now" arithmetic. Do NOT use your own training cutoff date.\n`;
    const valuesList = Array.isArray(preSelectedValues) && preSelectedValues.length
      ? preSelectedValues
      : (section.key === 'values' ? selectedValues : null);
    const valuesPreSelectedBlock = (section.key === 'values' && valuesList && valuesList.length) ? `

VALUES PRE-SELECTED BY THE LEADER:
The leader has already identified these values to explore: ${valuesList.join(', ')}.

How to handle this list:
- Work through the values IN THE ORDER LISTED. One at a time.
- For each value, ask 1–2 probing questions max. You want to surface:
  (a) how it shows up in actual behaviour or decisions (not aspiration);
  (b) whether there's a non-negotiable line — what would never be crossed;
  (c) how often it genuinely drives decisions (rarely / sometimes / often / always).
  You don't need all three in detail — push back on vague answers but
  move on once the value is grounded in real examples.
- After each value is probed, announce the next: "Good — now let's
  look at {next value}." or similar natural transition.
- Do NOT suggest additional values yourself. If the leader brings up a
  new one mid-conversation, acknowledge and add it to the queue.
- Once the last listed value has been probed sufficiently, ask if there
  are any other values to add. If no, emit READY_TO_SUMMARISE.` : '';

    // ── Refinement mode block ──────────────────────────────────────
    // Set when the leader has returned to chat from the review screen.
    // Tells the LLM to keep probing on whatever the leader wants to
    // refine and NOT to auto-summarise; the leader will explicitly
    // ask to update the review when they're ready.
    // Also surfaces the last evaluation's score + improvements so the
    // LLM can target questions at the specific gaps the leader is
    // trying to close — not just open-ended probing.
    const lastEval = sectionData?._evaluation || sectionData?.current?._evaluation;
    const evalCritiqueBlock = (isRefining && lastEval && Array.isArray(lastEval.improvements) && lastEval.improvements.length > 0) ? `

LATEST REVIEW CRITIQUE — the leader is refining to close these gaps:
Score: ${lastEval.score ?? '?'}/5 — ${lastEval.headline || ''}
Gaps to close:
${lastEval.improvements.map(s => `- ${s}`).join('\n')}

Your job in refinement: ask ONE focused question that targets the most
important gap above. After each answer, pick the next gap. Mention the
gap you're addressing in plain language ("Let's tighten the bit about
X — ..."). Do not list the gaps to the leader as a bulleted menu.` : '';
    const refinementBlock = isRefining ? `

REFINEMENT MODE — read this carefully:
The leader has returned to this conversation from the review screen.
They want to refine, add to, or pressure-test what they've already
captured. Behaviour for this turn and the next several turns:
- Do NOT emit READY_TO_SUMMARISE on this turn or any subsequent turn
  unless the leader EXPLICITLY says they're done, asks you to update
  the review, or says something like "wrap up" / "save it" / "let's
  finalise". A simple new piece of information from them is NOT a
  request to wrap up.
- Continue probing wherever the conversation goes. Apply the same
  ONE QUESTION PER TURN / NEUTRAL TRANSITIONS / OPEN PROBE rules.
- Every 3 or so turns, naturally check in: "Anything else worth
  adding, or shall I update the review now?" — but only when it
  feels natural, not as a stock phrase.
- If they explicitly ask to update the review, THEN emit
  READY_TO_SUMMARISE on its own line (no question mark in the same
  turn).${evalCritiqueBlock}` : '';

    const fieldNames = section.fields.map(f => f.label).join(', '); // eslint-disable-line no-unused-vars
    const fieldDetails = section.fields.map(f => {
      const parts = [`- ${f.label} (key: ${f.key}, type: ${f.type}`];
      if (f.options) parts.push(`, options: ${f.options.join(' | ')}`);
      if (f.multi) parts.push(', multi-select');
      if (f.required) parts.push(', required');
      parts.push(')');
      if (f.placeholder) parts.push(` — ${f.placeholder}`);
      return parts.join('');
    }).join('\n');

    // Existing data — shown to LLM only if business leader chose to build on it.
    // When useExistingData is false, we explicitly tell the LLM to ignore it.
    const existingCore = sectionData
      ? Object.fromEntries(Object.entries(sectionData).filter(([k]) => !k.startsWith('_')))
      : null;
    const hasExisting = existingCore && Object.keys(existingCore).length > 0;
    const existing = !hasExisting
      ? 'No existing data — starting fresh.'
      : useExistingData
        ? `Existing data (business leader asked to build on this): ${JSON.stringify(existingCore)}`
        : `Note: there is prior data for this section but the business leader asked to start fresh. Do not refer to it or assume any of it. If asked, acknowledge that we are setting it aside.`;

    // Pull rich guidance content for this section, if defined.
    // Placed here so the per-section context blocks below can read it.
    const g = SECTION_GUIDANCE?.[section.key] || {};

    // ── Customers: optional proposal_guidance ──────────────────────
    // The customers Explore dialogue may, sparingly, propose a segment
    // the leader hasn't considered. Triggered by the proposal_guidance
    // block in sectionGuidance.customers; injected only for that section.
    const customersProposalBlock = (section.key === 'customers' && g.proposal_guidance)
      ? `\n\nPROPOSAL GUIDANCE:\n${g.proposal_guidance}`
      : '';

    // ── Vision: stage-aware calibration ─────────────────────────────
    const visionStageBlock = (() => {
      if (section.key !== 'vision') return '';
      const stageKey = visionStageKey(sectionData?.vision_stage);
      const stageBlock = stageKey && g.stage_guidance && g.stage_guidance[stageKey];
      if (stageBlock) {
        return `\n\nSTAGE-AWARE CALIBRATION (already captured):
${stageBlock}

The leader has already picked their stage so DO NOT re-ask for it. Apply the calibration above throughout the conversation.`;
      }
      const probeText = g.business_stage_question || "Before we get into the vision itself — where would you say the business is right now? Early days and still finding your footing, actively scaling a model that's working, or more established and stable?";
      return `\n\nSTAGE-AWARE CALIBRATION (not yet captured):
This is the Vision section. Vision needs to do two things at once: paint an aspirational long-term picture AND show a credible near-term path. The weighting between them depends on stage — early-stage emphasises ambition, growth balances both, established prioritises near-term realism.

The leader has NOT yet told us their stage. Your FIRST question in this conversation must be the stage probe — verbatim or close to it:
  "${probeText}"

Wait for their answer. Once they tell you the stage (early / growth / established), confirm and apply the matching calibration:

EARLY: Push hard on the 10–15 year ambition. The aspirational picture is doing the heavy lifting at this stage — a modest vision signals a small TAM. Challenge anything incremental. Don't over-probe near-term milestones — ask only for the first meaningful step.

GROWTH: Balance both equally. Probe the long-term ambition for staying power AND probe the 3–5 year view for specificity, milestones, evidence.

ESTABLISHED: Anchor on the 3–5 year view as the primary output. Push back hard if the vision is vague or sounds like marketing copy. Probe specificity, credibility, alignment with current capabilities. Overly aspirational language reads as unfocused.`;
    })();

    const guidanceBlock = [
      g.objective && `OBJECTIVE FOR THIS SECTION\n${g.objective}`,
      g.why_it_matters && `WHY THIS MATTERS\n${g.why_it_matters}`,
      g.good_answer_looks_like && `WHAT A GOOD ANSWER LOOKS LIKE\n${g.good_answer_looks_like}`,
      g.probe_deeper_on && `SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON\n${g.probe_deeper_on}`,
      g.conversation_archetype && `HOW TO RUN THIS CONVERSATION\n${g.conversation_archetype}`,
      g.future_outlook_instruction && `FUTURE OUTLOOK — TREND + NEW ENTRIES\n${g.future_outlook_instruction}`,
    ].filter(Boolean).join('\n\n');

    // Followup turns exclude conversation_archetype — it describes the overall
    // shape of the conversation (multi-step, ask about X then Y then Z) and
    // causes the model to bundle multiple questions together. The opening turn
    // needs it for framing; followup turns don't.
    const followupGuidanceBlock = [
      g.objective && `SECTION OBJECTIVE\n${g.objective}`,
      g.probe_deeper_on && `THINGS WORTH PROBING DEEPER ON\n${g.probe_deeper_on}`,
      g.future_outlook_instruction && `FUTURE OUTLOOK — TREND + NEW ENTRIES\n${g.future_outlook_instruction}`,
    ].filter(Boolean).join('\n\n');

    if (phaseArg === 'open') {
      const openerGuidance = useExistingData && hasExisting
        ? `The business leader has asked to build on the existing data above. In your opening message: briefly reflect back what the existing data suggests (2–3 sentences max), then ask your first question to deepen it. Be warm, concise, don't over-summarise.`
        : `Ask ONE thoughtful opening question aligned with the conversation approach above. Be warm, direct, treat the business leader as an intelligent adult.`;

      return `You are GuruOne, a strategic advisor helping a business leader articulate their ${section.title}.
${todayBlock}${exploreContext?.businessProfile ? `\nBusiness profile: ${exploreContext.businessProfile}` : ''}
${exploreContext?.sectionContext ? `\nRelevant context for this conversation:\n${exploreContext.sectionContext}` : ''}

${guidanceBlock || `Your goal is to explore: ${section.explorePrompt}.`}
${valuesPreSelectedBlock}${visionStageBlock}${customersProposalBlock}${refinementBlock}

FIELDS TO POPULATE AFTER CONVERSATION
${fieldDetails}

${existing}

CRITICAL RULES — read these before every response:
1. ONE QUESTION PER TURN. Ask exactly one focused question and wait for the answer. Do not chain two questions together with "and" or "—". Do not use **Q1:** / **Q2:** markers — they are no longer rendered specially and will read as noise. If you have two things to explore, pick the one most worth asking first.
2. WAIT FOR THE ANSWER. Do not pre-answer your own question, do not offer multiple framings until asked.
3. PROBE, DON'T ACCEPT. If an answer is vague or generic, ask for the specific version before moving on.
4. NO EXTERNAL TASKS. If external research or data would be useful, do not tell the business leader to go do it. Instead, propose a reasonable placeholder answer (e.g. "Let's assume X for now — we can update this later") and note that a Research feature is coming that can help gather that data. Keep the canvas moving.
5. CONCISE. Your responses should be conversational — 2–5 sentences before the question. Not essays.
6. STAY IN SECTION SCOPE. Do not surface or echo facts from other canvas sections (revenue figures, deal sizes, customer segment counts, cost details, etc.) unless this conversation is specifically about that section. Vision conversations are about ambition and direction, not current commercial numbers.

${openerGuidance}`;
    }
    if (phaseArg === 'followup') {
      const requiredFields = section.fields.filter(f => f.required).map(f => f.key);
      const fieldKeys = section.fields.map(f => f.key);
      return `You are GuruOne, a strategic advisor helping a business leader articulate their ${section.title}.
${todayBlock}${exploreContext?.businessProfile ? `\nBusiness profile: ${exploreContext.businessProfile}` : ''}
${exploreContext?.sectionContext ? `\nRelevant context for this conversation:\n${exploreContext.sectionContext}` : ''}

${followupGuidanceBlock || `Your goal is to explore: ${section.explorePrompt}.`}
${valuesPreSelectedBlock}${visionStageBlock}${customersProposalBlock}${refinementBlock}

FIELDS TO POPULATE
${fieldDetails}

${existing}

STAY IN SCOPE — read this every turn:
The ONLY fields this conversation is for: ${fieldKeys.join(', ')}.
Your questions MUST aim at filling one of those fields. If the leader's
answer references topics that belong to other sections (e.g. business
model validation, pricing experiments, hiring plans, go-to-market
mechanics), acknowledge briefly but DO NOT pursue them — those will be
covered in other conversations. Bring the next question back to one of
the in-scope fields above.

COVERAGE CHECK — do this mentally before every response:
Review the full conversation above. For each field listed, decide whether it has been substantively answered:
${requiredFields.length > 0 ? `Required fields (must all have at least one answer before deep-probing any single field): ${requiredFields.join(', ')}` : ''}
All fields: ${fieldKeys.join(', ')}

TWO-PHASE APPROACH:
Phase 1 — Breadth. If any required field has not yet been asked about at all, your next question must address one of those uncovered fields. Do not go deeper on a field that already has an answer while other required fields are still untouched.
Phase 2 — Depth. Once every required field has at least one answer, probe freely and deeply. Shallow or vague answers deserve follow-up questions. Good probing on a single field may take 2–3 exchanges — that is the right behaviour. Push back on generic answers. Ask for specifics, evidence, consequences.

PROBE STYLE — open, not forced-choice:
When probing for depth, prefer open-ended forms ("what specifically…",
"say more about…", "what made that the case…") over forced-choice
multiple-choice ("was it A, B, C — or something else?"). Forced-choice
flattens the answer. The leader's own words are what populates the
fields. Only use a forced-choice probe when the dimension is genuinely
discrete (e.g. confirming a specific option label).

TRANSITION STYLE — neutral, not evaluative:
When you've gathered enough on one dimension and are moving to the
next, NEVER open with negation/critique framing ("That's solid. But…",
"Good — however…", "Useful — but I'm missing…"). The leader hasn't
fallen short; you're just moving on. Use neutral connectors:
  "Good. Now let me explore X with you."
  "That's enough on that for now. Next…"
  "OK — moving to X."
Only use "but" / "however" when you genuinely need to push back on a
specific answer that wasn't clear, not as a stock transition.

VOCABULARY SCOPE — match the section:
Word choice should match this section's domain. Do not bleed in
vocabulary that belongs to other sections:
- "Sacrifice", "personal risk", "what you're willing to give up" belong
  to founder_purpose ONLY. Never use those framings in mission,
  vision, value_proposition, or any other section.
- "Mission" framing should focus on contribution / beneficiaries /
  audience / what changes for them. Don't drift into personal
  founder-trade-off territory.
- "Vision" framing should focus on the concrete picture at the target
  date (what's true, scale, shape) — not personal motivation.

ONE QUESTION PER TURN — strict:
Ask exactly one focused question and wait for the answer. Do not chain
two questions together with "and" or "—" connectors. Do not use the
**Q1:** / **Q2:** markers — they will read as noise. If you have two
things to explore, pick the one that's most worth asking first.

Only emit READY_TO_SUMMARISE when every required field has a substantive (not just initial) answer AND you have no more questions to ask. If you still have a question — even a small clarifying one — ASK IT instead of summarising. The summarise token must NOT coexist with a question in the same turn.

You have asked ${questionCount.current} question(s) so far.

When you're truly done, respond with ONLY the literal token READY_TO_SUMMARISE on its own line — nothing before, nothing after, no preamble, no sign-off, no "shall I summarise now?". The system intercepts the token and moves to extraction automatically.

If you emit READY_TO_SUMMARISE AND include a question mark in the same turn, the system will treat the question as the real intent and ignore the summarise signal — wasting a turn. To wrap up cleanly, send the token alone.

Otherwise:
- Ask exactly ONE question. Never two, never three.
- Do not use **Q1:** / **Q2:** markers — they will read as raw text.
- If the last answer was vague, probe it — but only if the field hasn't been sufficiently answered.
- Never suggest external research — propose a placeholder and mention Research is coming.
- Keep your response to 2–4 sentences plus your question.`;
    }
    if (phaseArg === 'extract') {
      const repeatableHint = section.repeatable ? `

THIS IS A REPEATABLE SECTION. The leader can have multiple entries
(e.g. multiple values, multiple competitors). Your top-level JSON
MUST be shaped as:
  { "entries": [ { ...fields for entry 1 }, { ...fields for entry 2 } ] }
One object per entry inside the entries array. Do NOT return a bare
array, do NOT return a single object — always wrap in { entries: [...] }.` : '';
      return `You are GuruOne. Based on the conversation, extract structured data for the ${section.title} section.

FIELDS AVAILABLE
${fieldDetails}

${existing}
${repeatableHint}

Respond ONLY with valid JSON matching the field keys: ${section.fields.map(f => f.key).join(', ')}.
For chip fields with multi:false, return a single string that EXACTLY matches one of the listed options.
For chip fields with multi:true, return an array of strings that each match one of the listed options.
For text/textarea, return a string.
For split fields (type: split), return an object shaped as { mode: "percentage" | "absolute", splits: { <key>: <number>, ... }, absoluteValues: { <key>: <number>, ... }, customOpts: [] } — use percentage mode unless amounts were discussed, and the splits should sum to 100.
If a field wasn't covered in the conversation, omit it from the JSON rather than guessing.
No markdown, no explanation, no preamble.`;
    }
    if (phaseArg === 'narrative') {
      const narrativeSpec = g.narrative_output || {};
      const length = narrativeSpec.length_target || '3–5 sentences';
      const purpose = narrativeSpec.purpose || 'a short personal reflection capturing the essence of what was shared';
      const futureOutlookBlock = section.future_trend
        ? `

FUTURE OUTLOOK — required for this section:
The conversation also covered which of today's items will continue, how they will shift, and what new items the business will have by the vision horizon. The reflection MUST include one or two sentences naming:
- Which of today's items hold or shift, and why.
- Any net-new items the leader expects to add by the vision horizon.
Cover only what was actually surfaced. If the conversation didn't reach the future outlook, write only what was captured — do not invent.`
        : '';
      return `You are ghost-writing a short first-person reflection on behalf of the business leader, based on what they told you in the conversation. The output goes verbatim into a "Your X in your own words" block that the leader will read back as their own thinking. ${purpose}.${futureOutlookBlock}

LENGTH: ${length} — be concise. Do not exceed this.

PERSPECTIVE — non-negotiable:
- FIRST PERSON. Use "I", "we", "my business", "our".
- NEVER second person. Do not write "You've got…", "You're aiming…",
  "Your vision…". Those are out — the leader is reading this as
  their own words.
- NEVER third-person commentary. Do not write "The leader…",
  "The business will…" as if narrating about them.

CONTENT — non-negotiable:
- Reflect what the leader actually said. Their words, their nuance,
  their wording where possible.
- NO analysis, advice, judgment, or assessment of their thinking.
  No "that's the right sequencing", "the risk is…", "a tension worth
  naming…", "before we lock this in…". You are not coaching here —
  you are reflecting.
- NO questions of any kind. No "are you imagining…", "is it about…",
  no clarifying probes, no "ready to move forward?". This is closed
  prose — the reflection ends with a full stop, not an open thread.
- NO sign-offs, no meta ("here's a summary…", "this captures…"),
  no headings, no labels, no bullets, no markdown.

GOOD OPENING WORDS: "I", "We", "My", "Our", "This business", "What I…",
"What we…". BAD OPENING WORDS: "You", "Your", "You've", "You're",
"The leader", "The founder", "The vision is".

If you cannot write a clean first-person reflection (e.g. the
conversation was too thin), produce a SHORT honest one — even one
or two sentences — rather than padding with analysis.`;
    }
    if (phaseArg === 'assessment') {
      const rubric = SECTION_RUBRICS[section.key] || {};
      const strongCriteria = (rubric.strong || []).map(s => `- ${s}`).join('\n');
      const weakSignals = (rubric.weak_signals || []).map(s => `- ${s}`).join('\n');
      return `You are GuruOne, a strategic adviser. You have just completed a deep-dive conversation with a business leader about their ${section.title}.

Your task in this single turn is to deliver an honest, specific assessment of their thinking — not to ask a question.

QUALITY STANDARD FOR ${section.title.toUpperCase()}
A strong answer for this section:
${strongCriteria}

WEAK SIGNALS TO LOOK FOR
${weakSignals}

ASSESSMENT STRUCTURE — write in this order:
1. What is strong and why (1–2 sentences, specific — name what they said that makes it strong)
2. What is underdeveloped (1–3 named areas — specific, not vague — use the weak signals above as your guide)
3. Signal of intent: "I'd like to explore [gap X] with you." or similar

TONE AND STYLE
- Direct. Honest. Adviser-to-leader. Not a coach, not a teacher.
- Name things specifically. "Your risk positioning is underdeveloped" is weak. "Your risk tolerance reads as Moderate but you haven't given any specific examples of decisions taken or avoided — that makes it feel like a self-assessment rather than a real anchor" is strong.
- Do not be sycophantic. Do not open with praise.
- Do not ask a question. This is a statement turn.
- End with the branch offer — always: "I've identified [N] area(s) to work through. Do you want to explore them now, or save your thinking as it stands and come back when ready?"

IMPORTANT: This turn does not ask a question. It makes a statement and offers a branch. Nothing more.`;
    }
    if (phaseArg === 'extract_gaps') {
      return `You are GuruOne. Based on the assessment message provided, extract the named underdeveloped areas as structured gap data.

Respond ONLY with valid JSON in this shape:
{
  "gaps": [
    { "description": "one-sentence description of the gap, specific", "type": "underdeveloped" }
  ]
}

Rules:
- Maximum 3 gaps
- Each description should be specific enough to be actionable — not "risk tolerance" but "risk tolerance lacks concrete examples of decisions taken or avoided"
- type is always "underdeveloped" unless the gap is clearly a genuine strategic choice between two options, in which case use "decision"
- No markdown, no explanation, no preamble — only the JSON object`;
    }
    if (phaseArg === 'advisory') {
      const rubric = SECTION_RUBRICS[section.key] || {};
      const weakSignals = (rubric.weak_signals || []).map(s => `- ${s}`).join('\n');
      return `You are GuruOne, a strategic adviser having a substantive conversation about the business leader's ${section.title}.

You have just delivered an assessment identifying specific underdeveloped areas. You are now in advisory mode.

WHAT THIS PHASE IS FOR
This is not more interviewing. This is genuine engagement. You have a view — share it. You see patterns — name them. You know frameworks — use them without naming them. The leader has heard what's weak; now help them think through it.

HOW TO ENGAGE
- Name what you see directly: "Your positioning is trying to cover too much ground — that's a common trap and it makes pricing harder to justify"
- Offer ideas proactively: "Three approaches work well here — let me outline them and you can tell me what fits"
- Challenge constructively: "A sophisticated competitor could claim exactly the same thing — what makes this genuinely hard to copy?"
- Build on responses — develop the thinking, don't just record it
- Use frameworks without naming them: "The question worth testing is: what would a client lose that they couldn't easily replace elsewhere?"

WEAK SIGNALS STILL IN SCOPE
${weakSignals}

DECISION GURU ROUTING
If during this conversation a genuine choice emerges with real trade-offs — two or more meaningful options the leader is genuinely torn between — surface it:
"This is starting to look like a genuine strategic choice between X and Y — that's exactly what Decision Guru is built for. Want me to flag that for a separate session?"

PACING — you decide when we're done
This phase is not turn-capped. Take a view on when the named gaps have been substantively worked through. Only offer the check-in when a NATURAL break point has actually arrived — the thinking on a gap has clearly landed, a real position has formed, the leader has visibly shifted or concluded. That might be after 3 exchanges. It might be after 10. Do not impose a cadence; do not check in just because some turns have passed. If the leader is still mid-thought, keep engaging. When a natural break does arrive, use this framing (or a close paraphrase):
  "Are you happy with where we've got to? I'll write up a new summary to go in the card."

CLOSING
When the leader signals they're happy / ready / done in response to the check-in (or unprompted), deliver a brief summary of what was developed and emit CLOSE_ADVISORY on its own line to signal the session should close and save.

Phrases that should trigger a close: "save it", "wrap up", "done", "that's enough", "let's save this", "happy", "yes write it up".

ONE QUESTION PER TURN — strict. Same as capture mode.
CONCISE — 2–5 sentences before the question or prompt.`;
    }
    return '';
  }

  function getOpeningQuestion(sec) {
    // Vision opens with the stage probe when the stage isn't yet captured.
    if (sec.key === 'vision' && !visionStageKey(sectionData?.vision_stage)) {
      const g = SECTION_GUIDANCE?.vision || {};
      if (g.business_stage_question) return g.business_stage_question;
    }
    const openers = {
      founder_purpose: "What made you start this business — was there a problem you experienced, an opportunity you spotted, or something else that drove you?",
      values: "What do you stand for in this business that you'd never compromise on, even under pressure?",
      mission: "In a sentence, who does this business primarily exist to serve — and what are you trying to do for them?",
      vision: "Where do you want this business to be in 3–5 years? Give me a concrete picture.",
      value_proposition: "What problem does your business solve, and who has that problem most acutely?",
      customers: "Tell me about your best customers — who are they and what makes them valuable to you?",
      competitors: "When a prospect doesn't choose you, who do they go with instead — and why?",
      differentiators: "What would a customer who chose you over the alternatives say was the reason they did?",
      go_to_market: "How do you typically find and win new customers right now?",
      revenue_model: "Walk me through how the business makes money — what do customers pay for and how?",
      cost_model: "What are your biggest costs, and are they mostly fixed or do they flex with revenue?",
      current_challenges: "What's the biggest challenge sitting on your desk right now that you haven't cracked yet?",
    };
    return openers[sec.key] || `Tell me about your ${sec.title.toLowerCase()}.`;
  }

  async function sendMessage(overrideInput) {
    const text = (overrideInput || input).trim();
    if (!text || loading) return;
    if (!overrideInput) setInput('');
    await sendToAI(text);
  }

  // Core send logic — appends user message, calls AI, handles
  // READY_TO_SUMMARISE signal, and checks for Q1/Q2 format in reply.
  async function sendToAI(text) {
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const isAdvisory = journeyPhaseRef.current === 'advisory';
      const systemPrompt = buildSystemPrompt(isAdvisory ? 'advisory' : 'followup');
      const res = await axios.post(`${API}/chat`, {
        messages: newMessages,
        context: systemPrompt
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      // Refinement mode bumps the hard cap so the leader can keep
      // probing for ~10 more turns without the system forcing wrap-up.
      // Advisory mode is uncapped — the LLM self-paces and offers to
      // wrap up on its own; CLOSE_ADVISORY / a user close-phrase is the
      // only path out, never a turn-count force-wrap.
      const hardCap = isAdvisory
        ? Infinity
        : isRefining
          ? (questionCount.current + 10)
          : 10;

      if (isReady || questionCount.current >= hardCap) {
        const cleanReply = reply.replace(/READY_TO_SUMMARISE/g, '').trim();

        // If the model emitted READY_TO_SUMMARISE but ALSO has a pending
        // question, defer the summary and ask the question properly. The
        // model can re-emit READY_TO_SUMMARISE on the next turn once the
        // answer is in. Hard cap still triggers wrap-up regardless —
        // avoids an infinite "one more question" loop.
        const hasPendingQuestion = cleanReply.includes('?');
        if (hasPendingQuestion && questionCount.current < hardCap) {
          setMessages(prev => [...prev, { role: 'assistant', content: cleanReply }]);
        } else {
          // True wrap-up — proceed. Strip any residual question-fragment
          // (the cap-hit path) so a dangling probe doesn't end up in the
          // saved transcript.
          let finalReply = cleanReply;
          const qIdx = finalReply.indexOf('?');
          if (qIdx !== -1) {
            const head = finalReply.slice(0, qIdx);
            const lastSep = Math.max(head.lastIndexOf('.'), head.lastIndexOf('!'), head.lastIndexOf('\n'));
            finalReply = lastSep >= 0 ? finalReply.slice(0, lastSep + 1).trim() : '';
          }
          const convoMessages = finalReply
            ? [...newMessages, { role: 'assistant', content: finalReply }]
            : newMessages;
          // Tag the reason so extractData can pick the right tone for
          // the wrap-up message — celebratory when the LLM signalled
          // a natural finish, reflective when we forced the cap at Q10.
          const wrapReason = isReady ? 'ready' : 'cap';
          // Capture finishing on READY_TO_SUMMARISE routes through the
          // assessment phase first; advisory finishing (or any cap-hit)
          // goes straight to extract.
          if (journeyPhaseRef.current === 'advisory') {
            journeyPhaseRef.current = 'closing';
            setJourneyPhase('closing');
            await extractData(convoMessages, wrapReason);
          } else {
            await runAssessment(convoMessages);
          }
        }
      } else {
        // Strip any **Q1:** / **Q2:** markers the model still emits so the
        // bubble reads as natural prose. The user answers freely in the
        // text input regardless of how many questions are in the reply.
        const cleaned = reply
          .replace(/\*\*Q1:\*\*\s*/g, '')
          .replace(/\*\*Q2:\*\*\s*/g, '');

        // In advisory phase, watch for an explicit close signal — either
        // the model emitting CLOSE_ADVISORY, or the leader typing a known
        // close phrase. Both paths route to extract.
        let routedToClose = false;
        if (isAdvisory) {
          const closeSignal = cleaned.includes('CLOSE_ADVISORY');
          const userWantsClose = /\b(save it|wrap up|done|let's save|save this|that's enough|close this|write it up|i'?m happy|yes,? (write|save))\b/i.test(text);
          if (closeSignal || userWantsClose) {
            routedToClose = true;
            const finalMsg = cleaned.replace(/CLOSE_ADVISORY/g, '').trim();
            const allMessages = finalMsg
              ? [...newMessages, { role: 'assistant', content: finalMsg }]
              : newMessages;
            journeyPhaseRef.current = 'closing';
            setJourneyPhase('closing');
            await extractData(allMessages, 'ready');
          }
        }
        if (!routedToClose) {
          setMessages(prev => [...prev, { role: 'assistant', content: cleaned }]);
        }
      }
    } catch {
      // Tag the error message so the renderer can attach a retry button
      // rather than forcing the leader to type an unprompted message.
      setMessages(prev => [...prev, {
        role: 'assistant',
        error: true,
        retryFor: text,
        content: "Sorry, something went wrong. Click Retry below to try again.",
      }]);
    }
    setLoading(false);
  }

  // Attempt to pull valid JSON out of a raw LLM response string.
  // Tries several strategies in order so minor formatting variations
  // (markdown fences, extra text, whitespace) don't cause failures.
  function tryParseJSON(raw) {
    if (!raw) return null;
    // Strategy 1: direct parse (ideal case — pure JSON response)
    try { return JSON.parse(raw.trim()); } catch {}
    // Strategy 2: strip ```json ... ``` or ``` ... ``` markdown fences
    const fenced = raw.replace(/```(?:json)?\s*/gi, '').replace(/```\s*/g, '').trim();
    try { return JSON.parse(fenced); } catch {}
    // Strategy 3: extract first {...} object (may have preamble/postamble)
    const s = raw.indexOf('{');
    const e = raw.lastIndexOf('}');
    if (s !== -1 && e !== -1 && e > s) {
      try { return JSON.parse(raw.slice(s, e + 1)); } catch {}
    }
    // Strategy 4: extract first [...] array (repeatable sections)
    const sa = raw.indexOf('[');
    const ea = raw.lastIndexOf(']');
    if (sa !== -1 && ea !== -1 && ea > sa) {
      try { return JSON.parse(raw.slice(sa, ea + 1)); } catch {}
    }
    return null;
  }

  // Condense the full conversation into a compact form for the extract call.
  // The extract LLM doesn't need the AI's lengthy guidance-block responses —
  // it only needs to know what the FOUNDER said. Condensing prevents context
  // overload which is the most common cause of malformed extract responses.
  function condenseConvoForExtract(convo) {
    const founderAnswers = convo
      .filter(m => m.role === 'user')
      .map((m, i) => `Business leader answer ${i + 1}: ${m.content}`)
      .join('\n\n');
    const lastAI = [...convo].reverse().find(m => m.role === 'assistant');
    return [
      lastAI && { role: 'assistant', content: `[Conversation completed. Key questions covered: ${section.fields.map(f => f.label).join(', ')}]` },
      { role: 'user', content: `Here are my answers from the conversation:\n\n${founderAnswers}\n\nPlease extract the structured data now.` },
    ].filter(Boolean);
  }

  // Separate condensed form for the narrative call — deliberately does NOT
  // include any "extract" instruction so the LLM doesn't append structured
  // data to the narrative. Just the business leader's answers in plain form.
  function condenseConvoForNarrative(convo) {
    const founderAnswers = convo
      .filter(m => m.role === 'user')
      .map((m, i) => `${i + 1}. ${m.content}`)
      .join('\n\n');
    return [
      { role: 'user', content: `Here is what I shared in a conversation about my ${section.title}:\n\n${founderAnswers}` },
    ];
  }

  // Assessment phase — single AI turn that evaluates the captured thinking
  // against the section quality rubric and names 1–3 underdeveloped areas.
  // Called instead of extractData when READY_TO_SUMMARISE fires in capture
  // mode. After the assessment renders, the editor transitions into
  // 'advisory' so the leader can engage with the gaps; questionCount is
  // reset to give the advisory phase its own budget (mirrors the pattern
  // used by enterRefinementMode).
  async function runAssessment(convo) {
    journeyPhaseRef.current = 'assessing';
    setJourneyPhase('assessing');

    const transitionMsg = "I've got a good picture of your thinking here. Let me reflect on what I'm hearing.";
    setMessages(prev => [...prev, { role: 'assistant', content: transitionMsg }]);
    setLoading(true);

    try {
      const systemPrompt = buildSystemPrompt('assessment');
      const condensed = condenseConvoForExtract(convo);
      const res = await axios.post(`${API}/chat`, {
        messages: condensed,
        context: systemPrompt,
      });
      const assessmentReply = (res.data.reply || '').trim();
      setMessages(prev => [...prev, { role: 'assistant', content: assessmentReply }]);

      // Extract and persist gaps for the gap tracker. Silent on failure —
      // the assessment itself is the primary output; gap structuring is
      // a secondary surface and should never block the journey.
      try {
        const gapSystemPrompt = buildSystemPrompt('extract_gaps');
        const gapRes = await axios.post(`${API}/chat`, {
          messages: [{ role: 'user', content: assessmentReply }],
          context: gapSystemPrompt,
        });
        const rawGaps = gapRes.data.reply || '{}';
        const gapData = JSON.parse(rawGaps.replace(/```json|```/g, '').trim());
        const uid = user?.id || 'default';
        const existing = JSON.parse(localStorage.getItem(`guruone_gaps_${uid}`) || '[]');
        const newGaps = (gapData.gaps || []).map(g => ({
          id: `${section.key}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          section_key: section.key,
          section_title: section.title,
          description: g.description || '',
          type: g.type || 'underdeveloped',
          status: 'open',
          created_at: new Date().toISOString(),
        }));
        // Replace any existing OPEN gaps for this section so re-running
        // assessment doesn't pile up duplicates; resolved ones are kept.
        const withoutThisSection = existing.filter(g => g.section_key !== section.key || g.status === 'resolved');
        localStorage.setItem(`guruone_gaps_${uid}`, JSON.stringify([...withoutThisSection, ...newGaps]));
      } catch (gapErr) {
        console.warn('[GuruOne] Gap extraction failed — continuing without gap data', gapErr);
      }

      // Reset the question counter so advisory has its own budget before
      // the hard cap forces a wrap. Capture's question budget shouldn't
      // bleed into advisory.
      questionCount.current = 0;
      journeyPhaseRef.current = 'advisory';
      setJourneyPhase('advisory');
    } catch (err) {
      console.error('[GuruOne] Assessment failed', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I couldn't generate the assessment. You can keep going or save your thinking as it stands.",
      }]);
      questionCount.current = 0;
      journeyPhaseRef.current = 'advisory';
      setJourneyPhase('advisory');
    }
    setLoading(false);
  }

  async function extractData(convo, reason = 'ready') {
    // Pick the wrap-up tone based on how we got here. A natural
    // READY_TO_SUMMARISE signal means the LLM judged we had enough —
    // celebratory feels right. A cap-hit at Q10 means we've been
    // going a while and forced the wrap; that warrants a more
    // reflective, take-stock tone so it doesn't feel abrupt after
    // the leader has just given us something detailed.
    const wrapMessage = reason === 'cap'
      ? "We've been through a lot here — let me take stock of where we got to and pull it together into a draft for you. Nothing's locked in: you'll be able to review what I've heard, refine it, and come back to deepen any of it later."
      : "Great — I've got enough to work with. Let me put together what I've heard and suggest values for each field. You'll be able to review and edit before saving.";
    setMessages(prev => [...prev, { role: 'assistant', content: wrapMessage }]);
    setPhase('extracting');
    setExtractError(null);
    setIsRefining(false);

    // Outer try/catch — guarantees we never strand the user on an
    // 'extracting' phase with no UI to recover from. Any unexpected
    // error falls back to chatting + a manual-extract retry button.
    try {
      const condensed = condenseConvoForExtract(convo);
      const systemPrompt = buildSystemPrompt('extract');

      // Helper — one extract attempt, returns parsed object or null
      async function attemptExtract(messages, prompt) {
        try {
          const res = await axios.post(`${API}/chat`, { messages, context: prompt });
          const raw = res.data.reply || '';
          console.debug('[GuruOne extract] raw response:', raw.slice(0, 200));
          return tryParseJSON(raw);
        } catch {
          return null;
        }
      }

      // Attempt 1 — condensed conversation with full extract prompt
      let extracted = await attemptExtract(condensed, systemPrompt);

      // Attempt 2 — if first failed, retry with a stripped-down prompt
      // that removes field type details and just lists keys + options.
      if (!extracted) {
        const simplePrompt = `You are GuruOne. Extract data from the business leader's answers below.
Return ONLY a JSON object with these keys: ${section.fields.map(f => f.key).join(', ')}.
${section.fields.filter(f => f.options).map(f => `${f.key}: one of: ${f.options.join(' | ')}`).join('\n')}
${section.repeatable ? '\nThis is a repeatable section — wrap the entries in { "entries": [ {...}, {...} ] }.' : ''}
No markdown, no explanation. JSON only.`;
        extracted = await attemptExtract(condensed, simplePrompt);
      }

      if (!extracted) {
        setExtractError({ reason: 'Both extract attempts failed.' });
        setPhase('chatting');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I wasn't able to pull the data into fields automatically this time. Your conversation is saved — you can hit the button below to try again, or use Quick Select to fill the fields in directly.",
        }]);
        // Re-show the manual extract button as a last resort
        setShowManualExtract(true);
        return;
      }

      setProposed(extracted);

      // Narrative generation — only for sections with narrative_output defined.
      // Uses a clean message set WITHOUT the "extract" instruction so the LLM
      // doesn't append structured data to what should be plain prose.
      const g = SECTION_GUIDANCE?.[section.key] || {};
      if (g.narrative_output) {
        try {
          const narrativeMessages = condenseConvoForNarrative(convo);
          const narrativePrompt = buildSystemPrompt('narrative');
          const narrRes = await axios.post(`${API}/chat`, {
            messages: narrativeMessages,
            context: narrativePrompt,
          });
          let text = (narrRes.data.reply || '').trim();
          // Strip any markdown headings the LLM may add despite instructions
          text = text.replace(/^#+\s+[^\n]+\n?/gm, '').trim();
          // Strip any trailing structured-data bleed (lines with | delimiters)
          text = text.split('\n').filter(line => !line.includes('|')).join('\n').trim();
          // Defensive — the narrative must be closed prose. If the model
          // still smuggled a trailing question past the prompt rules, drop
          // from the last sentence boundary before the first '?' onwards.
          const qIdx = text.indexOf('?');
          if (qIdx !== -1) {
            const head = text.slice(0, qIdx);
            const lastSep = Math.max(head.lastIndexOf('.'), head.lastIndexOf('!'), head.lastIndexOf('\n'));
            text = (lastSep >= 0 ? text.slice(0, lastSep + 1) : '').trim();
          }
          // Defensive — narrative should be first-person ("we / our / I").
          // If the LLM produced coach-voice prose addressing the leader
          // ("You've got…", "Your vision is…"), drop it. Detection is
          // restricted to SENTENCE-STARTING second-person to avoid
          // false positives on legitimate mid-sentence references
          // (e.g. "we serve customers in your sector" — perfectly fine
          // in a competitors / customers / value-proposition narrative).
          // Threshold: 2+ sentence-start hits is coach-voice; 1 is a
          // stylistic slip we tolerate.
          const sentenceStartYou = (text.match(/(?:^|[.!?]\s+)(You|Your)\b/g) || []).length;
          if (sentenceStartYou >= 2) {
            text = '';
          }
          if (text) setNarrative(text);
        } catch {
          // Narrative is optional — don't block on failure
        }
      }

      setShowManualExtract(false);
      setPhase('reviewing');
    } catch (err) {
      console.error('[GuruOne extract] unexpected error', err);
      setExtractError({ reason: 'Unexpected error during extract.' });
      setPhase('chatting');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Something went wrong on my side while writing up the conversation — but your answers are saved. Hit the button below to retry the write-up.",
      }]);
      setShowManualExtract(true);
    }
  }

  // Wrap onSave to attach the Explore transcript and narrative alongside field data.
  // This is what makes the conversation persist across section re-entries,
  // and what makes the narrative survive into the saved state.
  // evalState shape:
  //   null                                — no evaluation in flight
  //   { stage:'evaluating', payload }     — modal showing "Reviewing…"
  //   { stage:'done', payload, evaluation } — modal showing score
  //   { stage:'error', payload }          — eval failed; allow save anyway
  const [evalState, setEvalState] = useState(null);

  async function handleSaveWithTranscript(fieldData) {
    const transcriptPayload = {
      messages,
      questionCount: questionCount.current,
      completedAt: new Date().toISOString(),
    };
    const payload = {
      ...fieldData,
      _explore_transcript: transcriptPayload,
    };
    if (narrative) payload._narrative = narrative;

    // Kick off evaluation. The modal stays visible during the call,
    // updates to show the score when it arrives, and triggers the
    // actual onSave only when the leader dismisses the modal.
    setEvalState({ stage: 'evaluating', payload });
    try {
      const g = SECTION_GUIDANCE?.[section.key] || {};
      const guidanceText = [
        g.objective && `Objective: ${g.objective}`,
        g.good_answer_looks_like && `Good answer looks like: ${g.good_answer_looks_like}`,
        g.probe_deeper_on && `Probe deeper on: ${g.probe_deeper_on}`,
      ].filter(Boolean).join('\n\n');
      // Pass a compact field schema so the evaluator only suggests
      // improvements that fit fields actually on this section — no more
      // "add an assumption field" critiques for fields that don't exist.
      const fieldsSchema = (section.fields || []).map(f => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: !!f.required,
        options: Array.isArray(f.options) ? f.options : undefined,
      }));
      const res = await axios.post(`${API}/evaluate-section`, {
        sectionKey: section.key,
        sectionTitle: section.title,
        sectionData: payload,
        sectionFields: fieldsSchema,
        repeatable: !!section.repeatable,
        transcript: transcriptPayload,
        guidance: guidanceText,
      });
      const evaluation = res.data?.evaluation;
      if (evaluation) {
        setEvalState({ stage: 'done', payload: { ...payload, _evaluation: evaluation }, evaluation });
      } else {
        setEvalState({ stage: 'error', payload });
      }
    } catch (err) {
      console.warn('Evaluate-section failed:', err.message);
      setEvalState({ stage: 'error', payload });
    }
  }

  // Modal "Save & continue" handler — commits the saved payload (with
  // _evaluation when available) and clears the in-progress safety copy.
  function commitSaveFromEvaluation() {
    if (!evalState) return;
    onSave(evalState.payload);
    clearInProgress(section.key);
    setEvalState(null);
  }

  // Modal "Save & open Quick Select" handler — commits like above but
  // switches the section's mode from Explore to Quick Select so the
  // leader can immediately tweak field values directly. Only available
  // when the parent provides onSwitchToQuick.
  function commitAndOpenQuickSelect() {
    if (!evalState) return;
    onSave(evalState.payload);
    clearInProgress(section.key);
    setEvalState(null);
    if (onSwitchToQuick) onSwitchToQuick();
  }

  // Refinement-mode flag — set when the leader returns to chat from a
  // review screen (either via the Refine button on the eval modal or
  // "Back to conversation" on ProposedReview). Drives:
  //   - Hard cap relaxation: refinement adds 10 more turns before the
  //     system force-summarises again.
  //   - Prompt block (REFINEMENT_MODE) telling the LLM NOT to
  //     auto-emit READY_TO_SUMMARISE — the leader is genuinely
  //     refining, not asking to wrap up.
  // Reset on next entry / extract.
  const [isRefining, setIsRefining] = useState(false);

  // Helper — when returning to chat from review, strip the auto-added
  // "Great — I've got enough" wrap-up message so the conversation
  // doesn't read as already complete, and append a transition line
  // that targets the first improvement from the latest review when
  // one exists. Also reset the question counter so the leader gets a
  // fresh refinement budget regardless of how many turns the prior
  // session burned through.
  function enterRefinementMode() {
    const lastEval = sectionData?._evaluation || sectionData?.current?._evaluation;
    const firstGap = (lastEval && Array.isArray(lastEval.improvements) && lastEval.improvements.length > 0)
      ? lastEval.improvements[0] : null;
    setMessages(prev => {
      const next = [...prev];
      const lastIdx = next.length - 1;
      if (lastIdx >= 0 && next[lastIdx].role === 'assistant'
        && typeof next[lastIdx].content === 'string'
        && (next[lastIdx].content.startsWith("Great — I've got enough")
            || next[lastIdx].content.startsWith("We've been through a lot here"))) {
        next.splice(lastIdx, 1);
      }
      next.push({
        role: 'assistant',
        content: firstGap
          ? `Back to it — let's close the gaps the review flagged. First: ${firstGap} — what would you add or change here?`
          : "Back to it — what would you like to add or refine? When you're ready for me to update the review, just say so.",
      });
      return next;
    });
    questionCount.current = 0;
    setIsRefining(true);
    setPhase('chatting');
  }

  // Modal "Refine" handler — drop the eval, return the leader to the
  // chat in refinement mode. The Explore transcript is still
  // auto-persisted in localStorage, so nothing is lost.
  function refineFromEvaluation() {
    setEvalState(null);
    enterRefinementMode();
  }

  // Helper — renders a compact read-only view of existing section data
  // so the business leader can see what's already there before deciding.
  function ExistingDataPreview() {
    if (!sectionData) return null;
    const rows = [];

    const renderValue = (field, val) => {
      if (val == null || val === '') return null;
      if (Array.isArray(val)) {
        if (val.length === 0) return null;
        return val.join(', ');
      }
      if (typeof val === 'object') {
        // Split field — show the active category labels
        const splits = val.splits || {};
        const active = Object.entries(splits)
          .filter(([, v]) => v > 0)
          .sort(([, a], [, b]) => b - a);
        if (active.length === 0) return null;
        const opts = field.widget_config?.options || [];
        return active.map(([k, v]) => {
          const label = opts.find(o => o.key === k)?.label || k;
          return `${label} ${Math.round(v)}%`;
        }).join(' · ');
      }
      return String(val).trim() || null;
    };

    // For repeatable sections — show entry count and first entry's primary field
    if (section.repeatable) {
      const entries = sectionData.entries || [];
      if (entries.length === 0) return null;
      const primaryKey = section.fields[0]?.key;
      return (
        <div style={{ background: '#f0f0f4', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#555' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#1a1a2e' }}>
            {entries.length} {section.title.toLowerCase().replace(/s$/, '')} entry{entries.length !== 1 ? 's' : ''} captured:
          </div>
          {entries.map((e, i) => (
            <div key={i} style={{ marginBottom: 2 }}>
              · {e[primaryKey] || `Entry ${i + 1}`}
            </div>
          ))}
        </div>
      );
    }

    // Non-repeatable — show each populated field
    section.fields.forEach(field => {
      const raw = sectionData[field.key];
      const display = renderValue(field, raw);
      if (display) rows.push({ label: field.label, value: display });
    });

    if (rows.length === 0) return null;

    return (
      <div style={{ background: '#f0f0f4', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.75rem' }}>
        {rows.map(({ label, value }) => (
          <div key={label} style={{ fontSize: '0.8rem', marginBottom: '0.3rem', textAlign: 'left' }}>
            <span style={{ color: '#888' }}>{label}: </span>
            <span style={{ color: '#1a1a2e', fontWeight: 500 }}>{value}</span>
          </div>
        ))}
        {sectionData._narrative && (
          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e0e0e8', fontSize: '0.8rem', color: '#555', fontStyle: 'italic', textAlign: 'left' }}>
            "{sectionData._narrative}"
          </div>
        )}
      </div>
    );
  }

  // ── Entry-choice branch: business leader picks how to begin when prior data/transcript exists ──
  if (phase === 'entry_choice') {
    return (
      <div>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem' }}>
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '1.75rem', color: section.colour }}>{section.icon}</span>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>{section.title}</h2>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Explore — let's set the starting point</p>
          </div>
        </div>
        <div style={{ height: 1, background: '#f0f0f0', margin: '1.25rem 0' }} />

        {hasInProgress && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#7B4A0F', marginBottom: 4 }}>You have a conversation in progress here</div>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem' }}>
              An earlier session left {initialInProgress.messages.filter(m => m.role === 'user').length} of your answers unsaved — last updated{' '}
              {initialInProgress.lastUpdated ? new Date(initialInProgress.lastUpdated).toLocaleString() : 'recently'}. Resume to pick up where you left off, or discard and start over.
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleResumeInProgress}
                style={{ padding: '0.55rem 1rem', borderRadius: 6, background: '#d97706', color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                Resume conversation
              </button>
              <button onClick={() => { clearInProgress(section.key); window.location.reload(); }}
                style={{ padding: '0.55rem 1rem', borderRadius: 6, background: 'white', color: '#666', border: '1px solid #ddd', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                Discard and start over
              </button>
            </div>
          </div>
        )}

        {hasPriorTranscript && (
          <div style={{ background: '#f0faf6', border: '1px solid #b8e4d4', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0F6E56', marginBottom: 4 }}>Pick up where we left off</div>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem' }}>
              You had a conversation about this section before — {priorTranscript.messages.filter(m => m.role === 'user').length} exchange(s). Here's what was captured:
            </div>
            <ExistingDataPreview />
            <button onClick={handleContinueFromTranscript}
              style={{ padding: '0.55rem 1rem', borderRadius: 6, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Continue the conversation
            </button>
          </div>
        )}

        {hasPriorFieldData && !hasPriorTranscript && (
          <div style={{ background: '#f8f5ef', border: '1px solid #e4d4b8', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#9B6B3A', marginBottom: 4 }}>There's already some content here</div>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem' }}>
              This section already has data — likely from a document upload or Quick Select. Here's what's there:
            </div>
            <ExistingDataPreview />
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem' }}>
              Do you want to build on this, or set it aside and start fresh?
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={handleStartFromInferred}
                style={{ padding: '0.55rem 1rem', borderRadius: 6, background: '#9B6B3A', color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                Build on what's there
              </button>
              <button onClick={handleStartFresh}
                style={{ padding: '0.55rem 1rem', borderRadius: 6, background: 'white', color: '#555', border: '1.5px solid #ddd', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                Start fresh
              </button>
            </div>
          </div>
        )}

        {hasPriorTranscript && (
          <div style={{ marginTop: '0.5rem' }}>
            <button onClick={handleStartFresh}
              style={{ padding: '0.5rem 1rem', borderRadius: 6, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Start over from scratch
            </button>
          </div>
        )}
      </div>
    );
  }

  if (phase === 'reviewing' && proposed) {
    return (
      <>
        <ProposedReview section={section} proposed={proposed} sectionData={sectionData}
          narrative={narrative} onSave={handleSaveWithTranscript}
          onBack={onBack}
          onBackToConversation={enterRefinementMode} />
        {evalState && (
          <EvaluationModal
            section={section}
            evalState={evalState}
            onContinue={commitSaveFromEvaluation}
            onRefine={refineFromEvaluation}
            onOpenQuickSelect={onSwitchToQuick ? commitAndOpenQuickSelect : undefined}
            onCancel={() => setEvalState(null)}
          />
        )}
      </>
    );
  }

  if (phase === 'value_chip_picker') {
    return (
      <ValueChipPicker
        section={section}
        onBack={onBack}
        onSubmit={handleValuesPickerSubmit}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
      {/* Fixed header */}
      <div style={{ flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.25rem' }}>
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.75rem', color: section.colour }}>{section.icon}</span>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>{section.title}</h2>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Explore — working through your thinking</p>
          </div>
        </div>
      </div>

      {/* Scrollable chat area — flex: 1 fills all remaining viewport height */}
      <div style={{ flex: 1, background: '#f9f9f9', borderRadius: 12, padding: '1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
        {contextLoading && (
          <p style={{ color: '#888', fontSize: '13px', fontStyle: 'italic', marginBottom: '8px' }}>
            Getting your context ready...
          </p>
        )}
        {messages.map((msg, i) => {
          return (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '85%' }}>
                <div style={{
                  padding: '0.75rem 1rem', borderRadius: 12,
                  background: msg.role === 'user' ? '#185FA5' : msg.error ? '#FEF2F2' : 'white',
                  color: msg.role === 'user' ? 'white' : msg.error ? '#991B1B' : '#1a1a2e',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  border: msg.error ? '1px solid #FECACA' : 'none',
                }}>
                  <ChatMessage content={msg.content} role={msg.role} />
                  {msg.error && msg.retryFor && (
                    <button
                      onClick={() => {
                        // Drop the error message, then re-send the last user input.
                        setMessages(prev => prev.filter(m => m !== msg));
                        sendToAI(msg.retryFor);
                      }}
                      style={{
                        marginTop: '0.55rem', padding: '0.32rem 0.85rem',
                        background: '#991B1B', color: 'white', border: 'none',
                        borderRadius: 6, fontSize: '0.78rem', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                      }}>
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {loading && (
          <div style={{ display: 'flex' }}>
            <div style={{ background: 'white', padding: '0.75rem 1rem', borderRadius: 12, fontSize: '0.82rem', color: '#aaa', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              Thinking…
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div style={{ flexShrink: 0, display: 'flex', gap: '0.75rem' }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
          placeholder="Type your answer… (Enter to send)"
          rows={2}
          style={{ flex: 1, fontSize: '0.88rem', padding: '0.6rem 0.85rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none' }}
        />
        <button onClick={() => sendMessage()} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1.25rem', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontSize: '0.88rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-end' }}>
          Send
        </button>
      </div>

      <div style={{ flexShrink: 0, marginTop: '0.75rem' }}>
        {showManualExtract && (
          <button
            onClick={() => { setShowManualExtract(false); extractData(messages); }}
            style={{ fontSize: '0.78rem', color: '#C0556E', background: 'white', border: '1px solid #C0556E', borderRadius: 20, padding: '0.3rem 0.9rem', cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
            Try extracting again →
          </button>
        )}
      </div>
    </div>
  );
}

// ── Infer editor ──────────────────────────────────────────────────
function InferEditor({ section, sectionData, onSave, onBack }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [proposed, setProposed] = useState(null);

  async function analyse() {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const fieldNames = section.fields.map(f => `${f.key} (${f.label})`).join(', ');
      const systemPrompt = `You are GuruOne. Read the provided text and extract information relevant to the ${section.title} section of a strategy canvas.
Fields to populate: ${fieldNames}.
Respond ONLY with valid JSON using these exact field keys: ${section.fields.map(f => f.key).join(', ')}.
For chip fields with multi:false, return a single string. For multi:true, return an array. For text/textarea, return a string. For rating, return a number 1-5.
If you cannot determine a value from the text, omit that field. No markdown, no explanation, just JSON.`;
      const res = await axios.post(`${API}/chat`, {
        messages: [{ role: 'user', content: `Extract ${section.title} information from this text:\n\n${text}` }],
        context: systemPrompt
      });
      const raw = res.data.reply;
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        setProposed(JSON.parse(raw.slice(s, e + 1)));
      }
    } catch {
      alert('Could not analyse the text. Please try again.');
    }
    setLoading(false);
  }

  if (proposed) {
    return <ProposedReview section={section} proposed={proposed} sectionData={sectionData} onSave={onSave} onBack={onBack} />;
  }

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem' }}>
        ← Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <span style={{ fontSize: '1.75rem', color: '#9B6B3A' }}>↑</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Infer — paste text and AI suggests values</p>
        </div>
      </div>
      <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.8rem', color: '#5D4037' }}>
        Paste relevant text from a business plan, investor deck, website copy, or any document. AI will read it and suggest values for each field — you review before saving.
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Paste your document text here…"
        rows={12}
        style={{ width: '100%', fontSize: '0.85rem', padding: '0.75rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: '1rem' }}
      />
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={analyse} disabled={loading || !text.trim()}
          style={{ flex: 2, padding: '0.75rem', borderRadius: 8, background: '#9B6B3A', color: 'white', border: 'none', fontSize: '0.9rem', fontWeight: 600, cursor: loading || !text.trim() ? 'not-allowed' : 'pointer', opacity: loading || !text.trim() ? 0.6 : 1, fontFamily: 'inherit' }}>
          {loading ? 'Analysing…' : 'Analyse and suggest values →'}
        </button>
        <button onClick={onBack} style={{ flex: 1, padding: '0.75rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Proposed review (shared by Explore + Infer) ───────────────────
function ProposedReview({ section, proposed, sectionData, narrative, onSave, onBack, onBackToConversation }) {
  // If a "back to chat" handler is provided (Explore path), prefer it
  // for the in-review back affordances so the leader can return to the
  // dialogue without losing the conversation. Otherwise fall back to
  // onBack (which exits to the canvas dashboard).
  const handleBackToConversation = onBackToConversation || onBack;
  const [local, setLocal] = useState(() => {
    const base = sectionData || (section.repeatable ? { entries: [{}] } : {});
    // Strip meta keys from base so they don't overwrite the proposed values
    const baseClean = Object.fromEntries(Object.entries(base).filter(([k]) => !k.startsWith('_')));
    if (section.repeatable) {
      // Normalise three possible shapes the extract LLM can produce:
      //   { entries: [{...}, {...}] }  — correct
      //   [{...}, {...}]               — bare array (treat as entries)
      //   { value_theme: '...' }       — single object (treat as one entry)
      if (Array.isArray(proposed)) return { entries: proposed };
      if (proposed && proposed.entries) return proposed;
      return { entries: [{ ...proposed }] };
    }
    return { ...baseClean, ...proposed };
  });
  const update = (key, val) => setLocal(prev => ({ ...prev, [key]: val }));
  const filledCount = section.fields.filter(f => {
    const v = local[f.key];
    return v && (Array.isArray(v) ? v.length > 0 : (typeof v === 'object' ? Object.keys(v.splits || {}).length > 0 : String(v).trim().length > 0));
  }).length;

  return (
    <div>
      <button onClick={handleBackToConversation} style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem' }}>
        ← Back to conversation
      </button>

      {/* Distinct header — clearly a review screen, not Quick Select */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #185FA5 100%)', borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1.25rem', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '1.5rem' }}>{section.icon}</span>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{section.title} — Explore results</div>
            <div style={{ fontSize: '0.78rem', opacity: 0.75 }}>
              {filledCount} of {section.fields.length} fields populated from your conversation
            </div>
          </div>
        </div>
        <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: 4 }}>
          Check the suggested values below. Edit anything that doesn't look right, then save.
        </div>
      </div>

      {narrative && (
        <div style={{ background: '#fafaf7', border: '1px solid #e8e4d4', borderRadius: 8, padding: '1rem 1.2rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', color: '#9B6B3A', textTransform: 'uppercase', marginBottom: 6 }}>Your {section.title} in your own words</div>
          <div style={{ fontSize: '0.88rem', color: '#1a1a2e', lineHeight: 1.65, fontStyle: 'italic', maxHeight: 140, overflowY: 'auto' }}>{narrative}</div>
        </div>
      )}

      <div style={{ height: 1, background: '#f0f0f0', margin: '0 0 1.25rem' }} />
      {section.repeatable ? (
        <RepeatableEditor section={section} data={local} onChange={next => setLocal(next)} />
      ) : (
        section.fields.map(f => <FieldEditor key={f.key} field={f} value={local[f.key]} onChange={v => update(f.key, v)} sectionData={local} />)
      )}
      {section.future_trend && !section.future_trend.per_entry && (
        <FutureTrendBlock config={section.future_trend}
          value={local._future} onChange={v => update('_future', v)}
          colour={section.colour} />
      )}
      {section.future_additions && (
        <FutureAdditionsAppendix config={section.future_additions}
          value={local.future_additions} onChange={v => update('future_additions', v)}
          colour={section.colour} visionHorizonYear={readVisionHorizonYear()} />
      )}
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
        <button onClick={() => onSave(local)} style={{ flex: 2, padding: '0.75rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Save section ✓
        </button>
        <button onClick={handleBackToConversation} style={{ flex: 1, padding: '0.75rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' }}>
          Back to conversation
        </button>
      </div>
    </div>
  );
}

// ── Section card ──────────────────────────────────────────────────
function SectionCard({ section, sectionData, onOpen, id }) {
  const tier = getSectionTier(section, sectionData);
  // Started flag derives from the structural tier now (level >= 1)
  // rather than the (deprecated) mechanical star count, so the border
  // styling still flips correctly when any field is filled.
  const started = (tier?.level || 0) >= 1;
  const qualityScore = getSectionQuality(sectionData);
  const hasEval = !!(sectionData?._evaluation && typeof sectionData._evaluation.score === 'number');
  const [reviewOpen, setReviewOpen] = useState(false);
  return (
    <div id={id} style={{
      background: 'white', borderRadius: 12, padding: '1.1rem 1.25rem',
      border: `1.5px solid ${started ? section.colour + '44' : '#f0f0f0'}`,
      boxShadow: started ? `0 2px 8px ${section.colour}18` : '0 1px 3px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column', gap: '0.5rem',
      height: '100%', boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span style={{ fontSize: '1.2rem', color: section.colour }}>{section.icon}</span>
        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1a2e', flex: 1, lineHeight: 1.2 }}>{section.title}</span>
        <StatusBadge tier={tier} compact />
      </div>
      <p style={{ fontSize: '0.75rem', color: '#888', margin: 0, lineHeight: 1.4, flex: 1 }}>{section.description}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Stars score={qualityScore} title={starsTooltip(sectionData)} />
        {hasEval && (
          <button onClick={() => setReviewOpen(true)}
            style={{
              marginLeft: 'auto',
              background: 'none', border: 'none', color: section.colour,
              fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', padding: '0.15rem 0.35rem', width: 'auto',
              textDecoration: 'underline', textUnderlineOffset: 2,
            }}>
            View review
          </button>
        )}
      </div>
      {reviewOpen && hasEval && (
        <EvaluationModal
          section={section}
          evalState={{ stage: 'done', evaluation: sectionData._evaluation }}
          readOnly
          onCancel={() => setReviewOpen(false)}
          onRefine={() => { setReviewOpen(false); onOpen(); }}
          refineLabel="Open section to refine →"
        />
      )}
      <button onClick={onOpen}
        style={{ width: '100%', padding: '0.4rem', fontSize: '0.75rem', borderRadius: 7, border: `1px solid ${section.colour}`, background: started ? section.colour : 'white', color: started ? 'white' : section.colour, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, marginTop: 'auto' }}>
        {started ? '✎ Edit' : '+ Start'}
      </button>
    </div>
  );
}

// ── Global document upload panel ──────────────────────────────────
// One upload, both canvases. Hits /api/upload/combined which runs the
// Direction and Design extraction prompts in parallel against the same
// extracted document/URL text, then surfaces a single review screen
// covering both canvases. Saves Direction via the onSectionsLoaded
// prop (so the parent's bulkSave/editAfter wiring still works) and
// writes Design straight to localStorage 'guruone_operating_model'
// in the per-section { current: {...} } shape that OperatingModelCanvas
// expects.
function GlobalUploadPanel({ onSectionsLoaded }) {
  const [files, setFiles] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlEditable, setUrlEditable] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [accepted, setAccepted] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  // Scope selector — leader can opt to only pre-fill one canvas. Saves
  // tokens server-side AND avoids accidental drift into a canvas they
  // don't want touched. Default is 'both' for backwards-compat.
  const [scope, setScope] = useState('both');
  const fileInputRef = useRef(null);
  const ACCEPTED = '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp';

  // Seed the URL from the saved user profile — locked by default if
  // present, with a small "Edit" link to unlock for editing.
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('guruone_user'));
      if (user?.websiteUrl) {
        setWebsiteUrl(user.websiteUrl);
        setUrlEditable(false);
      }
    } catch { /* non-fatal */ }
  }, []);

  const handleFiles = (e) => {
    // Accumulate — don't replace — so multiple "Choose files" clicks add to the list
    const incoming = Array.from(e.target.files);
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...incoming.filter(f => !existingNames.has(f.name))];
    });
    setResult(null);
    // Reset input so the same file can be re-added after removal
    e.target.value = '';
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const incoming = Array.from(e.dataTransfer.files);
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      return [...prev, ...incoming.filter(f => !existingNames.has(f.name))];
    });
    setResult(null);
  };
  const removeFile = (name) => setFiles(prev => prev.filter(f => f.name !== name));

  const hasInput = files.length > 0 || websiteUrl.trim().length > 0;

  const handleUpload = async () => {
    if (!hasInput) return;
    setUploading(true); setResult(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (websiteUrl.trim()) formData.append('url', websiteUrl.trim());
      formData.append('scope', scope);
      const res = await axios.post(UPLOAD_API + '/combined', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      const stratSecs = res.data.strategy?.sections || {};
      const omSecs = res.data.operating_model?.sections || {};
      if (Object.keys(stratSecs).length === 0 && Object.keys(omSecs).length === 0) {
        // Honest feedback instead of dropping the leader into an empty
        // review screen with nothing to accept.
        alert("We couldn't pull anything usable from what you shared. This can happen with a scanned/image-only PDF we couldn't read, a password-protected file, or a file with very little text. Try a clearer or text-based file, paste your website URL, or email Glenn at glenn@guruone.ai and he'll take a look.");
        setUploading(false);
        return;
      }
      setResult(res.data);
      // Pre-accept every section returned from either canvas. Prefix
      // keys so Direction and Design never collide in the same map.
      const init = {};
      Object.keys(stratSecs).forEach(k => { init[`strat_${k}`] = true; });
      Object.keys(omSecs).forEach(k => { init[`om_${k}`] = true; });
      setAccepted(init);
      setReviewing(true);
    } catch (e) { alert(e.response?.data?.error || 'Upload failed. Please try again.'); }
    setUploading(false);
  };

  const handleSave = () => {
    const stratToSave = {};
    const editAfter = [];
    const omToSave = {};
    Object.keys(accepted).forEach(k => {
      if (k.startsWith('_edit_')) { if (accepted[k]) editAfter.push(k.replace('_edit_', '')); return; }
      if (!accepted[k]) return;
      if (k.startsWith('strat_')) {
        const key = k.slice(6);
        if (result?.strategy?.sections?.[key]) stratToSave[key] = result.strategy.sections[key];
      } else if (k.startsWith('om_')) {
        const key = k.slice(3);
        if (result?.operating_model?.sections?.[key]) omToSave[key] = result.operating_model.sections[key];
      }
    });

    // Read existing canvas data so we can merge the upload INTO it
    // instead of replacing it. mergeSectionPreservingMeta keeps any
    // _narrative / _explore_transcript / _evaluation already on the
    // section, plus any non-empty entries arrays. Without this guard
    // a leader who has done a day of deep-dive on a section loses
    // it the moment they upload — exactly what bit Glenn.
    let existingStrategy = {};
    try {
      const sUser = JSON.parse(localStorage.getItem('guruone_user') || '{}');
      const userKey = sUser?.id ? `user_${sUser.id}` : 'default';
      const raw = localStorage.getItem('guruone_strategy');
      const all = raw ? JSON.parse(raw) : {};
      existingStrategy = (all[userKey] && typeof all[userKey] === 'object') ? all[userKey] : {};
    } catch { /* non-fatal */ }
    const mergedStrat = {};
    Object.entries(stratToSave).forEach(([k, incoming]) => {
      mergedStrat[k] = mergeSectionPreservingMeta(existingStrategy[k], incoming);
    });

    // Direction — let the parent merge so editAfter routing still works.
    if (Object.keys(mergedStrat).length > 0) {
      onSectionsLoaded(mergedStrat, editAfter);
    }

    // Design — write straight to the OM localStorage in the per-section
    // { current: {...} } shape that OperatingModelCanvas reads on mount.
    if (Object.keys(omToSave).length > 0) {
      try {
        const raw = localStorage.getItem('guruone_operating_model');
        const existing = raw ? JSON.parse(raw) : {};
        const next = { ...existing };
        Object.entries(omToSave).forEach(([key, fields]) => {
          const incomingPayload = { current: fields };
          next[key] = mergeSectionPreservingMeta(existing[key], incomingPayload);
        });
        localStorage.setItem('guruone_operating_model', JSON.stringify(next));
        track('upload_combined_save', { strategy: Object.keys(mergedStrat).length, om: Object.keys(omToSave).length, scope });
      } catch { /* localStorage write — non-fatal */ }
    }

    setReviewing(false); setResult(null); setFiles([]); setWebsiteUrl('');
  };

  const LABELS = { founder_purpose:'Founder Purpose',values:'Values',mission:'Mission',vision:'Vision',value_proposition:'Value Proposition',customers:'Customers',competitors:'Competitors',differentiators:'Differentiators',go_to_market:'Go to Market',revenue_model:'Revenue Model',cost_model:'Cost Model',current_challenges:'Current Challenges' };
  const OM_LABELS = {
    people_roles: 'People, Roles & Structure',
    culture_leadership: 'Culture & Leadership',
    location_infrastructure: 'Location & Infrastructure',
    suppliers_partners: 'Suppliers & Partners',
    customer_interactions: 'Customer Interactions',
    process_ways_of_work: 'Process & Ways of Work',
    governance_controls: 'Governance & Review Rhythm',
    finance_commercial: 'Finance & Commercial',
    it_data_security: 'IT & Data Security',
    portfolio: 'Product & Service Portfolio',
    business_development: 'Business Development',
    marketing_brand: 'Marketing & Brand',
  };
  const OM_ICONS = {
    people_roles: '👥', culture_leadership: '🌱', location_infrastructure: '🏢',
    suppliers_partners: '🤝', customer_interactions: '💬', process_ways_of_work: '⚙️',
    governance_controls: '🔄', finance_commercial: '💰', it_data_security: '🔒',
    portfolio: '🗂️', business_development: '🤝', marketing_brand: '📣',
  };

  // "What helps and why" — pairs of (doc category, what it unlocks). Shown
  // in an expander on the upload screen so leaders know what to include.
  const UPLOAD_HINTS = [
    { doc: 'Business plan or vision deck', unlocks: 'Founder purpose, values, mission, vision, value proposition' },
    { doc: 'Pitch deck or sales collateral', unlocks: 'Value proposition, competitors, differentiators, go-to-market' },
    { doc: 'P&L / management accounts / cost breakdown', unlocks: 'Revenue model, cost model, finance & commercial' },
    { doc: 'Customer list, case studies or testimonials', unlocks: 'Customers, customer interactions, differentiators' },
    { doc: 'Org chart or role descriptions', unlocks: 'People & roles, culture, governance' },
    { doc: 'Process docs, SOPs or playbooks', unlocks: 'Process & ways of work, suppliers, quality' },
    { doc: 'Marketing collateral or website', unlocks: 'Marketing & brand, GTM, value proposition' },
    { doc: 'IT estate diagram or systems list', unlocks: 'IT & data security, portfolio' },
  ];

  if (reviewing && result) {
    const stratSections = result.strategy?.sections || {};
    const stratConfidence = result.strategy?.confidence || {};
    const omSections = result.operating_model?.sections || {};
    const omConfidence = result.operating_model?.confidence || {};

    const CONF_STYLE = {
      high:    { label: 'High confidence',   bg: '#f0fdf8', border: '#a7f3d0', color: '#065f46' },
      medium:  { label: 'Medium confidence', bg: '#fffbeb', border: '#fde68a', color: '#78350f' },
      low:     { label: 'Low confidence',    bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
    };

    // Derive per-section confidence — accept either a direct hit or any
    // sub-key prefixed with the section key (e.g. value_proposition_*).
    const ORDER = ['high','medium','low'];
    const getConf = (key, conf) => {
      if (conf[key]) return conf[key];
      const hits = Object.entries(conf).filter(([k]) => k.startsWith(key)).map(([,v]) => v);
      if (!hits.length) return 'medium';
      return hits.sort((a,b) => ORDER.indexOf(b) - ORDER.indexOf(a))[0];
    };

    // Compact preview of what the model pulled for a section.
    const buildPreview = (data, sectionDef) => {
      if (!data || typeof data !== 'object') return String(data).slice(0, 80);
      if (data.entries) return `${data.entries.length} entr${data.entries.length === 1 ? 'y' : 'ies'} found`;
      const vals = Object.entries(data)
        .filter(([,v]) => v && !String(v).includes('unclear'))
        .slice(0, 3)
        .map(([k, v]) => {
          const label = sectionDef?.fields?.find(f => f.key === k)?.label || k.replace(/_/g,' ');
          const display = Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v).slice(0,40) : String(v).slice(0,60);
          return `${label}: ${display}`;
        });
      return vals.join(' · ') || 'Values extracted';
    };

    // Render a single row — used for both Direction and Design entries.
    // canvas = 'strat' | 'om' drives the accepted-key prefix and the
    // "Edit after save" affordance (Direction only — OM doesn't deep-link).
    const renderRow = (key, data, conf, sectionDef, icon, label, canvas, isLast) => {
      const cs = CONF_STYLE[conf] || CONF_STYLE.medium;
      const acceptKey = `${canvas}_${key}`;
      const preview = buildPreview(data, sectionDef);
      return (
        <div key={acceptKey} style={{
          padding: '0.9rem 1.25rem',
          borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
          background: accepted[acceptKey] ? 'white' : '#fafafa',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e', flex: 1, minWidth: 120 }}>
              {icon && <span style={{ marginRight: '0.35rem' }}>{icon}</span>}
              {label}
            </span>
            <span style={{
              fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', padding: '2px 8px', borderRadius: 10,
              background: cs.bg, border: `1px solid ${cs.border}`, color: cs.color,
              whiteSpace: 'nowrap',
            }}>
              {cs.label}
            </span>
            {canvas === 'strat' && (
              <button
                onClick={() => setAccepted(prev => ({ ...prev, [acceptKey]: true, [`_edit_${key}`]: true }))}
                style={{
                  padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600,
                  border: '1px solid #d1d5db', background: 'white', color: '#555',
                  cursor: 'pointer', fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap',
                }}
                title="Accept this section and open it for editing after saving"
              >
                ✎ Edit after save
              </button>
            )}
            <button
              onClick={() => setAccepted(prev => ({ ...prev, [acceptKey]: !prev[acceptKey] }))}
              style={{
                padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600,
                border: accepted[acceptKey] ? '1.5px solid #1D9E75' : '1.5px solid #ddd',
                background: accepted[acceptKey] ? '#E1F5EE' : 'white',
                color: accepted[acceptKey] ? '#0F6E56' : '#888',
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap',
              }}
            >
              {accepted[acceptKey] ? '✓ Accepted' : 'Accept'}
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.35rem', lineHeight: 1.4 }}>
            {preview}
          </div>
        </div>
      );
    };

    const stratKeys = Object.keys(stratSections);
    const omKeys = Object.keys(omSections);
    const acceptedCount = Object.entries(accepted)
      .filter(([k, v]) => v && !k.startsWith('_edit_'))
      .filter(([k]) => {
        if (k.startsWith('strat_')) return !!stratSections[k.slice(6)];
        if (k.startsWith('om_')) return !!omSections[k.slice(3)];
        return false;
      }).length;

    // Count how many sections on the existing canvases will have
    // deep-dive content protected. Used to reassure the leader that
    // narratives won't be overwritten by accepting upload suggestions.
    let existingStrategySnapshot = {};
    let existingOMSnapshot = {};
    try {
      const sUser = JSON.parse(localStorage.getItem('guruone_user') || '{}');
      const userKey = sUser?.id ? `user_${sUser.id}` : 'default';
      const sRaw = localStorage.getItem('guruone_strategy');
      const sAll = sRaw ? JSON.parse(sRaw) : {};
      existingStrategySnapshot = (sAll[userKey] && typeof sAll[userKey] === 'object') ? sAll[userKey] : {};
      const oRaw = localStorage.getItem('guruone_operating_model');
      existingOMSnapshot = oRaw ? JSON.parse(oRaw) : {};
    } catch { /* non-fatal */ }
    const protectedDeepDives = countDeepDiveSections(existingStrategySnapshot) + countDeepDiveSections(existingOMSnapshot);

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ background: '#f0faf6', border: '1.5px solid #1D9E75', borderRadius: '12px 12px 0 0', padding: '1rem 1.25rem', borderBottom: 'none' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F6E56' }}>
            Found information across {stratKeys.length + omKeys.length} section{(stratKeys.length + omKeys.length) !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#1D9E75', marginTop: '0.2rem' }}>
            Review each section — accept or reject, and edit low-confidence areas before saving. Direction sections land on your Direction canvas; Design sections land on your Design canvas.
          </div>
          {protectedDeepDives > 0 && (
            <div style={{ marginTop: '0.6rem', padding: '0.55rem 0.8rem', background: 'white', border: '1px solid #A6DCC8', borderRadius: 6, fontSize: '0.72rem', color: '#0F6E56', lineHeight: 1.5 }}>
              <strong>Your deep-dive work is safe.</strong> {protectedDeepDives} section{protectedDeepDives !== 1 ? 's have' : ' has'} a narrative or transcript captured — those will not be overwritten. Accepted suggestions will only update chip selections and short text fields.
            </div>
          )}
        </div>

        {stratKeys.length > 0 && (
          <>
            <div style={{ background: '#1a1a2e', color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              Direction canvas — {stratKeys.length} section{stratKeys.length !== 1 ? 's' : ''}
            </div>
            <div style={{ borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              {stratKeys.map((key, i) => {
                const sectionDef = SECTIONS.find(s => s.key === key);
                return renderRow(
                  key,
                  stratSections[key],
                  getConf(key, stratConfidence),
                  sectionDef,
                  sectionDef?.icon,
                  LABELS[key] || key,
                  'strat',
                  i === stratKeys.length - 1 && omKeys.length === 0,
                );
              })}
            </div>
          </>
        )}

        {omKeys.length > 0 && (
          <>
            <div style={{ background: '#185FA5', color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              Design canvas — {omKeys.length} section{omKeys.length !== 1 ? 's' : ''}
            </div>
            <div style={{ borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              {omKeys.map((key, i) => renderRow(
                key,
                omSections[key],
                getConf(key, omConfidence),
                null,
                OM_ICONS[key],
                OM_LABELS[key] || key,
                'om',
                i === omKeys.length - 1,
              ))}
            </div>
          </>
        )}

        <div style={{ border: '1.5px solid #1D9E75', borderTop: 'none', borderRadius: '0 0 12px 12px', height: 1 }} />

        {result.errors?.length > 0 && (
          <div style={{ fontSize: '0.72rem', color: '#BA7517', margin: '0.75rem 0' }}>
            ⚠ {result.errors.join(' · ')}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.55rem 1.25rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save {acceptedCount} section{acceptedCount !== 1 ? 's' : ''} to canvases →
          </button>
          <button onClick={() => { setReviewing(false); setResult(null); setFiles([]); setWebsiteUrl(''); }}
            style={{ padding: '0.55rem 1rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Discard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div onDragOver={e => e.preventDefault()} onDrop={handleDrop}
        style={{ border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '0.75rem 1rem', background: '#f9fffe' }}>

        {/* Label row */}
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.25rem' }}>
          Pre-fill both canvases from existing materials
        </div>
        <div style={{ fontSize: '0.72rem', color: '#666', lineHeight: 1.5, marginBottom: '0.4rem' }}>
          Upload once — we&apos;ll suggest values across <strong>both your Direction canvas and your Design canvas</strong> for you to review before saving. The more relevant material you include, the more we can populate.
        </div>

        {/* What helps and why — expander */}
        <button
          type="button"
          onClick={() => setHelpOpen(o => !o)}
          aria-expanded={helpOpen}
          style={{
            background: 'none', border: 'none', color: '#1D9E75', cursor: 'pointer',
            padding: 0, fontSize: '0.72rem', fontWeight: 600, fontFamily: 'inherit',
            textDecoration: 'underline', textUnderlineOffset: 2, marginBottom: '0.55rem', width: 'auto',
          }}>
          {helpOpen ? 'Hide what helps and why' : 'What helps and why →'}
        </button>
        {helpOpen && (
          <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#666', lineHeight: 1.5, marginBottom: '0.45rem' }}>
              Different documents unlock different parts of the canvas. Include anything you have for the areas that matter most.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.3rem' }}>
              {UPLOAD_HINTS.map(({ doc, unlocks }) => (
                <div key={doc} style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', fontSize: '0.72rem', lineHeight: 1.4 }}>
                  <span style={{ color: '#1D9E75', flexShrink: 0 }}>·</span>
                  <span style={{ fontWeight: 600, color: '#1a1a2e', flexShrink: 0 }}>{doc}</span>
                  <span style={{ color: '#888' }}>→ {unlocks}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scope selector — leader can opt to only pre-fill one
            canvas. Default 'both' keeps existing behaviour; narrowing
            saves tokens and avoids touching a canvas they don't want
            drifted into. Whatever scope is chosen, accepted sections
            never overwrite narratives / transcripts / evaluations —
            only chip selections and short text fields are updated. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#666', fontWeight: 600 }}>Populate:</span>
          {[
            { key: 'both', label: 'Both canvases' },
            { key: 'strategy', label: 'Direction only' },
            { key: 'operating_model', label: 'Design only' },
          ].map(opt => (
            <button key={opt.key} type="button" onClick={() => setScope(opt.key)}
              style={{
                padding: '0.25rem 0.7rem', borderRadius: 14, fontSize: '0.7rem', fontWeight: 600,
                border: scope === opt.key ? '1.5px solid #1D9E75' : '1px solid #d1d5db',
                background: scope === opt.key ? '#E1F5EE' : 'white',
                color: scope === opt.key ? '#0F6E56' : '#666',
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              {opt.label}
            </button>
          ))}
          <span style={{ fontSize: '0.66rem', color: '#888', fontStyle: 'italic' }}>
            Narratives + deep-dive content on existing sections are always preserved.
          </span>
        </div>

        {/* Single input row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>🌐 Website</span>
          {urlEditable ? (
            <input
              type="url"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              style={{ width: 220, height: 32, fontSize: '0.78rem', padding: '0 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
            />
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              height: 32, padding: '0 0.7rem', borderRadius: 6,
              background: '#E1F5EE', border: '1px solid #A5D6C3',
              fontSize: '0.75rem', color: '#0F6E56', fontWeight: 600,
              boxSizing: 'border-box',
            }}>
              <span style={{ color: '#1D9E75', fontWeight: 800 }}>✓</span>
              {websiteUrl}
              <button onClick={() => setUrlEditable(true)}
                style={{ background: 'none', border: 'none', color: '#0F6E56', fontSize: '0.7rem', cursor: 'pointer', padding: 0, marginLeft: '0.3rem', textDecoration: 'underline', fontFamily: 'inherit' }}>
                Edit
              </button>
            </span>
          )}

          <span style={{ color: '#ddd' }}>|</span>

          <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>📄 Documents</span>
          <input ref={fileInputRef} type="file" accept={ACCEPTED} multiple onChange={handleFiles} style={{ display: 'none' }} />
          <button onClick={() => fileInputRef.current?.click()}
            style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: '1px solid #1D9E75', background: 'white', color: '#1D9E75', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', width: 'auto', fontFamily: 'inherit', flexShrink: 0 }}>
            + Add files
          </button>
          <span style={{ fontSize: '0.68rem', color: '#ccc', flexShrink: 0 }}>PDF, Word, images, text</span>

          {hasInput && (
            <button onClick={handleUpload} disabled={uploading}
              style={{ marginLeft: 'auto', padding: '0.35rem 0.9rem', borderRadius: 6, background: uploading ? '#ccc' : '#1D9E75', border: 'none', color: 'white', fontSize: '0.75rem', fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', width: 'auto', fontFamily: 'inherit', flexShrink: 0 }}>
              {uploading ? 'Analysing…' : 'Analyse & pre-fill →'}
            </button>
          )}
        </div>

        {/* File chips — second row if files added */}
        {files.length > 0 && (
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {files.map((f, i) => (
              <span key={i} style={{ fontSize: '0.68rem', background: '#E1F5EE', color: '#0F6E56', padding: '2px 6px 2px 8px', borderRadius: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                {f.name}
                <button onClick={() => removeFile(f.name)}
                  style={{ background: 'none', border: 'none', color: '#0F6E56', cursor: 'pointer', padding: 0, fontSize: '0.75rem', lineHeight: 1, width: 'auto', opacity: 0.7 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Direction journey track (4-stage indicator) ──────────────────
const DIRECTION_STAGES = [
  { title: 'Foundation',     subtitle: 'Who you are and what you stand for',     keys: ['founder_purpose', 'values', 'mission', 'vision'] },
  { title: 'Direction',      subtitle: 'Where you are going and how you will win', keys: ['value_proposition', 'differentiators'] },
  { title: 'Market & Model', subtitle: 'How you compete and win customers',      keys: ['customers', 'competitors', 'go_to_market', 'revenue_model', 'cost_model'] },
  { title: 'Focus',          subtitle: 'Current challenges to work through',     keys: ['current_challenges'] },
];

function stageColour(filled, total) {
  if (filled === 0) return '#9CA3AF';
  const pct = filled / total;
  if (pct >= 1) return '#1D9E75';
  if (pct >= 0.5) return '#D97706';
  return '#DC2626';
}

function DirectionJourneyTrack({ canvasData }) {
  const computeFilled = (stage) => stage.keys.filter(k => {
    const data = canvasData[k];
    if (!data) return false;
    const section = SECTIONS.find(s => s.key === k);
    return section ? getSectionCompleteness(section, data) > 0 : false;
  }).length;

  const handleStageClick = (stage) => {
    const firstKey = stage.keys[0];
    if (!firstKey) return;
    const el = document.getElementById(`direction-section-${firstKey}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', margin: '0 0 1.75rem', padding: '0.75rem 0.25rem 0' }}>
      {DIRECTION_STAGES.map((stage, i) => {
        const filled = computeFilled(stage);
        const total = stage.keys.length;
        const colour = stageColour(filled, total);
        const fullyFilled = filled === total;
        return (
          <div key={stage.title} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', minWidth: 0 }}>
            {i < DIRECTION_STAGES.length - 1 && (
              <div style={{
                position: 'absolute', top: 9,
                left: 'calc(50% + 12px)', right: 'calc(-50% + 12px)',
                height: '1.5px',
                background: fullyFilled ? colour : '#e0e0e0',
                zIndex: 0,
              }} />
            )}
            <button
              onClick={() => handleStageClick(stage)}
              aria-label={`${stage.title}: ${filled} of ${total} sections filled`}
              title={`${stage.title} — ${filled} of ${total} filled`}
              style={{
                background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', fontFamily: 'inherit',
                width: 'auto',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                position: 'relative', zIndex: 1,
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: filled > 0 ? colour : 'white',
                border: `2px solid ${colour}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 6, flexShrink: 0,
              }}>
                {fullyFilled ? (
                  <svg viewBox="0 0 8 8" width="9" height="9">
                    <polyline points="1,4 3,6.5 7,1.5" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : filled > 0 ? (
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'white' }} />
                ) : null}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: filled > 0 ? '#1a1a2e' : '#666', textAlign: 'center', lineHeight: 1.2 }}>
                {stage.title}
              </div>
              <div style={{ fontSize: 10.5, color: '#999', textAlign: 'center', marginTop: 3, lineHeight: 1.35, padding: '0 0.4rem' }}>
                {stage.subtitle}
              </div>
              <div style={{ fontSize: 10, color: colour, fontWeight: 700, marginTop: 4, letterSpacing: '0.04em' }}>
                {filled}/{total}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── How to use Direction Guru — slide-in panel ───────────────────
// Shared "How to use" section block — icon + bigger title + body
function HowToSection({ Icon, colour, title, children, isLast }) {
  return (
    <div style={{ paddingBottom: isLast ? 0 : '1rem', marginBottom: isLast ? 0 : '1rem', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.45rem' }}>
        <Icon colour={colour} />
        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
      </div>
      <div style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function HowToUseDirectionPanel({ onClose }) {
  const accent = '#7C3AED';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 150, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 380, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Help</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>How to use Direction Guru</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
          <HowToSection Icon={PurposeIcon} colour={accent} title="What Direction Guru is for">
            <p style={{ margin: 0 }}>
              Direction Guru is where you clarify why your business exists, where it is going, and how it will win. It is the foundation everything else in GuruOne builds on — Design Guru shapes the operating model that delivers it, Delivery Guru turns it into action, and Decision Guru challenges every choice against it. Spend time here when you want to think clearly, not just execute faster.
            </p>
          </HowToSection>

          <HowToSection Icon={FlowIcon} colour={accent} title="How to work through it">
            <p style={{ margin: 0 }}>
              The canvas is split into four stages. <strong>Foundation</strong> is who you are and what you stand for — your purpose, values, mission and vision. <strong>Direction</strong> is where you are heading and how you intend to win — value proposition and differentiators. <strong>Market &amp; Model</strong> is how you compete and make money — customers, competitors, go-to-market, revenue and cost models. <strong>Focus</strong> is the live challenges you need to address. Each section has a Capture mode for quick chips and dropdowns, and an Explore mode for an AI-guided conversation when you want to think it through more deeply.
            </p>
          </HowToSection>

          <HowToSection Icon={OutcomeIcon} colour={accent} title="What you will get out of it">
            <p style={{ margin: 0 }}>
              A completed Direction Canvas — a structured articulation of your strategy you can share, edit and revisit. A one-page direction statement that reads cleanly for advisors, investors and your team. And a foundation that grounds every Decision Guru conversation, every Design Guru choice and every Delivery Guru initiative in your specific context rather than a generic answer.
            </p>
          </HowToSection>

          <HowToSection Icon={TipsIcon} colour={accent} title="Tips for getting the most from it" isLast>
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              <li style={{ marginBottom: '0.4rem' }}>Do not try to complete it in one sitting — the canvas rewards reflection.</li>
              <li style={{ marginBottom: '0.4rem' }}>Use Explore mode when you are uncertain — it walks you through the questions with you.</li>
              <li style={{ marginBottom: '0.4rem' }}>Your answers will evolve as the business does — treat this as a living document, not a one-time exercise.</li>
              <li style={{ marginBottom: '0.4rem' }}>The canvas grounds every Decision Guru conversation — keep coming back to it.</li>
            </ul>
          </HowToSection>
        </div>

        <div style={{ padding: '0.9rem 1.4rem', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ width: '100%', padding: '0.65rem', borderRadius: 8, background: 'white', border: '1.5px solid #e0e0e0', color: '#555', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Canvas overview ───────────────────────────────────────────────
function CanvasOverview({ canvasData, onOpen, navigate, onBulkSave, onOpenGapTracker }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [prefillOpen, setPrefillOpen] = useState(false);
  const [onePagerLoading, setOnePagerLoading] = useState(false);

  const handleOpenOnePager = async () => {
    track('direction_canvas_downloaded');
    setOnePagerLoading(true);
    try {
      // canvasData is already the unwrapped flat canvas (section keys at top level).
      // Do NOT re-read from localStorage — that gives the keyed { user_N: { … } }
      // wrapper which the summaries endpoint cannot parse (it looks for canvas.vision,
      // canvas.mission etc. at the top level).
      const res = await axios.post(
        `${API.replace('/canvas', '')}/summaries/onepager`,
        canvasData,
        { timeout: 25000 }
      );

      if (res.data && res.data.ok && res.data.summaries) {
        localStorage.setItem('guruone_strategy_summaries', JSON.stringify(res.data.summaries));
      }
    } catch (err) {
      console.warn('One-pager summarisation failed, opening with fallback truncation:', err.message);
    } finally {
      setOnePagerLoading(false);
      window.open('/OnePageDirection.html', '_blank', 'noopener');
    }
  };
  // Calculation kept in case future surfaces (Dashboard, journey indicator, etc.)
  // need section completeness — display has been moved into the dark toolbar.
  // eslint-disable-next-line no-unused-vars
  const _pct = (() => {
    const totalScore = SECTIONS.reduce((sum, s) => sum + getSectionCompleteness(s, canvasData[s.key]), 0);
    const maxScore = SECTIONS.length * 5;
    return Math.round((totalScore / maxScore) * 100);
  })();

  const toolbarBtn = {
    fontSize: '0.74rem', padding: '0.4rem 0.85rem', borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.25)', background: 'transparent',
    color: 'white', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
    width: 'auto', whiteSpace: 'nowrap',
  };

  return (
    <div>
      {/* Journey indicator — sits above the masthead per spec, mirroring DecisionGuru */}
      <DirectionJourneyTrack canvasData={canvasData} />

      {/* Inline guru title — uses #48B650 for the "Guru" word per spec */}
      <div style={{ textAlign: 'center', margin: '0 0 1.5rem' }}>
        <div style={{ fontSize: '2.5rem', fontWeight: 300, color: '#1a1a2e', letterSpacing: '-0.02em', lineHeight: 1 }}>
          Direction
          <strong style={{ color: '#7C3AED', fontWeight: 700 }}> Guru</strong>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.4rem', letterSpacing: '0.04em' }}>
          Clarify your why. Define where you win.
        </div>
      </div>

      {/* Dark guru toolbar — left group (logo + How to use + canvas label), right group (Pre-fill + Download) */}
      <div style={{
        background: '#1a1a2e', color: 'white', borderRadius: 10,
        padding: '0.55rem 0.85rem', marginBottom: '1.25rem',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        {/* Left group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flex: 1 }}>
          <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src="/Logo_emblem_only.png" alt="GuruOne" style={{ height: 22, width: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          </div>
          <button onClick={() => setHelpOpen(true)} aria-label="How to use Direction Guru" style={toolbarBtn}>
            How to use
          </button>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
            Your Direction Canvas
          </span>
        </div>
        {/* Right group */}
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          {onOpenGapTracker && (
            <button onClick={onOpenGapTracker} style={toolbarBtn}>
              Development areas
            </button>
          )}
          <button onClick={() => setPrefillOpen(true)} style={toolbarBtn}>
            ✨ Pre-fill canvas
          </button>
          <button
            onClick={handleOpenOnePager}
            disabled={onePagerLoading}
            style={{ ...toolbarBtn, borderColor: 'rgba(167,227,167,0.55)', color: '#A7E3A7', opacity: onePagerLoading ? 0.6 : 1, cursor: onePagerLoading ? 'wait' : 'pointer' }}
          >
            {onePagerLoading ? '⏳ Preparing…' : '⬇ Download 1-pager'}
          </button>
        </div>
      </div>

      {LAYOUT_ROWS.map(row => {
        const sections = SECTIONS.filter(s => s.group === row.key);
        if (sections.length === 0) return null;
        return (
          <div key={row.key} style={{ marginBottom: row.label ? '1.75rem' : '0.75rem' }}>
            {row.label && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ width: 4, height: 18, background: row.colour, borderRadius: 2, flexShrink: 0, alignSelf: 'center' }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{row.label}</span>
                {row.sublabel && <span style={{ fontSize: '0.75rem', color: '#aaa' }}>{row.sublabel}</span>}
              </div>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${row.cols}, 1fr)`,
              gap: '0.75rem',
            }}>
              {sections.map(s => (
                <SectionCard key={s.key} id={`direction-section-${s.key}`} section={s} sectionData={canvasData[s.key]} onOpen={() => onOpen(s.key)} />
              ))}
            </div>
          </div>
        );
      })}

      {helpOpen && <HowToUseDirectionPanel onClose={() => setHelpOpen(false)} />}

      {/* Pre-fill slide-in — wraps GlobalUploadPanel; closes after a successful save */}
      {prefillOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
          <div style={{ width: '100%', maxWidth: 520, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Pre-fill</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>Pre-fill your Direction canvas</div>
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.35rem', lineHeight: 1.5 }}>
                  Add your website, business plan, deck or other documents — we'll suggest values for the canvas you can review and accept.
                </div>
              </div>
              <button onClick={() => setPrefillOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
              <GlobalUploadPanel onSectionsLoaded={(sections, editAfter) => { onBulkSave(sections, editAfter); setPrefillOpen(false); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gap Tracker — surfaces open deep-dive gaps across all sections ────────────
// Reads the per-user gap list written by runAssessment and renders a slide-out
// panel listing open and resolved development areas. Each gap can be marked
// resolved; resolved gaps stay in the list (faded) for context.
function GapTracker({ user, onClose }) {
  const uid = user?.id || 'default';
  const [gaps, setGaps] = useState([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(`guruone_gaps_${uid}`) || '[]');
      setGaps(stored);
    } catch { setGaps([]); }
  }, [uid]);

  function resolveGap(id) {
    const updated = gaps.map(g => g.id === id ? { ...g, status: 'resolved' } : g);
    setGaps(updated);
    try {
      localStorage.setItem(`guruone_gaps_${uid}`, JSON.stringify(updated));
    } catch { /* quota / disabled — non-fatal */ }
  }

  const openGaps = gaps.filter(g => g.status === 'open');
  const resolvedGaps = gaps.filter(g => g.status === 'resolved');

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 1000, display: 'flex', flexDirection: 'column', fontFamily: 'inherit' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>Development areas</div>
          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>
            {openGaps.length} open · {resolvedGaps.length} resolved
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', padding: '0.25rem', width: 'auto', fontFamily: 'inherit' }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem' }}>
        {openGaps.length === 0 && (
          <div style={{ color: '#aaa', fontSize: '0.88rem', textAlign: 'center', marginTop: '2rem' }}>
            No open development areas yet. Complete a deep dive on any canvas section to surface them here.
          </div>
        )}
        {openGaps.map(gap => (
          <div key={gap.id} style={{ marginBottom: '1rem', padding: '1rem', borderRadius: 10, border: '1.5px solid #e8e8e8', background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                  {gap.section_title}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#222', lineHeight: 1.45 }}>{gap.description}</div>
                {gap.type === 'decision' && (
                  <div style={{ marginTop: 6, display: 'inline-block', fontSize: '0.72rem', fontWeight: 600, color: '#185FA5', background: '#f0f6ff', borderRadius: 20, padding: '2px 8px' }}>
                    → Decision Guru
                  </div>
                )}
              </div>
              <button
                onClick={() => resolveGap(gap.id)}
                title="Mark as resolved"
                style={{ flexShrink: 0, background: 'none', border: '1.5px solid #ddd', borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer', color: '#888', whiteSpace: 'nowrap', width: 'auto', fontFamily: 'inherit' }}
              >
                Resolve
              </button>
            </div>
          </div>
        ))}
        {resolvedGaps.length > 0 && (
          <>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '1.5rem', marginBottom: '0.75rem' }}>Resolved</div>
            {resolvedGaps.map(gap => (
              <div key={gap.id} style={{ marginBottom: '0.75rem', padding: '0.85rem 1rem', borderRadius: 10, border: '1.5px solid #f0f0f0', background: 'white', opacity: 0.6 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
                  {gap.section_title}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#888' }}>{gap.description}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────
export default function StrategyCanvas() {
  const navigate = useNavigate();
  const user = (() => { try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; } })();
  const storageKey = user?.id ? `user_${user.id}` : 'default';
  // Seed from localStorage for an instant first paint while the server
  // fetch is in flight. The server response (when it arrives) is the
  // source of truth and replaces this seed if it differs.
  const [canvasData, setCanvasData] = useState(() => loadCanvasData(storageKey));
  const [activeSection, setActiveSection] = useState(null); // section key
  const [activeMode, setActiveMode] = useState(null);       // null | 'quick' | 'explore' | 'infer'
  const [exploreContext, setExploreContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  // serverLoaded gates two things: (1) the debounced server-save
  // effect, so the first render doesn't immediately echo localStorage
  // back to the server; (2) the loading state shown to the leader
  // while we figure out whether the server has data.
  const [serverLoaded, setServerLoaded] = useState(false);
  const [serverLoadError, setServerLoadError] = useState(null);
  // Suppresses the next debounced save once — used after the load
  // effect applies server data, so we don't immediately re-PUT what
  // we just GOT.
  const skipNextSaveRef = useRef(false);
  // Holds the pending debounce timer so each new canvasData change
  // resets the clock instead of stacking saves.
  const saveTimerRef = useRef(null);

  // ── Server load on mount ──────────────────────────────────────
  // Source of truth: the server. localStorage is now a cache only.
  // Flow:
  //   - GET /api/canvas-state/strategy
  //   - If server has data (version > 0): apply it to state (with
  //     skipNextSave so the load doesn't trigger an immediate echo).
  //   - If server is empty but localStorage has data: upload it once
  //     (the migration path for users with local-only data — Glenn,
  //     and anyone using the platform before this shipped).
  //   - If both are empty: leader gets a fresh canvas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('guruone_token');
        if (!token) {
          if (!cancelled) { setServerLoaded(true); }
          return;
        }
        const res = await axios.get(`${API.replace('/canvas','')}/canvas-state/strategy`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const serverData = res.data?.data || {};
        const serverHasData = res.data?.version > 0 && Object.keys(serverData).length > 0;
        const localData = loadCanvasData(storageKey);
        const localHasData = Object.keys(localData).length > 0;

        if (serverHasData) {
          // Server wins. Apply, suppress the next save.
          skipNextSaveRef.current = true;
          setCanvasData(serverData);
        } else if (localHasData) {
          // Migration: push localStorage data up so it's never lost again.
          try {
            await axios.put(
              `${API.replace('/canvas','')}/canvas-state/strategy`,
              { data: localData, origin: 'localstorage_migration' },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            track('canvas_localstorage_migrated', { canvas: 'strategy', sections: Object.keys(localData).length });
          } catch (migErr) {
            console.warn('Strategy localStorage migration failed:', migErr.message);
          }
        }
        setServerLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.warn('Strategy server load failed — falling back to localStorage:', err.message);
        setServerLoadError(err.message || 'load_failed');
        setServerLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // Deliberately runs once on mount only. storageKey is a derived
    // constant for this leader's session; if they re-auth as someone
    // else the whole page unmounts via PrivateRoute first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cache to localStorage on every change ──────────────────────
  // Keeps the fast-paint cache fresh. Unchanged from before.
  useEffect(() => {
    if (Object.keys(canvasData).length > 0) {
      saveCanvasData(storageKey, canvasData);
      if (activeSection) track('direction_section_saved', { section: activeSection });
    }
  }, [canvasData, storageKey, activeSection]);

  // ── Debounced server save ──────────────────────────────────────
  // After server-loaded, every canvasData change schedules a PUT
  // 1.5s after the last change. Coalesces flurries of edits into one
  // save. skipNextSaveRef short-circuits the save triggered by the
  // load itself (so we don't echo server data back to the server).
  useEffect(() => {
    if (!serverLoaded) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    if (Object.keys(canvasData).length === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('guruone_token');
        if (!token) return;
        await axios.put(
          `${API.replace('/canvas','')}/canvas-state/strategy`,
          { data: canvasData, origin: 'strategy_save' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.warn('Strategy server save failed (localStorage cache still good):', err.message);
      }
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [canvasData, serverLoaded]);

  // Clear any prior Explore context whenever the active section changes,
  // so a fresh section never reads context belonging to a different topic.
  useEffect(() => {
    setExploreContext(null);
  }, [activeSection]);

  async function fetchExploreContext(sectionKey, sectionLabel) {
    setContextLoading(true);
    try {
      const sUser = JSON.parse(localStorage.getItem('guruone_user') || '{}');
      const directionCanvas = JSON.parse(localStorage.getItem('guruone_strategy') || '{}');
      const designCanvas = JSON.parse(localStorage.getItem('guruone_operating_model') || '{}');
      const res = await fetch(`${API}/context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('guruone_token')}` },
        body: JSON.stringify({ userId: sUser.id, guru: 'Direction Guru', sectionKey, sectionLabel, directionCanvas, designCanvas }),
      });
      if (res.ok) {
        const data = await res.json();
        setExploreContext(data);
      }
    } catch (e) {
      console.warn('Context pre-call failed — continuing without context', e);
    } finally {
      setContextLoading(false);
    }
  }

  const handleSave = (sectionKey, data) => {
    setCanvasData(prev => ({ ...prev, [sectionKey]: data }));
    setActiveSection(null);
    setActiveMode(null);
    setSkipNextEntryChoice(false);
  };

  // Non-navigating partial update — used by editors that need to
  // persist a small change (e.g. a re-evaluation result) without
  // bouncing the leader back to the canvas overview. The debounced
  // server save picks the change up automatically once canvasData
  // updates.
  const handlePatch = (sectionKey, patch) => {
    setCanvasData(prev => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), ...patch },
    }));
  };

  const handleDecision = (sectionOrKey, localData) => {
    const section = typeof sectionOrKey === 'string' ? SECTIONS.find(s => s.key === sectionOrKey) : sectionOrKey;
    if (!section) return;
    const contextData = localData || canvasData[section.key] || {};
    navigate('/decision', { state: {
      challenge: section.decisionPrompt,
      sectionKey: section.key,
      sectionTitle: section.title,
      strategyContext: formatStrategyContext({ ...canvasData, [section.key]: contextData }),
    } });
  };

  const handleBulkSave = (sections, editAfter = []) => {
    setCanvasData(prev => ({ ...prev, ...sections }));
    if (editAfter.length > 0) setActiveSection(editAfter[0]);
  };

  const handleBack = () => {
    if (activeMode) { setActiveMode(null); setSkipNextEntryChoice(false); }
    else { setActiveSection(null); setSkipNextEntryChoice(false); }
  };

  const activeSecDef = activeSection ? SECTIONS.find(s => s.key === activeSection) : null;

  // Track when the leader explicitly picked "Revise with deep dive"
  // from QuickEditor on a completed section. Bypasses the
  // entry-choice screen inside ExploreEditor.
  const [skipNextEntryChoice, setSkipNextEntryChoice] = useState(false);

  // Development-areas (deep-dive gap tracker) slide-out — populated by
  // runAssessment in ExploreEditor and visible across all canvas views.
  const [showGapTracker, setShowGapTracker] = useState(false);

  const renderContent = () => {
    if (!activeSection) {
      return <CanvasOverview canvasData={canvasData} onOpen={key => setActiveSection(key)} navigate={navigate} onBulkSave={handleBulkSave} onOpenGapTracker={() => setShowGapTracker(true)} />;
    }
    // For completed sections (level 2 or 3 — fields all filled,
    // optionally with narrative), land directly in QuickEditor rather
    // than asking the leader to pick a mode again. They can switch
    // into Explore or Infer from the QuickEditor header.
    const sd = activeSecDef ? canvasData[activeSection] : null;
    const sectionTier = activeSecDef ? getSectionTier(activeSecDef, sd) : { level: 0 };
    const isCompleted = sectionTier.level >= 2;
    const effectiveMode = activeMode || (isCompleted ? 'quick' : null);

    if (!effectiveMode) {
      return (
        <ModeSelector
          section={activeSecDef}
          existing={!!canvasData[activeSection]}
          onSelect={mode => {
            if (mode === 'explore' && activeSecDef) {
              fetchExploreContext(activeSecDef.key, activeSecDef.title || activeSecDef.key);
            }
            setActiveMode(mode);
          }}
          onBack={() => setActiveSection(null)}
        />
      );
    }
    if (effectiveMode === 'quick') {
      return <QuickEditor
        section={activeSecDef}
        sectionData={canvasData[activeSection]}
        onSave={data => handleSave(activeSection, data)}
        onBack={handleBack}
        onDecision={handleDecision}
        onPatch={patch => handlePatch(activeSection, patch)}
        onSwitchToExplore={isCompleted ? () => {
          if (activeSecDef) fetchExploreContext(activeSecDef.key, activeSecDef.title || activeSecDef.key);
          setSkipNextEntryChoice(true);
          setActiveMode('explore');
        } : undefined}
        onSwitchToInfer={isCompleted ? () => setActiveMode('infer') : undefined}
      />;
    }
    if (effectiveMode === 'explore') {
      return <ExploreEditor
        section={activeSecDef}
        sectionData={canvasData[activeSection]}
        onSave={data => handleSave(activeSection, data)}
        onBack={handleBack}
        onSwitchToQuick={() => setActiveMode('quick')}
        exploreContext={exploreContext}
        contextLoading={contextLoading}
        skipEntryChoice={skipNextEntryChoice}
        user={user}
      />;
    }
    if (effectiveMode === 'infer') {
      return <InferEditor section={activeSecDef} sectionData={canvasData[activeSection]} onSave={data => handleSave(activeSection, data)} onBack={handleBack} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f9' }}>
      <Header />
      <main style={{ maxWidth: 1300, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ background: 'white', borderRadius: 14, padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          {!serverLoaded ? (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>
              Loading your canvas{serverLoadError ? ' (cached copy)' : '…'}
            </div>
          ) : renderContent()}
        </div>
      </main>
      <DeliveryJourneySidebar />
      {showGapTracker && (
        <GapTracker user={user} onClose={() => setShowGapTracker(false)} />
      )}
    </div>
  );
}
